import { FC, useState, useEffect, useCallback, useMemo, useRef, CSSProperties, MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { KTSVG } from '../../../../_metronic/helpers'
import PortSelect from '../../../shared/components/PortSelect'
import {
  getRFQById,
  getRFQsPage,
  getQuotationById,
  getQuotationComparison,
  getQuotationsByRFQ,
  selectQuotation,
  updateQuotationAward,
  closeQuotation,
  QuotationAwardLineAssignment,
  ownerApproveQuotation,
  ownerRejectQuotation,
  requestQuotationRework,
  sendQuotationToVessel,
  sendQuotationsToVesselSelection,
  vesselSelectQuotation,
  vesselRejectQuotation,
  createPurchaseOrderFromQuotation,
  getAccountingAccountsWithParams,
  getAllAccountingAccounts,
  getSubAccounts,
  getCompanyPoInfo,
  getInventoryAddresses,
  requestQuotationReworkForRfq,
  listVendorLogisticsChangeRequests,
  approveVendorLogisticsChangeRequest,
  rejectVendorLogisticsChangeRequest,
  VendorLogisticsChangeRequest,
} from '../core/_requests'
import {
  AccountingAccount,
  CompanyPoInfo,
  QuotationComparisonLine,
  QuotationComparisonResponse,
  RequisitionLine,
  SubAccount,
  InventoryAddress,
} from '../core/_models'
import { useAuth } from '../../auth'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import QuotationDPCDocument from './QuotationDPCDocument'
import ProcurementFilterPanel, { FilterOption } from './ProcurementFilterPanel'
import ProcurementDocumentPreviewModal from './ProcurementDocumentPreviewModal'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { resolveProcurementLoggedInDisplayName } from '../core/roles'
import ProcurementAttachmentGallery from './ProcurementAttachmentGallery'
import { normalizeQuotationCommercialFields } from '../core/quotationCommercialFields'
import MoneyDisplay from './MoneyDisplay'
import {shouldShowInActiveQuotationManagement} from '../core/quotationManagementVisibility'
import {
  collectMissingAssignedQuotationAccounting,
  formatAccountingOptionLabel,
  validateAssignedQuotationAccountingLines,
} from '../core/quotationAccountingValidation'
import VendorLogisticsChangeRequestModal from './VendorLogisticsChangeRequestModal'
import { buildVendorLogisticsRequestMaps } from '../core/vendorLogisticsChangeRequests'
import QuotationRevisionRequestModal, { RevisionVendorOption } from './QuotationRevisionRequestModal'
import { downloadElementAsPdf } from '../core/exportUtils'
import { canRequestQuotationReworkForStatus } from '../core/quotationReworkEligibility'
import {
  normalizeProcurementListResponse,
  resolveBuyerCompanyGroupAdminId,
} from '../core/resolveBuyerCompanyGroupAdminId'
import {
  convertOriginalAmountToCompany,
  readFiniteAmount,
  resolveQuotationComparisonMetadata,
  resolveQuotationCompanyCurrency,
  resolveQuotationDisplayCurrency,
  resolveQuotationOriginalTotal,
  calculateQuotationLineAmount,
  isNonZeroLogisticsValue,
  readComparisonQuantity,
  QUOTATION_COMPARISON_VENDOR_COLUMN_ORDER,
} from '../core/quotationComparison'
import SearchableSelect from '../../../shared/components/SearchableSelect'

type QuotationAttachment = {
  id?: number | string | null
  fileName: string
  url?: string
  attachmentKey?: string | null
  sizeBytes?: number | null
  available?: boolean | null
}

interface VendorQuotation {
  id: number
  quoteRef?: string
  vendor: string
  vendorCode: string
  vendorEmail: string
  vendorContact: string
  price: number
  deliveryTime: string
  deliveryPort: string
  paymentTerms: string
  validity: string
  rating: number
  percentage: number
  comparableInCompanyCurrency?: boolean
  comparisonAmount?: number | null
  comparisonCurrency?: string | null
  comparisonUnavailableReason?: string | null
  currency: string
  quotationCurrency?: string | null
  companyCurrencyCode?: string | null
  originalTotal?: number | null
  companyTotal?: number | null
  exchangeRate?: number | null
  exchangeRateDisplay?: string | null
  status: string
  qualityRating?: number | null
  supplyRating?: number | null
  totalOrders: number
  issueCount: number
  lineSubtotal?: number
  lineDiscountTotal?: number
  lineGstVatTotal?: number
  totalDiscount?: number | null
  freightCost?: number | null
  packagingCost?: number | null
  truckCost?: number | null
  bargeCost?: number | null
  otherCost?: number | null
  vatOnLogistics?: number | null
  billingAddressId?: number | null
  shippingAddressId?: number | null
  billingAddressLabel?: string | null
  billingAddressDisplay?: string | null
  shippingAddressLabel?: string | null
  shippingAddressDisplay?: string | null
  grandTotal?: number
  ownerActionRemark?: string | null
  reworkRemark?: string | null
  vesselForwardRemark?: string | null
  requisitionQuantity?: number
  remainingQuantityToFulfillReq?: number
  actualQuantityAssignedToVendor?: number
  submittedAt?: string | null
  quotedItemCount?: number
  quotationId?: number
  lines?: QuotationLineDto[]
  attachments?: QuotationAttachment[]
  vendorDetails?: {
    id?: number
    code?: string
    name?: string
    email?: string
    contactNo?: string
    totalOrders?: number
    avgDeliveryDays?: number
    issueCount?: number
    qualityRating?: number | null
    supplyRating?: number | null
  }
}

interface ExpandedCards {
  [key: number]: boolean
}

interface RFQOption {
  id: number
  title: string
  requisitionLabel?: string
  requisitionDescription?: string
  status: string
  responseCount?: number | null
}

interface APIQuotation {
  id: number
  vendorId: number
  vendorName: string
  vendorCode?: string | null
  totalAmount?: number | null
  promisedDeliveryDate: string | null
  currencyCode?: string | null
  quotationCurrency?: string | null
  customCurrency?: string | null
  quotationCurrencyCode?: string | null
  companyCurrencyCode?: string | null
  exchangeRate?: number | null
  exchangeRateDisplay?: string | null
  originalTotal?: number | null
  companyTotal?: number | null
  originalSubtotal?: number | null
  originalDiscount?: number | null
  originalTax?: number | null
  originalLogisticsCost?: number | null
  companySubtotal?: number | null
  companyDiscount?: number | null
  companyTax?: number | null
  companyLogisticsCost?: number | null
  totalDiscount?: number | null
  freightCost?: number | null
  packagingCost?: number | null
  truckCost?: number | null
  bargeCost?: number | null
  otherCost?: number | null
  vatOnLogistics?: number | null
  paymentTerms?: string | null
  validity?: string | null
  status: string
  submittedAt?: string | null
  requiredQuantity?: number
  actualQuantityAssignedToVendor?: number
  remainingQuantityToFulfillReq?: number
  attachmentCount?: number | null
  quotedItemCount?: number | null
  billingAddressId?: number | null
  shippingAddressId?: number | null
  billingAddressLabel?: string | null
  billingAddressDisplay?: string | null
  shippingAddressLabel?: string | null
  shippingAddressDisplay?: string | null
  lines?: QuotationLineDto[]
  ownerActionRemark?: string | null
  reworkRemark?: string | null
  vesselForwardRemark?: string | null
}

type QuotationLineDto = QuotationComparisonLine

interface LineMeta {
  name: string
  unit?: string
  partNo?: string
  rob?: number | null
  reqQty?: number | null
  accountCodeId?: number | null
  accountCode?: string | null
  subaccountCodeId?: number | null
  subaccountCode?: string | null
}

interface ComparisonRow {
  rowKey: string
  lineId: number | null
  partNo: string
  itemName: string
  unit: string
  rob: string
  reqQty: string
  reqQtyValue: number
  accountCodeId: number | null
  accountCode: string
  subaccountCodeId: number | null
  subaccountCode: string
  vendorLineMap: Record<number, QuotationLineDto | null>
  orderQtyValue: number
}

interface RowAccountingSelection {
  accountCodeId: number | null
  accountCode: string
  subaccountCodeId: number | null
  subaccountCode: string
}


const resolveVesselName = (requisition: any): string => {
  const candidates = [
    requisition?.vesselName,
    requisition?.vessel?.fleet_name,
    requisition?.vessel?.fleetName,
    requisition?.vessel?.name,
    requisition?.vessel?.vesselName,
  ]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }
  return ''
}

const resolveComparisonDisplayCurrency = (quotation?: {
  currencyCode?: string | null
  quotationCurrency?: string | null
  quotationCurrencyCode?: string | null
  customCurrency?: string | null
} | null): string => {
  if (!quotation) return '-'
  const code = String(quotation.quotationCurrencyCode || '').trim().toUpperCase()
  const quotationCurrency = String(quotation.quotationCurrency || '').trim().toUpperCase()
  const currency = String(quotation.currencyCode || '').trim().toUpperCase()
  const customCurrency = String(quotation.customCurrency || '').trim()
  if ((code === 'CUSTOM' || code === 'OTHER' || quotationCurrency === 'CUSTOM' || quotationCurrency === 'OTHER') && customCurrency) {
    return customCurrency
  }
  return quotationCurrency || code || currency || customCurrency || '-'
}

const quotationStatusPresentation = (status?: string | null) => {
  const normalized = String(status || 'SUBMITTED').toUpperCase()
  if (normalized === 'SENT_FOR_REWORK') {
    return {className: 'badge bg-warning text-dark border', label: 'Sent for Rework'}
  }
  return {
    className: 'badge bg-light text-dark border text-uppercase',
    label: normalized.replace(/_/g, ' '),
  }
}

interface ViewPastRFQModalProps {
  visible: boolean
  onClose: () => void
  rfq: any | null
}

const ViewPastRFQModal: FC<ViewPastRFQModalProps> = ({ visible, onClose, rfq }) => {
  if (!visible || !rfq) return null

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { bg: string; text: string } } = {
      'CLOSED': { bg: 'bg-success', text: 'Closed' },
      'SENT': { bg: 'bg-primary', text: 'Sent' },
      'DRAFT': { bg: 'bg-secondary', text: 'Draft' }
    }
    const statusInfo = statusMap[status] || { bg: 'bg-secondary', text: status }
    return <span className={`badge ${statusInfo.bg} text-white`}>{statusInfo.text}</span>
  }

  return (
    <ProcurementDocumentPreviewModal
      visible={visible}
      onClose={onClose}
      title='Past RFQ Details'
      subtitle={rfq.title}
      toolbar={getStatusBadge(rfq.status)}
    >
      <QuotationDPCDocument rfq={rfq} />
    </ProcurementDocumentPreviewModal>
  )
}
const QuotationManagement: FC = () => {
  const vendorColumnCount = QUOTATION_COMPARISON_VENDOR_COLUMN_ORDER.length
  type AccountCodeMode = 'COMMON' | 'INDIVIDUAL'
  const [accountCodeMode, setAccountCodeMode] = useState<AccountCodeMode>('COMMON')
  const [commonBudgetAccountCodeId, setCommonBudgetAccountCodeId] = useState<number | null>(null)
  const [commonBudgetAccountCode, setCommonBudgetAccountCode] = useState('')
  const [commonBudgetSubaccountCodeId, setCommonBudgetSubaccountCodeId] = useState<number | null>(null)
  const [commonBudgetSubaccountCode, setCommonBudgetSubaccountCode] = useState('')
  const [commonLogisticAccountCodeId, setCommonLogisticAccountCodeId] = useState<number | null>(null)
  const [commonLogisticAccountCode, setCommonLogisticAccountCode] = useState('')
  const [commonLogisticSubaccountCodeId, setCommonLogisticSubaccountCodeId] = useState<number | null>(null)
  const [commonLogisticSubaccountCode, setCommonLogisticSubaccountCode] = useState('')
  const { currentUser, auth } = useAuth()
  const {
    loading: procurementPermissionsLoading,
    can: canProcurement,
    getScope: getProcurementScope,
  } = useProcurementPermissions()
  const canViewQuotation = canProcurement(PROCUREMENT_PERMS.QUOTATION_VIEW)
  const canCompareQuotation = canProcurement(PROCUREMENT_PERMS.QUOTATION_COMPARE)
  const canSelectQuotation = canProcurement(PROCUREMENT_PERMS.QUOTATION_SELECT)
  const canSendQuotationToVessel = canProcurement(PROCUREMENT_PERMS.QUOTATION_SEND_TO_VESSEL)
  const canRequestQuotationRework = canProcurement(PROCUREMENT_PERMS.QUOTATION_REWORK)
  const canOwnerApproveQuotation = canProcurement(PROCUREMENT_PERMS.QUOTATION_OWNER_APPROVE)
  const canOwnerRejectQuotation = canOwnerApproveQuotation
  const canViewVendorLogisticsRequests =
    canProcurement(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_VIEW) ||
    canProcurement(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW)
  const canReviewVendorLogisticsRequests = canProcurement(PROCUREMENT_PERMS.PO_VENDOR_LOGISTICS_REVIEW)
  const canManageQuotationWorkflow =
    canCompareQuotation || canSelectQuotation || canSendQuotationToVessel || canRequestQuotationRework
  const canRunOwnerApprovalActions = canOwnerApproveQuotation || canOwnerRejectQuotation
  const officeDeferredStatuses = useMemo(
    () =>
      new Set([
        'PENDING_OWNER_APPROVAL',
        'SENT_TO_VESSEL',
        'SENT_TO_VESSEL_FOR_SELECTION',
      ]),
    []
  )

  const [activeTab] = useState<'recent' | 'past'>('recent')
  // Legacy filter state remains only for the unreachable past-RFQ renderer.
  const [searchTerm, setSearchTerm] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [companyFilter, setCompanyFilter] = useState('')
  const [referenceFilter, setReferenceFilter] = useState('')
  const [vesselFilter, setVesselFilter] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('')
  const [catalogueFilter, setCatalogueFilter] = useState('')
  const [requisitionTitleFilter, setRequisitionTitleFilter] = useState('')
  const [rfqTitleFilter, setRfqTitleFilter] = useState('')
  const [approvalPendingWithFilter, setApprovalPendingWithFilter] = useState('')
  const [poDateFromFilter, setPoDateFromFilter] = useState('')
  const [poDateToFilter, setPoDateToFilter] = useState('')
  const [deliveredToVesselFilter, setDeliveredToVesselFilter] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedRFQ, setSelectedRFQ] = useState<number | null>(null)
  const [rfqOptions, setRfqOptions] = useState<RFQOption[]>([])
  const [selectedRfqOption, setSelectedRfqOption] = useState<RFQOption | null>(null)
  const [rfqSearchTerm, setRfqSearchTerm] = useState('')
  const [rfqPage, setRfqPage] = useState(0)
  const [rfqHasMore, setRfqHasMore] = useState(false)
  const [comparisonData, setComparisonData] = useState<QuotationComparisonResponse | null>(null)
  const [quotations, setQuotations] = useState<APIQuotation[]>([])
  const [rfqLoading, setRfqLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [vendorQuotations, setVendorQuotations] = useState<VendorQuotation[]>([])
  const [allVendorQuotations, setAllVendorQuotations] = useState<VendorQuotation[]>([])
  const [expandedCards, setExpandedCards] = useState<ExpandedCards>({})
  const [pendingLogisticsRequests, setPendingLogisticsRequests] = useState<VendorLogisticsChangeRequest[]>([])
  const [activeLogisticsRequest, setActiveLogisticsRequest] = useState<VendorLogisticsChangeRequest | null>(null)
  const [logisticsActionLoading, setLogisticsActionLoading] = useState(false)
  const [revisionRequestModalOpen, setRevisionRequestModalOpen] = useState(false)
  const [revisionRequestSubmitting, setRevisionRequestSubmitting] = useState(false)
  const [comparisonDownloadLoading, setComparisonDownloadLoading] = useState(false)
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false)

  // Past RFQs states
  const [pastRFQs, setPastRFQs] = useState<any[]>([])
  const [pastRFQsLoading, setPastRFQsLoading] = useState(false)
  const [pastCurrentPage, setPastCurrentPage] = useState(1)
  const [pastRowsPerPage, setPastRowsPerPage] = useState(10)
  const [selectedPastRFQ, setSelectedPastRFQ] = useState<any | null>(null)
  const [isViewPastRFQModalVisible, setIsViewPastRFQModalVisible] = useState(false)
  const [isQuotationModalVisible, setIsQuotationModalVisible] = useState(false)
  const [activeVendorForModal, setActiveVendorForModal] = useState<VendorQuotation | null>(null)
  const [quotationDetailCache, setQuotationDetailCache] = useState<Record<number, any>>({})
  const [quotationDetailLoading, setQuotationDetailLoading] = useState(false)
  const [allocationValues, setAllocationValues] = useState<Record<string, number>>({})
  const [inlineAllocationValues, setInlineAllocationValues] = useState<Record<string, number>>({})
  const [inlineSaving, setInlineSaving] = useState(false)
  const [modalLines, setModalLines] = useState<RequisitionLine[]>([])
  const [modalLinesLoading, setModalLinesLoading] = useState(false)
  const [selectedLineKey, setSelectedLineKey] = useState<string | null>(null)
  const [lineMetaById, setLineMetaById] = useState<Record<number, LineMeta>>({})
  const [poDateDraft, setPoDateDraft] = useState('')
  const [supplierReferenceDraft, setSupplierReferenceDraft] = useState('')
  const [invoiceAddressedToDraft, setInvoiceAddressedToDraft] = useState('')
  const [billingAddressDraft, setBillingAddressDraft] = useState('')
  const [billingAddressIdDraft, setBillingAddressIdDraft] = useState<number | null>(null)
  const [poRaisedByUserDraft, setPoRaisedByUserDraft] = useState('')
  const [accountCategoryDraft, setAccountCategoryDraft] = useState('')
  const [budgetCodeDraft, setBudgetCodeDraft] = useState('')
  const [ownerApprovalRequiredDraft, setOwnerApprovalRequiredDraft] = useState(false)
  const [ownerRemarksDraft, setOwnerRemarksDraft] = useState('')
  const [deliveryPortDraft, setDeliveryPortDraft] = useState('')
  const [deliveryDateDraft, setDeliveryDateDraft] = useState('')
  const [portCallDraft, setPortCallDraft] = useState('')
  const [tagReferenceDraft, setTagReferenceDraft] = useState('')
  const [poTermsDraft, setPoTermsDraft] = useState('')
  const [agentInstructionsDraft, setAgentInstructionsDraft] = useState('')
  const [approvalRemarksDraft, setApprovalRemarksDraft] = useState('')
  const [ownerAttachmentFiles, setOwnerAttachmentFiles] = useState<File[]>([])
  const [companyPoDefaults, setCompanyPoDefaults] = useState<CompanyPoInfo | null>(null)
  const [companyBillingAddresses, setCompanyBillingAddresses] = useState<InventoryAddress[]>([])
  const [poFormErrors, setPoFormErrors] = useState<Record<string, string>>({})
  const [detailVendorId, setDetailVendorId] = useState<number | null>(null)
  const [quickActionMenuState, setQuickActionMenuState] = useState<{
    vendorId: number
    top: number
    left: number
  } | null>(null)
  const [comparisonAccountCodes, setComparisonAccountCodes] = useState<AccountingAccount[]>([])
  const [comparisonSubAccounts, setComparisonSubAccounts] = useState<Record<number, SubAccount[]>>({})
  const [rowAccountingValues, setRowAccountingValues] = useState<Record<string, RowAccountingSelection>>({})
  const [actionDialog, setActionDialog] = useState<{
    type: 'OWNER_APPROVE' | 'OWNER_REJECT' | 'SEND_TO_VESSEL' | 'REWORK'
    quotationId: number
    requireRemark: boolean
    title: string
    placeholder: string
    submitLabel: string
  } | null>(null)
  const [actionRemark, setActionRemark] = useState('')
  const deliveryAutofillKeyRef = useRef<string>('')
  const shimmerBaseStyle = {
    background: 'linear-gradient(90deg, #f5f7fb 0%, #eef1f6 50%, #f5f7fb 100%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.3s ease-in-out infinite',
    borderRadius: '8px'
  }

  const comparisonRfq = comparisonData?.rfq ?? null
  const comparisonRequisition = comparisonData?.requisition ?? null
  const comparisonRequiredLines = comparisonData?.requiredLines ?? []

  const rfqDetails = selectedRFQ && comparisonRfq ? {
    rfqNumber: comparisonRfq.title || comparisonRfq.rfqNumber || `RFQ #${comparisonRfq.id}`,
    itemService: comparisonRfq.itemDescription || '-',
    lineItems: comparisonRequiredLines.length,
    department: comparisonRequisition?.categoryName || '-',
    requestedDate: comparisonRequisition?.requiredDeliveryDate || '-'
  } : {
    rfqNumber: 'Select RFQ',
    itemService: '-',
    lineItems: 0,
    department: '-',
    requestedDate: '-'
  }

  useEffect(() => {
    setQuickActionMenuState(null)
    setPoFormErrors({})
  }, [selectedRFQ, activeTab])

  const mapApiQuotationToVendorQuotation = useCallback((q: APIQuotation): VendorQuotation => {
    const normalizedQuotation = normalizeQuotationCommercialFields(q)
    const comparison = resolveQuotationComparisonMetadata(q, q.companyCurrencyCode ?? null)
    const deliveryDays = q.promisedDeliveryDate
      ? Math.ceil((new Date(q.promisedDeliveryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : 0
    const quoteLines = q.lines ?? []
    const lineSubtotal = quoteLines.reduce((sum, line) => {
      const qty = Number(line?.assignedQuantity ?? line?.quantity ?? 0)
      const unitPrice = Number(line?.unitPrice ?? 0)
      const base = Number.isFinite(qty) && Number.isFinite(unitPrice) ? qty * unitPrice : 0
      return sum + base
    }, 0)
    const lineDiscountTotal = quoteLines.reduce((sum, line) => {
      const qty = Number(line?.assignedQuantity ?? line?.quantity ?? 0)
      const unitPrice = Number(line?.unitPrice ?? 0)
      const discountPct = Number(line?.discountPercent ?? 0)
      const base = Number.isFinite(qty) && Number.isFinite(unitPrice) ? qty * unitPrice : 0
      const discountValue = Number.isFinite(discountPct) ? (base * discountPct) / 100 : 0
      return sum + discountValue
    }, 0)
    const lineGstVatTotal = quoteLines.reduce((sum, line) => {
      const qty = Number(line?.assignedQuantity ?? line?.quantity ?? 0)
      const unitPrice = Number(line?.unitPrice ?? 0)
      const discountPct = Number(line?.discountPercent ?? 0)
      const gstPct = Number(line?.gstPercent ?? 0)
      const base = Number.isFinite(qty) && Number.isFinite(unitPrice) ? qty * unitPrice : 0
      const discountedBase = base - (Number.isFinite(discountPct) ? (base * discountPct) / 100 : 0)
      const gstValue = Number.isFinite(gstPct) ? (discountedBase * gstPct) / 100 : 0
      return sum + gstValue
    }, 0)
    const freightCost = Number(q.freightCost ?? 0)
    const packagingCost = Number(q.packagingCost ?? 0)
    const truckCost = Number(q.truckCost ?? 0)
    const bargeCost = Number(q.bargeCost ?? 0)
    const otherCost = Number(q.otherCost ?? 0)
    const vatOnLogistics = Number(q.vatOnLogistics ?? 0)
    const totalDiscount = Number(q.totalDiscount ?? 0)
    const computedTotalBeforeHeaderDiscount =
      lineSubtotal +
      lineGstVatTotal +
      freightCost +
      packagingCost +
      truckCost +
      bargeCost +
      otherCost +
      vatOnLogistics
    const computedGrandTotal = Math.max(computedTotalBeforeHeaderDiscount - totalDiscount, 0)
    const quotedGrandTotal = Number(q.totalAmount ?? 0)
    const resolvedGrandTotal = quotedGrandTotal > 0 ? quotedGrandTotal : computedGrandTotal
    const remainingQuantityToFulfillReq =
      q.remainingQuantityToFulfillReq ??
      q.requiredQuantity ??
      null
    const displayCurrency = resolveComparisonDisplayCurrency(q)

    return {
      id: q.id,
      quoteRef: `Q-${q.id}`,
      quotationId: q.id,
      vendor: q.vendorName,
      vendorCode: q.vendorCode || '',
      vendorEmail: '',
      vendorContact: '',
      price: comparison.comparisonAmount ?? Number.MAX_SAFE_INTEGER,
      deliveryTime: deliveryDays > 0 ? `${deliveryDays} days` : 'TBD',
      deliveryPort: comparisonRfq?.deliveryPortName || 'N/A',
      paymentTerms: normalizedQuotation.termsOfPayment,
      validity: q.status === 'SUBMITTED' ? 'Valid' : q.status,
      rating: 4.0,
      percentage: 0,
      comparableInCompanyCurrency: comparison.comparableInCompanyCurrency,
      comparisonAmount: comparison.comparisonAmount,
      comparisonCurrency: comparison.comparisonCurrency,
      comparisonUnavailableReason: comparison.comparisonUnavailableReason,
      currency: displayCurrency,
      quotationCurrency: q.quotationCurrency ?? null,
      companyCurrencyCode: q.companyCurrencyCode ?? null,
      originalTotal: resolveQuotationOriginalTotal(q),
      companyTotal: comparison.companyAmount,
      exchangeRate: q.exchangeRate ?? null,
      exchangeRateDisplay: q.exchangeRateDisplay ?? null,
      status: q.status,
      qualityRating: null,
      supplyRating: null,
      submittedAt:
        q.submittedAt ?? null,
      quotedItemCount: quoteLines.length,
      totalOrders: 0,
      issueCount: 0,
      lineSubtotal,
      lineDiscountTotal,
      lineGstVatTotal,
      totalDiscount,
      freightCost,
      packagingCost,
      truckCost,
      bargeCost,
      otherCost,
      vatOnLogistics,
      billingAddressId: q.billingAddressId ?? null,
      shippingAddressId: q.shippingAddressId ?? null,
      billingAddressLabel: q.billingAddressLabel ?? null,
      billingAddressDisplay: q.billingAddressDisplay ?? null,
      shippingAddressLabel: q.shippingAddressLabel ?? null,
      shippingAddressDisplay: q.shippingAddressDisplay ?? null,
      grandTotal: q.originalTotal ?? resolvedGrandTotal,
      ownerActionRemark: q.ownerActionRemark ?? null,
      reworkRemark: q.reworkRemark ?? null,
      vesselForwardRemark: q.vesselForwardRemark ?? null,
      requisitionQuantity: q.requiredQuantity ?? q.actualQuantityAssignedToVendor ?? remainingQuantityToFulfillReq ?? null,
      remainingQuantityToFulfillReq,
      actualQuantityAssignedToVendor: q.actualQuantityAssignedToVendor ?? 0,
      lines: q.lines ?? [],
      attachments: q.attachmentCount && q.attachmentCount > 0
        ? [{
            fileName: `${q.attachmentCount} attachment(s)`,
            available: true,
          }]
        : [],
      vendorDetails: {
        id: q.vendorId,
        code: q.vendorCode || '',
        name: q.vendorName,
        email: '',
        contactNo: '',
        totalOrders: 0,
        avgDeliveryDays: 0,
        issueCount: 0,
        qualityRating: null,
        supplyRating: null,
      },
    }
  }, [comparisonRfq])

  const mapRfqToOption = useCallback((rfq: any): RFQOption => ({
    id: rfq.id,
    title: rfq.title || rfq.rfqNumber || `RFQ #${rfq.id}`,
    requisitionLabel:
      String(rfq?.requisitionTitle || '').trim() ||
      String(rfq?.requisitionCode || '').trim() ||
      undefined,
    requisitionDescription: String(rfq?.requisitionDescription || '').trim() || undefined,
    status: rfq.status,
    responseCount: rfq.responseCount ?? 0,
  }), [])

  const fetchRFQs = useCallback(async (searchValue = '', page = 0, append = false) => {
    setRfqLoading(true)
    try {
      const normalizedSearch = searchValue.trim().toLowerCase()
      const resolvedCgaId =
        Number(
          (currentUser as any)?.companyGroupAdminId ??
          (currentUser as any)?.companyGroupAdmin?.id ??
          (currentUser as any)?.cgaid?.id ??
          (currentUser as any)?.cgaid ??
          (currentUser as any)?.roleEntityId ??
          0
        ) || undefined
      const rfqScope = getProcurementScope(PROCUREMENT_PERMS.RFQ_VIEW)
      const cgaid = rfqScope === 'ALL' ? undefined : resolvedCgaId
      const matchesSearch = (rfq: any) => [
        rfq.rfqNumber,
        rfq.title,
        rfq.requisitionCode,
        rfq.requisitionTitle,
        rfq.requisitionDescription,
      ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch))

      let loadedPage = page
      let responsePage: Awaited<ReturnType<typeof getRFQsPage>>
      let matchingContent: any[] = []
      let hasMorePages = true
      do {
        responsePage = await getRFQsPage({
          cgaid,
          page: loadedPage,
          size: 20,
          q: normalizedSearch || undefined,
          sort: ['id,desc'],
        })
        const content = responsePage.content || []
        matchingContent = matchingContent.concat(normalizedSearch ? content.filter(matchesSearch) : content)
        hasMorePages = !responsePage.last && loadedPage + 1 < Number(responsePage.totalPages ?? loadedPage + 1)
        if ((normalizedSearch && matchingContent.length > 0) || !hasMorePages) break
        loadedPage += 1
      } while (true)

      const firstTarget = matchingContent.filter((rfq: any) => shouldShowInActiveQuotationManagement(rfq))
      const nextOptions = firstTarget.map(mapRfqToOption)
      const replacementOptions = selectedRfqOption &&
        !nextOptions.some((option) => option.id === selectedRfqOption.id) &&
        shouldShowInActiveQuotationManagement(selectedRfqOption as any)
        ? [selectedRfqOption, ...nextOptions]
        : nextOptions

      setRfqOptions((previousOptions) => append
        ? [...previousOptions, ...nextOptions.filter((option) => !previousOptions.some((existing) => existing.id === option.id))]
        : replacementOptions)
      setRfqPage(loadedPage)
      setRfqHasMore(hasMorePages)
      setRfqLoading(false)

      if (!append && selectedRFQ && !replacementOptions.some((option) => option.id === selectedRFQ)) {
        setSelectedRFQ(null)
        setSelectedRfqOption(null)
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error)
      setRfqOptions([])
      setRfqLoading(false)
    }
  }, [currentUser, selectedRFQ, selectedRfqOption, getProcurementScope, mapRfqToOption])

  const loadMoreRFQs = useCallback(() => {
    if (!rfqLoading && rfqHasMore) {
      void fetchRFQs(rfqSearchTerm, rfqPage + 1, true)
    }
  }, [fetchRFQs, rfqHasMore, rfqLoading, rfqPage, rfqSearchTerm])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void fetchRFQs(rfqSearchTerm, 0, false)
    }, 250)
    return () => window.clearTimeout(timeout)
  }, [fetchRFQs, rfqSearchTerm])

  // Fetch quotations when RFQ is selected
  useEffect(() => {
    if (selectedRFQ) {
      fetchQuotations(selectedRFQ)
    }
  }, [selectedRFQ])

  const loadPendingLogisticsRequests = useCallback(async () => {
    if (!canViewVendorLogisticsRequests) {
      setPendingLogisticsRequests([])
      return
    }
    try {
      const requestResponse = await listVendorLogisticsChangeRequests({ status: 'PENDING' })
      const logisticsRequests = normalizeProcurementListResponse<VendorLogisticsChangeRequest>(requestResponse)
      setPendingLogisticsRequests(logisticsRequests)
    } catch (error: any) {
      console.error('Failed to load pending vendor logistics requests:', error)
      toast.error(error?.message || 'Failed to load vendor logistics requests')
    }
  }, [canViewVendorLogisticsRequests])

  useEffect(() => {
    void loadPendingLogisticsRequests()
  }, [loadPendingLogisticsRequests])

  const pendingLogisticsMaps = useMemo(
    () => buildVendorLogisticsRequestMaps(pendingLogisticsRequests),
    [pendingLogisticsRequests]
  )

  const refreshPendingLogisticsRequests = useCallback(async () => {
    await loadPendingLogisticsRequests()
    if (selectedRFQ) {
      await fetchQuotations(selectedRFQ)
    }
  }, [loadPendingLogisticsRequests, selectedRFQ])

  useEffect(() => {
    setInlineAllocationValues({})
  }, [selectedRFQ])

  const fetchQuotations = async (rfqId: number) => {
    setLoading(true)
    try {
      const comparison = await getQuotationComparison(rfqId)
      const quotationsData = (comparison?.quotations || []).map((quotation) => ({
        ...quotation,
        currencyCode: quotation.currencyCode ?? null,
      })) as APIQuotation[]
      setComparisonData(comparison)
      setQuotations(quotationsData)
      setQuotationDetailCache({})

      const detailedComparisonStatuses = new Set([
        'SUBMITTED',
        'PENDING_OWNER_APPROVAL',
        'SELECTED',
        'OWNER_APPROVED',
        'OWNER_REWORK',
        'OWNER_REJECTED',
        'SENT_TO_VESSEL',
        'SENT_FOR_REWORK',
        'REWORK_REQUESTED',
        'UPDATED_AFTER_REWORK',
      ])
      const allTransformedData: VendorQuotation[] = quotationsData.map(mapApiQuotationToVendorQuotation)
      setAllVendorQuotations(allTransformedData)

      const validQuotations = quotationsData.filter((q: APIQuotation) => {
        const normalizedStatus = String(q.status || '').toUpperCase()
        if (!detailedComparisonStatuses.has(normalizedStatus)) {
          return false
        }
        if (
          canManageQuotationWorkflow &&
          !canRunOwnerApprovalActions &&
          officeDeferredStatuses.has(normalizedStatus)
        ) {
          return false
        }
        return true
      })

      // Check if response has data
      if (!validQuotations || validQuotations.length === 0) {
        console.log('No valid quotations found in response')
        setVendorQuotations([])
        setLineMetaById({})
        setLoading(false)
        return
      }

      const lineMap: Record<number, LineMeta> = {}
      ;(comparison?.requiredLines || []).forEach((line, idx) => {
        if (line?.requisitionLineId != null) {
          lineMap[line.requisitionLineId] = {
            name: line.itemName || `Item ${idx + 1}`,
            unit: line.unitOfMeasurement || undefined,
            partNo: `Line #${line.requisitionLineId}`,
            rob: null,
            reqQty: line.requiredQuantity ?? null,
            accountCodeId: null,
            accountCode: null,
            subaccountCodeId: null,
            subaccountCode: null,
          }
        }
      })
      setLineMetaById(lineMap)

      const transformedData: VendorQuotation[] = validQuotations.map(mapApiQuotationToVendorQuotation)

      console.log('Transformed vendor quotations:', transformedData)

      // Calculate percentages based on price competitiveness
      const totalPrice = transformedData
        .filter((v) => v.comparableInCompanyCurrency && Number.isFinite(v.comparisonAmount))
        .reduce((sum, v) => sum + Number(v.comparisonAmount ?? 0), 0)
      transformedData.forEach(v => {
        const amount = Number(v.comparisonAmount ?? 0)
        v.percentage =
          v.comparableInCompanyCurrency && totalPrice > 0 && Number.isFinite(amount)
            ? Math.round((amount / totalPrice) * 100)
            : 0
      })

      const initialInlineAllocations: Record<string, number> = {}
      transformedData.forEach((vendor) => {
        ; (vendor.lines || []).forEach((line) => {
          if (line.requisitionLineId == null) return
          const assigned = Number(line.assignedQuantity ?? 0)
          if (assigned > 0) {
            initialInlineAllocations[`line-${line.requisitionLineId}-${vendor.id}`] = assigned
          }
        })
      })

      setVendorQuotations(transformedData)
      setInlineAllocationValues(initialInlineAllocations)
    } catch (error) {
      console.error('Error fetching quotations:', error)
      setVendorQuotations([])
      setAllVendorQuotations([])
      setInlineAllocationValues({})
    } finally {
      setLoading(false)
    }
  }

  const buildModalLinesForQuotation = useCallback((quotation: APIQuotation | null | undefined): RequisitionLine[] => {
    const quoteLineMap = new Map<number, QuotationLineDto>()
    ;(quotation?.lines || []).forEach((line) => {
      const lineId = Number(line?.requisitionLineId ?? 0)
      if (lineId > 0 && !quoteLineMap.has(lineId)) {
        quoteLineMap.set(lineId, line)
      }
    })

    if (comparisonRequiredLines.length > 0) {
      return comparisonRequiredLines.map((line, idx) => {
        const lineId = Number(line.requisitionLineId ?? 0)
        const quoteLine = lineId > 0 ? quoteLineMap.get(lineId) : undefined
        return {
          partId: lineId > 0 ? lineId : undefined,
          subcomponentId: undefined,
          manualItemName: line.itemName || quoteLine?.lineItemName || `Item ${idx + 1}`,
          quantity: line.requiredQuantity ?? 0,
          unitOfMeasurement: line.unitOfMeasurement ?? quoteLine?.unitOfMeasurement ?? undefined,
          estimatedUnitPrice: quoteLine?.unitPrice ?? undefined,
          remarks: quoteLine?.remarks ?? undefined,
          notes: line.notes ?? quoteLine?.remarks ?? undefined,
        }
      })
    }

    return (quotation?.lines || []).map((line, idx) => ({
      partId: line.requisitionLineId != null ? line.requisitionLineId : undefined,
      subcomponentId: undefined,
      manualItemName:
        (line.requisitionLineId != null ? lineMetaById[line.requisitionLineId]?.name : null) ||
        line.lineItemName ||
        `Item ${idx + 1}`,
      quantity: line.quantity ?? 0,
      unitOfMeasurement: line.unitOfMeasurement ?? undefined,
      estimatedUnitPrice: line.unitPrice ?? undefined,
      remarks: line.remarks ?? undefined,
      notes: line.remarks ?? undefined,
    }))
  }, [comparisonRequiredLines, lineMetaById])

  const bestMatch = vendorQuotations.filter((vendor) => vendor.comparableInCompanyCurrency).length > 0
    ? vendorQuotations
      .filter((vendor) => vendor.comparableInCompanyCurrency)
      .reduce((prev, current) =>
        Number(current.comparisonAmount ?? Number.MAX_SAFE_INTEGER) <
          Number(prev.comparisonAmount ?? Number.MAX_SAFE_INTEGER)
          ? current
          : prev
      )
    : null

  const modalQuotationDetails = activeVendorForModal
    ? quotationDetailCache[activeVendorForModal.id] ?? null
    : null

  const priceBreakdown = modalLines.map((line, idx) => {
    const qty = line.quantity ?? 0
    const unit = line.estimatedUnitPrice ?? undefined
    const share = unit !== undefined ? unit * qty : undefined
    const label =
      line.manualItemName ||
      (line.partId ? `Part #${line.partId}` : line.subcomponentId ? `Subcomponent #${line.subcomponentId}` : `Item ${idx + 1}`)
    return { label, qty, share, unit }
  })

  const selectedLine = (() => {
    if (!selectedLineKey) return null
    const idx = modalLines.findIndex((line, i) =>
      `${line.partId ?? 'p'}-${line.subcomponentId ?? 's'}-${line.manualItemName ?? 'm'}-${i}` === selectedLineKey
    )
    return idx >= 0 ? modalLines[idx] : null
  })()

  const selectedLineQty = selectedLine?.quantity ?? null

  const totalRequisitionQuantity = selectedLineQty
    ?? modalQuotationDetails?.requiredQuantity
    ?? activeVendorForModal?.requisitionQuantity
    ?? null
  const resolvedTotalRequisitionQuantity = totalRequisitionQuantity ?? 0
  const displayTotalRequired = totalRequisitionQuantity ?? 'N/A'

  const currentVendorAllocation = activeVendorForModal
    ? allocationValues[selectedLineKey ? `${activeVendorForModal.id}-${selectedLineKey}` : `${activeVendorForModal.id}`] ?? activeVendorForModal.actualQuantityAssignedToVendor ?? 0
    : 0

  const allocatedOtherVendors = vendorQuotations.reduce((sum, vendor) => {
    if (!activeVendorForModal || vendor.id === activeVendorForModal.id) return sum
    return sum + (allocationValues[selectedLineKey ? `${vendor.id}-${selectedLineKey}` : `${vendor.id}`] ?? vendor.actualQuantityAssignedToVendor ?? 0)
  }, 0)

  const totalAllocatedQuantity = vendorQuotations.reduce((sum, vendor) => {
    return sum + (allocationValues[selectedLineKey ? `${vendor.id}-${selectedLineKey}` : `${vendor.id}`] ?? vendor.actualQuantityAssignedToVendor ?? 0)
  }, 0)

  const remainingQuantity = Math.max(
    resolvedTotalRequisitionQuantity - currentVendorAllocation,
    0
  )
  const isOverAllocated =
    resolvedTotalRequisitionQuantity > 0 && currentVendorAllocation > resolvedTotalRequisitionQuantity

  const getVendorColor = (vendor: string, index: number) => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']
    return colors[index % colors.length]
  }

  const renderVendorAmount = useCallback(
    (vendor: VendorQuotation, originalValue?: number | null, companyValue?: number | null) => {
      const resolvedOriginal = readFiniteAmount(originalValue)
      const resolvedCompany =
        readFiniteAmount(companyValue) ?? convertOriginalAmountToCompany(resolvedOriginal, vendor)
      if (resolvedCompany != null && vendor.comparableInCompanyCurrency) {
        return (
          <MoneyDisplay
            compact
            primaryAmount={resolvedCompany}
            primaryCurrency={vendor.comparisonCurrency ?? vendor.companyCurrencyCode}
            secondaryAmount={resolvedOriginal}
            secondaryCurrency={vendor.currency}
            exchangeRateDisplay={vendor.exchangeRateDisplay}
            sameCurrency={
              String(vendor.currency || '').toUpperCase() ===
              String(vendor.comparisonCurrency || vendor.companyCurrencyCode || '').toUpperCase()
            }
          />
        )
      }
      if (resolvedOriginal != null) {
        return (
          <div>
            <MoneyDisplay compact primaryAmount={resolvedOriginal} primaryCurrency={vendor.currency} sameCurrency />
            {vendor.comparisonUnavailableReason ? (
              <div className='text-warning fs-8 mt-1'>{vendor.comparisonUnavailableReason}</div>
            ) : null}
          </div>
        )
      }
      return <span className='text-muted'>-</span>
    },
    []
  )

  const formatVendorAmount = useCallback(
    (vendor: VendorQuotation, originalValue?: number | null, companyValue?: number | null) => {
      const resolvedOriginal = readFiniteAmount(originalValue)
      const resolvedCompany =
        readFiniteAmount(companyValue) ?? convertOriginalAmountToCompany(resolvedOriginal, vendor)
      if (resolvedCompany != null && vendor.comparableInCompanyCurrency) {
        const originalSuffix =
          resolvedOriginal != null ? ` | ${vendor.currency} ${resolvedOriginal.toFixed(2)}` : ''
        const rateSuffix = vendor.exchangeRateDisplay ? ` | ${vendor.exchangeRateDisplay}` : ''
        return `${vendor.comparisonCurrency || vendor.companyCurrencyCode || vendor.currency} ${resolvedCompany.toFixed(2)}${originalSuffix}${rateSuffix}`
      }
      if (resolvedOriginal != null) {
        return `${vendor.currency} ${resolvedOriginal.toFixed(2)}${vendor.comparisonUnavailableReason ? ` | ${vendor.comparisonUnavailableReason}` : ''
          }`
      }
      return '-'
    },
    []
  )

  const formatPastRfqTotalAmount = useCallback((selectedQuotations: any[]) => {
    if (!Array.isArray(selectedQuotations) || selectedQuotations.length === 0) {
      return 'N/A'
    }

    const totalsByCurrency = new Map<string, number>()
    selectedQuotations.forEach((quotation: any) => {
      const currency = resolveQuotationDisplayCurrency(quotation)
      const amount = Number(quotation?.totalAmount ?? 0)
      if (!Number.isFinite(amount)) return
      totalsByCurrency.set(currency, (totalsByCurrency.get(currency) || 0) + amount)
    })

    if (totalsByCurrency.size === 0) return 'N/A'
    if (totalsByCurrency.size === 1) {
      const [currency, total] = Array.from(totalsByCurrency.entries())[0]
      return `${currency} ${total.toFixed(2)}`
    }

    return Array.from(totalsByCurrency.entries())
      .map(([currency, total]) => `${currency} ${total.toFixed(2)}`)
      .join(' + ')
  }, [])

  const vendorSummaryRows: Array<{
    key: string
    label: string
    value: (vendor: VendorQuotation) => { original: number | null; company: number | null }
    highlight?: boolean
  }> = [
      {
        key: 'totalAmount',
        label: 'Total Amount',
        value: (vendor) => {
          const totals = computeVendorAwardTotals(vendor.id)
          return { original: totals.amountSum, company: totals.companyAmountSum }
        },
        highlight: true,
      },
      {
        key: 'lineGstVat',
        label: 'Line GST/VAT Total',
        value: (vendor) => {
          const totals = computeVendorAwardTotals(vendor.id)
          return { original: totals.taxSum, company: convertOriginalAmountToCompany(totals.taxSum, vendor) }
        },
      },
      {
        key: 'lineDiscount',
        label: 'Line Discount Total',
        value: (vendor) => {
          const totals = computeVendorAwardTotals(vendor.id)
          return { original: totals.discountSum, company: convertOriginalAmountToCompany(totals.discountSum, vendor) }
        },
      },
      {
        key: 'freight',
        label: 'Trucking/Freight Cost',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.freightCost),
          company: convertOriginalAmountToCompany(vendor.freightCost, vendor),
        }),
      },
      {
        key: 'packaging',
        label: 'Pkg & Handling Cost',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.packagingCost),
          company: convertOriginalAmountToCompany(vendor.packagingCost, vendor),
        }),
      },
      {
        key: 'truck',
        label: 'Truck Cost',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.truckCost),
          company: convertOriginalAmountToCompany(vendor.truckCost, vendor),
        }),
      },
      {
        key: 'barge',
        label: 'Barge/Workboat Cost',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.bargeCost),
          company: convertOriginalAmountToCompany(vendor.bargeCost, vendor),
        }),
      },
      {
        key: 'other',
        label: 'Other Cost',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.otherCost),
          company: convertOriginalAmountToCompany(vendor.otherCost, vendor),
        }),
      },
      {
        key: 'vatLogistics',
        label: 'VAT/GST on Logistic/Other',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.vatOnLogistics),
          company: convertOriginalAmountToCompany(vendor.vatOnLogistics, vendor),
        }),
      },
      {
        key: 'headerDiscount',
        label: 'Header Discount / D/C',
        value: (vendor) => ({
          original: readFiniteAmount(vendor.totalDiscount),
          company: convertOriginalAmountToCompany(vendor.totalDiscount, vendor),
        }),
      },
      {
        key: 'grandTotal',
        label: 'Grand Total',
        value: (vendor) => {
          const inline = computeVendorAwardTotals(vendor.id)
          const freight = readFiniteAmount(vendor.freightCost) ?? 0
          const packaging = readFiniteAmount(vendor.packagingCost) ?? 0
          const truck = readFiniteAmount(vendor.truckCost) ?? 0
          const barge = readFiniteAmount(vendor.bargeCost) ?? 0
          const other = readFiniteAmount(vendor.otherCost) ?? 0
          const vatLogistics = readFiniteAmount(vendor.vatOnLogistics) ?? 0
          const headerDiscount = readFiniteAmount(vendor.totalDiscount) ?? 0
          return {
            original:
              inline.amountSum + freight + packaging + truck + barge + other + vatLogistics - headerDiscount,
            company:
              inline.companyAmountSum +
              (convertOriginalAmountToCompany(freight, vendor) ?? 0) +
              (convertOriginalAmountToCompany(packaging, vendor) ?? 0) +
              (convertOriginalAmountToCompany(truck, vendor) ?? 0) +
              (convertOriginalAmountToCompany(barge, vendor) ?? 0) +
              (convertOriginalAmountToCompany(other, vendor) ?? 0) +
              (convertOriginalAmountToCompany(vatLogistics, vendor) ?? 0) -
              (convertOriginalAmountToCompany(headerDiscount, vendor) ?? 0),
          }
        },
        highlight: true,
      },
    ]

  const stageOptions = useMemo<FilterOption[]>(() => {
    const base: FilterOption[] = [
      { value: '', label: 'All Stages' },
      { value: 'SUBMITTED', label: 'Submitted' },
      { value: 'PENDING_OWNER_APPROVAL', label: 'Pending Owner Approval' },
      { value: 'OWNER_APPROVED', label: 'Owner Approved' },
      { value: 'OWNER_REWORK', label: 'Owner Rework' },
      { value: 'OWNER_REJECTED', label: 'Owner Rejected' },
      { value: 'SENT_TO_VESSEL', label: 'Sent To Vessel' },
      { value: 'SENT_TO_VESSEL_FOR_SELECTION', label: 'Sent To Vessel (Selection)' },
      { value: 'VESSEL_SELECTED', label: 'Vessel Selected' },
      { value: 'VESSEL_REJECTED', label: 'Vessel Rejected' },
      { value: 'SENT_FOR_REWORK', label: 'Sent for Rework' },
      { value: 'REWORK_REQUESTED', label: 'Rework Requested' },
      { value: 'UPDATED_AFTER_REWORK', label: 'Updated After Rework' },
      { value: 'SELECTED', label: 'Selected' },
      { value: 'CLOSED', label: 'Closed' },
    ]
    if (canManageQuotationWorkflow && !canRunOwnerApprovalActions) {
      return base.filter((option) => !officeDeferredStatuses.has(option.value))
    }
    return base
  }, [canManageQuotationWorkflow, canRunOwnerApprovalActions, officeDeferredStatuses])

  const approvalPendingWithOptions: FilterOption[] = [
    { value: '', label: 'All' },
    { value: 'COMPANY', label: 'Company' },
    { value: 'VENDOR', label: 'Vendor' },
    { value: 'NONE', label: 'None' },
  ]

  useEffect(() => {
    if (stageFilter && !stageOptions.some((option) => option.value === stageFilter)) {
      setStageFilter('')
    }
  }, [stageFilter, stageOptions])

  const vesselOptions = useMemo<FilterOption[]>(() => {
    const recentVessel = String(comparisonRequisition?.vesselName || '').trim()
    const pastVessels = pastRFQs.map((rfq) => resolveVesselName(rfq?.requisition)).filter(Boolean)
    const values = Array.from(new Set([recentVessel, ...pastVessels].filter(Boolean))).sort()
    return [{ value: '', label: 'All Vessels' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [comparisonRequisition, pastRFQs])

  const vendorOptions = useMemo<FilterOption[]>(() => {
    const recentVendors = vendorQuotations.map((vendor) => vendor.vendor).filter(Boolean)
    const pastVendors = pastRFQs
      .flatMap((rfq) => (Array.isArray(rfq?.selectedQuotations) ? rfq.selectedQuotations : []))
      .map((quotation: any) => quotation?.vendor?.name)
      .filter(Boolean)
    const values = Array.from(new Set([...recentVendors, ...pastVendors])).sort()
    return [{ value: '', label: 'All Vendors' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [vendorQuotations, pastRFQs])

  const companyOptions = useMemo<FilterOption[]>(() => {
    const recentCompany = String(comparisonRequisition?.companyName || '').trim()
    const pastCompanies = pastRFQs
      .map((rfq) => (rfq?.requisition as any)?.category?.cgaid?.name || (rfq?.requisition as any)?.companyName)
      .filter(Boolean)
    const values = Array.from(new Set([recentCompany, ...pastCompanies].filter(Boolean))).sort()
    return [{ value: '', label: 'All Companies' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [comparisonRequisition, pastRFQs])

  const departmentOptions = useMemo<FilterOption[]>(() => {
    const recentDepartment = comparisonRequisition?.categoryName
    const pastDepartments = pastRFQs.map((rfq) => rfq?.requisition?.category?.name).filter(Boolean)
    const values = Array.from(new Set([recentDepartment, ...pastDepartments].filter(Boolean))).sort()
    return [{ value: '', label: 'All Departments' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [comparisonRequisition, pastRFQs])

  const catalogueOptions = useMemo<FilterOption[]>(() => {
    const recentCatalogue = comparisonRequisition?.categoryName
    const pastCatalogues = pastRFQs
      .map((rfq) => (rfq?.requisition as any)?.inventoryItemSubCategory?.name || rfq?.requisition?.category?.name)
      .filter(Boolean)
    const values = Array.from(new Set([recentCatalogue, ...pastCatalogues].filter(Boolean))).sort()
    return [{ value: '', label: 'All Catalogues' }, ...values.map((v) => ({ value: String(v), label: String(v) }))]
  }, [comparisonRequisition, pastRFQs])

  const filteredVendorQuotations = useMemo(() => {
    const selectedRfQRef = comparisonRfq?.title || ''
    const reqRef = comparisonRequisition?.code || ''
    const reqTitle = comparisonRequisition?.title || ''
    const vesselName = String(comparisonRequisition?.vesselName || '')
    const companyName = String(comparisonRequisition?.companyName || '')
    const departmentName = comparisonRequisition?.categoryName || ''

    return vendorQuotations.filter((vendor) => {
      const vendorText = `${vendor.vendor} ${vendor.vendorCode} ${vendor.deliveryPort}`.toLowerCase()
      const matchesSearch = searchTerm === '' || vendorText.includes(searchTerm.toLowerCase())
      const matchesVendor = vendorFilter === '' || vendor.vendor.toLowerCase().includes(vendorFilter.toLowerCase())
      const matchesCompany = companyFilter === '' || companyName.toLowerCase().includes(companyFilter.toLowerCase())
      const matchesReference =
        referenceFilter === '' ||
        selectedRfQRef.toLowerCase().includes(referenceFilter.toLowerCase()) ||
        reqRef.toLowerCase().includes(referenceFilter.toLowerCase())
      const matchesVessel =
        vesselFilter === '' || vesselName.toLowerCase().includes(vesselFilter.toLowerCase())
      const matchesDepartment =
        departmentFilter === '' ||
        departmentName.toLowerCase().includes(departmentFilter.toLowerCase())
      const matchesCatalogue =
        catalogueFilter === '' ||
        departmentName.toLowerCase().includes(catalogueFilter.toLowerCase())
      const matchesReqTitle =
        requisitionTitleFilter === '' ||
        reqTitle.toLowerCase().includes(requisitionTitleFilter.toLowerCase())
      const matchesRfqTitle =
        rfqTitleFilter === '' ||
        selectedRfQRef.toLowerCase().includes(rfqTitleFilter.toLowerCase())
      const pendingWith = vendor.status === 'SELECTED' ? 'NONE' : 'COMPANY'
      const matchesApprovalPending =
        approvalPendingWithFilter === '' || pendingWith === approvalPendingWithFilter
      const promisedDate = (
        quotations.find((q) => q.id === vendor.id)?.promisedDeliveryDate || ''
      ).slice(0, 10)
      const matchesDateFrom = poDateFromFilter === '' || promisedDate >= poDateFromFilter
      const matchesDateTo = poDateToFilter === '' || promisedDate <= poDateToFilter
      const deliveredFlag = vendor.status === 'SELECTED' ? 'YES' : 'NO'
      const matchesDelivered =
        deliveredToVesselFilter === '' || deliveredFlag === deliveredToVesselFilter
      const matchesStage = stageFilter === '' || vendor.status === stageFilter

      // For PENDING_OWNER_APPROVAL status, verify the user is in the queue
      let matchesOwnerQueue = true
      if (vendor.status === 'PENDING_OWNER_APPROVAL' && stageFilter === 'PENDING_OWNER_APPROVAL') {
        const quotation = quotations.find((q) => q.id === vendor.id)
        const queuedForRoleId = (quotation as any)?.queuedForRoleId
        const queuedForUserIds = (quotation as any)?.queuedForUserIds
        const currentUserRoleId = (currentUser as any)?.role?.id
        const currentUserId = (currentUser as any)?.id

        // Only show if user's role ID matches the queued role AND user is in the queue list
        if (queuedForRoleId && Array.isArray(queuedForUserIds) && queuedForUserIds.length > 0) {
          const userRoleMatches = currentUserRoleId && currentUserRoleId === queuedForRoleId
          const userInQueue = currentUserId && queuedForUserIds.includes(currentUserId)
          matchesOwnerQueue = Boolean(userRoleMatches && userInQueue)
        }
      }

      return (
        matchesSearch &&
        matchesVendor &&
        matchesCompany &&
        matchesReference &&
        matchesVessel &&
        matchesDepartment &&
        matchesCatalogue &&
        matchesReqTitle &&
        matchesRfqTitle &&
        matchesApprovalPending &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesDelivered &&
        matchesStage &&
        matchesOwnerQueue
      )
    })
  }, [
    vendorQuotations,
    comparisonRfq,
    comparisonRequisition,
    searchTerm,
    vendorFilter,
    companyFilter,
    referenceFilter,
    vesselFilter,
    departmentFilter,
    catalogueFilter,
    requisitionTitleFilter,
    rfqTitleFilter,
    approvalPendingWithFilter,
    poDateFromFilter,
    poDateToFilter,
    deliveredToVesselFilter,
    stageFilter,
    currentUser,
  ])

  const filteredPastRFQs = useMemo(() => {
    return pastRFQs.filter((rfq) => {
      const selectedQuotations = Array.isArray(rfq?.selectedQuotations)
        ? rfq.selectedQuotations
        : (rfq?.selectedQuotation ? [rfq.selectedQuotation] : [])
      const vendorText = selectedQuotations
        .map((quotation: any) => quotation?.vendor?.name)
        .filter(Boolean)
        .join(' ')
      const companyName =
        (rfq?.requisition as any)?.category?.cgaid?.name || (rfq?.requisition as any)?.companyName || ''
      const rfqText = `${rfq?.title || ''} ${vendorText}`.toLowerCase()
      const matchesSearch = searchTerm === '' || rfqText.includes(searchTerm.toLowerCase())
      const matchesVendor = vendorFilter === '' || vendorText.toLowerCase().includes(vendorFilter.toLowerCase())
      const matchesCompany =
        companyFilter === '' || companyName.toLowerCase().includes(companyFilter.toLowerCase())
      const matchesReference =
        referenceFilter === '' ||
        (rfq?.title || '').toLowerCase().includes(referenceFilter.toLowerCase()) ||
        (rfq?.requisition?.requisition_code || '').toLowerCase().includes(referenceFilter.toLowerCase())
      const matchesVessel =
        vesselFilter === '' ||
        resolveVesselName(rfq?.requisition).toLowerCase().includes(vesselFilter.toLowerCase())
      const matchesDepartment =
        departmentFilter === '' ||
        (rfq?.requisition?.category?.name || '').toLowerCase().includes(departmentFilter.toLowerCase())
      const matchesCatalogue =
        catalogueFilter === '' ||
        (rfq?.requisition?.category?.name || '').toLowerCase().includes(catalogueFilter.toLowerCase())
      const reqTitle = `${rfq?.requisition?.name || ''} ${rfq?.requisition?.title || ''}`.toLowerCase()
      const matchesReqTitle =
        requisitionTitleFilter === '' || reqTitle.includes(requisitionTitleFilter.toLowerCase())
      const matchesRfqTitle =
        rfqTitleFilter === '' ||
        (rfq?.title || '').toLowerCase().includes(rfqTitleFilter.toLowerCase())
      const matchesApprovalPending =
        approvalPendingWithFilter === '' || (approvalPendingWithFilter === 'NONE' && rfq?.status === 'CLOSED')
      const promisedDates = selectedQuotations
        .map((quotation: any) => String(quotation?.promisedDeliveryDate || '').slice(0, 10))
        .filter((date: string) => !!date)
      const latestPromisedDate =
        promisedDates.length > 0 ? promisedDates.sort((a: string, b: string) => b.localeCompare(a))[0] : ''
      const matchesDateFrom = poDateFromFilter === '' || latestPromisedDate >= poDateFromFilter
      const matchesDateTo = poDateToFilter === '' || latestPromisedDate <= poDateToFilter
      const deliveredFlag = rfq?.status === 'CLOSED' ? 'YES' : 'NO'
      const matchesDelivered =
        deliveredToVesselFilter === '' || deliveredFlag === deliveredToVesselFilter
      const matchesStage =
        stageFilter === '' || rfq?.status === stageFilter || (stageFilter === 'CLOSED' && rfq?.status === 'CLOSED')

      return (
        matchesSearch &&
        matchesVendor &&
        matchesCompany &&
        matchesReference &&
        matchesVessel &&
        matchesDepartment &&
        matchesCatalogue &&
        matchesReqTitle &&
        matchesRfqTitle &&
        matchesApprovalPending &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesDelivered &&
        matchesStage
      )
    })
  }, [
    pastRFQs,
    searchTerm,
    vendorFilter,
    companyFilter,
    referenceFilter,
    vesselFilter,
    departmentFilter,
    catalogueFilter,
    requisitionTitleFilter,
    rfqTitleFilter,
    approvalPendingWithFilter,
    poDateFromFilter,
    poDateToFilter,
    deliveredToVesselFilter,
    stageFilter,
  ])

  const visibleVendorQuotations = filteredVendorQuotations
  const bestVisibleMatch =
    visibleVendorQuotations.filter((vendor) => vendor.comparableInCompanyCurrency).length > 0
      ? visibleVendorQuotations
        .filter((vendor) => vendor.comparableInCompanyCurrency)
        .reduce((prev, current) =>
          Number(current.comparisonAmount ?? Number.MAX_SAFE_INTEGER) <
            Number(prev.comparisonAmount ?? Number.MAX_SAFE_INTEGER)
            ? current
            : prev
        )
      : null
  const maxVisiblePrice = Math.max(
    ...visibleVendorQuotations
      .filter((vendor) => vendor.comparableInCompanyCurrency)
      .map((v) => Number(v.comparisonAmount ?? 0)),
    1
  )
  const maxVisibleDays = Math.max(
    ...visibleVendorQuotations.map((v) => parseInt(v.deliveryTime, 10) || 0),
    1
  )
  const isAllocationLockedForRework = useCallback((status?: string) => {
    const normalized = String(status || '').toUpperCase()
    return normalized === 'SENT_FOR_REWORK' || normalized === 'REWORK_REQUESTED' || normalized === 'OWNER_REWORK'
  }, [])
  const visibleVendorIdSet = useMemo(
    () => new Set(visibleVendorQuotations.map((vendor) => vendor.id)),
    [visibleVendorQuotations]
  )
  const reworkLockedVendors = useMemo(
    () => visibleVendorQuotations.filter((vendor) => isAllocationLockedForRework(vendor.status)),
    [isAllocationLockedForRework, visibleVendorQuotations]
  )

  const quickSubmittedStatuses = useMemo(
    () =>
      new Set([
        'SUBMITTED',
        'PENDING_OWNER_APPROVAL',
        'SELECTED',
        'OWNER_APPROVED',
        'OWNER_REWORK',
        'OWNER_REJECTED',
        'SENT_TO_VESSEL',
        'SENT_TO_VESSEL_FOR_SELECTION',
        'SENT_FOR_REWORK',
        'VESSEL_SELECTED',
        'VESSEL_REJECTED',
        'REWORK_REQUESTED',
        'UPDATED_AFTER_REWORK',
      ]),
    []
  )
 
  const submittedQuickVendors = useMemo(
    () =>
      allVendorQuotations.filter((vendor) =>
        quickSubmittedStatuses.has(String(vendor.status || '').toUpperCase())
      ),
    [allVendorQuotations, quickSubmittedStatuses]
  )
 
  const revisionEligibleVendors = useMemo<RevisionVendorOption[]>(
    () =>
      submittedQuickVendors
        .filter((vendor) => canRequestQuotationReworkForStatus(vendor.status))
        .map((vendor) => ({
          quotationId: vendor.id,
          vendorName: vendor.vendor,
          status: String(vendor.status || 'SUBMITTED'),
          submittedAt: vendor.submittedAt ?? null,
        }))
        .filter((vendor) => Number.isFinite(vendor.quotationId) && vendor.quotationId > 0),
    [submittedQuickVendors]
  )
 
  const pendingQuickVendors = useMemo(
    () =>
      allVendorQuotations.filter(
        (vendor) => !quickSubmittedStatuses.has(String(vendor.status || '').toUpperCase())
      ),
    [allVendorQuotations, quickSubmittedStatuses]
  )

  useEffect(() => {
    if (visibleVendorQuotations.length === 0) {
      setDetailVendorId(null)
      return
    }
    const workflowSelectedVendor = visibleVendorQuotations.find(
      (vendor) => ['SELECTED', 'VESSEL_SELECTED'].includes(String(vendor.status || '').toUpperCase())
    )
    if (!detailVendorId || !visibleVendorQuotations.some((vendor) => vendor.id === detailVendorId)) {
      setDetailVendorId((workflowSelectedVendor || visibleVendorQuotations[0]).id)
    }
  }, [visibleVendorQuotations, detailVendorId])

  const detailVendor = useMemo(
    () => visibleVendorQuotations.find((vendor) => vendor.id === detailVendorId) || null,
    [visibleVendorQuotations, detailVendorId]
  )

  const detailQuotation = useMemo(
    () => (detailVendor ? quotations.find((quotation) => quotation.id === detailVendor.id) || null : null),
    [detailVendor, quotations]
  )
  const normalizedDetailQuotation = useMemo(
    () => (detailQuotation ? normalizeQuotationCommercialFields(detailQuotation) : null),
    [detailQuotation]
  )
  const poRaisedByCompanyValue = useMemo(
    () =>
      String(
        companyPoDefaults?.poRaisedByCompany ||
        comparisonRequisition?.companyName ||
        ''
      ).trim(),
    [comparisonRequisition?.companyName, companyPoDefaults]
  )
  const requisitionCreatedDateValue = useMemo(() => {
    return '-'
  }, [])
  const hasLineLevelAccountCodes = accountCodeMode === 'INDIVIDUAL'
  const usesIndividualLineAccountCodes = accountCodeMode === 'INDIVIDUAL'

  useEffect(() => {
    let cancelled = false
    const companyGroupAdminId = Number(comparisonRequisition?.companyGroupAdminId || 0)

    if (!companyGroupAdminId) {
      setCompanyBillingAddresses([])
      return
    }

    void getInventoryAddresses(companyGroupAdminId, true)
      .then((addresses) => {
        if (!cancelled) setCompanyBillingAddresses(addresses || [])
      })
      .catch(() => {
        if (!cancelled) setCompanyBillingAddresses([])
      })

    return () => {
      cancelled = true
    }
  }, [comparisonRequisition?.companyGroupAdminId])

  const defaultBillingAddressDisplay = useMemo(() => {
    const quotationAddress = String(
      (detailQuotation as any)?.billingAddressDisplay ||
      (detailQuotation as any)?.billingAddressLabel ||
      detailVendor?.billingAddressDisplay ||
      detailVendor?.billingAddressLabel ||
      ''
    ).trim()
    if (quotationAddress) return quotationAddress

    const billingAddress = companyBillingAddresses.find((address) => {
      const type = String(address.addressType || 'BOTH').toUpperCase()
      return type === 'BILLING' || type === 'BOTH'
    })
    return String(
      billingAddress?.fullAddress ||
      billingAddress?.displayLabel ||
      billingAddress?.addressName ||
      ''
    ).trim()
  }, [companyBillingAddresses, detailQuotation, detailVendor])

  const billingAddressOptions = useMemo(
    () => companyBillingAddresses.filter((address) => {
      const type = String(address.addressType || 'BOTH').toUpperCase()
      return type === 'BILLING' || type === 'BOTH'
    }),
    [companyBillingAddresses]
  )

  const defaultBillingAddressId = useMemo(() => {
    const quotationAddressId = Number((detailQuotation as any)?.billingAddressId || 0)
    if (quotationAddressId > 0 && billingAddressOptions.some((address) => address.id === quotationAddressId)) {
      return quotationAddressId
    }
    return billingAddressOptions[0]?.id ?? null
  }, [billingAddressOptions, detailQuotation])

  useEffect(() => {
    setBillingAddressDraft(defaultBillingAddressDisplay)
    setBillingAddressIdDraft(defaultBillingAddressId)
  }, [defaultBillingAddressDisplay, defaultBillingAddressId])



  useEffect(() => {
    if (!detailVendor || !detailQuotation) return

    const accountCodeFallback =
      Object.values(lineMetaById)
        .map((meta) => String(meta.accountCode || '').trim())
        .find((value) => value.length > 0 && value !== '-') || ''
    const autoPortCall =
      comparisonRfq?.deliveryPortName ||
      detailVendor.deliveryPort ||
      String(comparisonRequisition?.vesselName || '') ||
      ''
    const autoTagReference =
      comparisonRequisition?.code ||
      comparisonRfq?.title ||
      ''
    const autoTerms =
      normalizedDetailQuotation?.terms || ''
    const autoAgentInstructions =
      ''
    const autoApprovalRemark = detailVendor.vesselForwardRemark || ''
    const currentAutofillKey = `${detailVendor.id}:${detailQuotation.id}:${selectedRFQ}:${comparisonRequisition?.companyGroupAdminId ?? 'none'}:${companyPoDefaults?.updatedAt || companyPoDefaults?.id || 'pending'}`

    if (deliveryAutofillKeyRef.current !== currentAutofillKey) {
      setPoDateDraft((detailQuotation as any)?.poDate?.slice(0, 10) || '')
      setSupplierReferenceDraft((detailQuotation as any)?.supplierReference || '')
      setInvoiceAddressedToDraft(
        (detailQuotation as any)?.invoiceAddressedTo ||
        companyPoDefaults?.invoiceAddressedTo ||
        comparisonRequisition?.companyName ||
        ''
      )
      setBillingAddressDraft(defaultBillingAddressDisplay)
      setBillingAddressIdDraft(defaultBillingAddressId)
      setPoRaisedByUserDraft(
        String(
          (detailQuotation as any)?.poRaisedByUser ||
          companyPoDefaults?.poRaisedByUser ||
          resolveProcurementLoggedInDisplayName(currentUser as any, auth as any) ||
          ''
        )
      )
      setAccountCategoryDraft(
        (detailQuotation as any)?.accountCategory ||
        companyPoDefaults?.accountCategory ||
        comparisonRequisition?.categoryName ||
        ''
      )
      // setBudgetCodeDraft(accountCodeFallback)
      if (typeof (detailQuotation as any)?.ownerApprovalRequired === 'boolean') {
        setOwnerApprovalRequiredDraft(Boolean((detailQuotation as any)?.ownerApprovalRequired))
      } else {
        setOwnerApprovalRequiredDraft(Boolean(companyPoDefaults?.ownerApprovalRequired))
      }
      setOwnerRemarksDraft(detailVendor.ownerActionRemark || '')
      setDeliveryPortDraft(
        comparisonRfq?.deliveryPortName ||
        detailVendor.deliveryPort ||
        ''
      )
      setDeliveryDateDraft(
        (detailQuotation?.promisedDeliveryDate ||
          comparisonRequisition?.requiredDeliveryDate ||
          '').slice(0, 10)
      )
      setPortCallDraft(autoPortCall)
      // setVoyageNumberDraft(autoVoyageNumber) // reserved for future voyage/job linkage
      setTagReferenceDraft(autoTagReference)
      setPoTermsDraft(autoTerms)
      setAgentInstructionsDraft(autoAgentInstructions)
      setApprovalRemarksDraft(autoApprovalRemark)
      // setPoLogisticAccountCode(accountCodeFallback)
      setCommonBudgetAccountCode(accountCodeFallback)
      setCommonBudgetAccountCodeId(resolveAccountIdFromCode(accountCodeFallback))
      setCommonLogisticAccountCode('')
      setCommonLogisticAccountCodeId(null)
      setCommonLogisticSubaccountCode('')
      setCommonLogisticSubaccountCodeId(null)
      setBudgetCodeDraft(accountCodeFallback)
      deliveryAutofillKeyRef.current = currentAutofillKey
    }
  }, [comparisonRfq, comparisonRequisition, detailVendor, normalizedDetailQuotation, detailQuotation, lineMetaById, currentUser, auth, companyPoDefaults, defaultBillingAddressDisplay, defaultBillingAddressId])

  useEffect(() => {
    const fallback = resolveProcurementLoggedInDisplayName(currentUser as any, auth as any)
    if (!String(poRaisedByUserDraft || '').trim() && fallback) {
      setPoRaisedByUserDraft(fallback)
    }
  }, [currentUser, auth, poRaisedByUserDraft])

  const comparisonRows = useMemo(() => {
    const rowMap = new Map<string, ComparisonRow>()

    Object.entries(lineMetaById).forEach(([lineIdKey, meta]) => {
      const lineId = Number(lineIdKey)
      const rowKey = `line-${lineId}`
      const reqQtyValue = Number(meta.reqQty ?? 0)
      const selectedAccounting = rowAccountingValues[rowKey]
      rowMap.set(rowKey, {
        rowKey,
        lineId,
        partNo: meta.partNo || `Part #${lineId}`,
        itemName: meta.name || `Line #${lineId}`,
        unit: meta.unit || '-',
        rob: meta.rob != null ? String(meta.rob) : '-',
        reqQty: meta.reqQty != null ? String(meta.reqQty) : '-',
        reqQtyValue: Number.isFinite(reqQtyValue) && reqQtyValue > 0 ? reqQtyValue : 0,
        accountCodeId:
          selectedAccounting?.accountCodeId ??
          (meta.accountCodeId != null ? Number(meta.accountCodeId) : null),
        accountCode:
          selectedAccounting?.accountCode ||
          meta.accountCode ||
          '-',
        subaccountCodeId:
          selectedAccounting?.subaccountCodeId ??
          (meta.subaccountCodeId != null ? Number(meta.subaccountCodeId) : null),
        subaccountCode:
          selectedAccounting?.subaccountCode ||
          meta.subaccountCode ||
          '-',
        vendorLineMap: {},
        orderQtyValue: 0,
      })
    })

    visibleVendorQuotations.forEach((vendor) => {
      ; (vendor.lines || []).forEach((line, lineIndex) => {
        const lineId = line.requisitionLineId ?? null
        const rowKey = lineId != null ? `line-${lineId}` : `vendor-${vendor.id}-line-${lineIndex}`
        const meta = lineId != null ? lineMetaById[lineId] : undefined
        const lineQty = Number(line.assignedQuantity ?? line.quantity ?? 0)
        const selectedAccounting = rowAccountingValues[rowKey]
        const resolvedAccountCode =
          selectedAccounting?.accountCode ||
          line?.accountCode ||
          meta?.accountCode ||
          '-'
        const resolvedSubaccountCode =
          selectedAccounting?.subaccountCode ||
          line?.subaccountCode ||
          meta?.subaccountCode ||
          '-'
        const existing = rowMap.get(rowKey) || {
          rowKey,
          lineId,
          partNo: meta?.partNo || (lineId != null ? `Part #${lineId}` : '-'),
          itemName: meta?.name || line.lineItemName || `Item ${lineIndex + 1}`,
          unit: line.unit || meta?.unit || '-',
          rob: meta?.rob != null ? String(meta.rob) : '-',
          reqQty: meta?.reqQty != null ? String(meta.reqQty) : (line.quantity != null ? String(line.quantity) : '-'),
          reqQtyValue:
            meta?.reqQty != null && Number.isFinite(Number(meta.reqQty))
              ? Math.max(0, Number(meta.reqQty))
              : (Number.isFinite(lineQty) && lineQty > 0 ? lineQty : 0),
          accountCodeId:
            selectedAccounting?.accountCodeId ??
            (line?.accountCodeId != null
              ? Number(line.accountCodeId)
              : (meta?.accountCodeId != null ? Number(meta.accountCodeId) : null)),
          accountCode: resolvedAccountCode,
          subaccountCodeId:
            selectedAccounting?.subaccountCodeId ??
            (line?.subaccountCodeId != null
              ? Number(line.subaccountCodeId)
              : (meta?.subaccountCodeId != null ? Number(meta.subaccountCodeId) : null)),
          subaccountCode: resolvedSubaccountCode,
          vendorLineMap: {} as Record<number, QuotationLineDto | null>,
          orderQtyValue: 0,
        }

        existing.vendorLineMap[vendor.id] = line
        const qty = Number(line.assignedQuantity ?? line.quantity ?? 0)
        const safeQty = Number.isFinite(qty) ? qty : 0
        existing.orderQtyValue = Math.max(existing.orderQtyValue, safeQty)
        rowMap.set(rowKey, existing)
      })
    })

    return Array.from(rowMap.values()).sort((a, b) => {
      const aNum = a.lineId ?? Number.MAX_SAFE_INTEGER
      const bNum = b.lineId ?? Number.MAX_SAFE_INTEGER
      return aNum - bNum
    })
  }, [lineMetaById, rowAccountingValues, visibleVendorQuotations])

  const resolveCompanyScopeContext = useCallback(() => {
    const requisitionCgaId = Number(
      comparisonRequisition?.companyGroupAdminId || 0
    )
    const userCgaId = Number(
      (currentUser as any)?.companyGroupAdminId ||
      (currentUser as any)?.companyGroupAdmin?.id ||
      (currentUser as any)?.cgaid?.id ||
      (currentUser as any)?.cgaid ||
      (currentUser as any)?.roleEntityId ||
      (currentUser as any)?.caid?.cgaid?.id ||
      0
    )
    const cgaId = requisitionCgaId > 0 ? requisitionCgaId : userCgaId
    const vesselId = Number(comparisonRequisition?.vesselId || 0)
    return {
      cgaId: cgaId > 0 ? cgaId : undefined,
      vesselId: vesselId > 0 ? vesselId : undefined,
    }
  }, [comparisonRequisition, currentUser])

  useEffect(() => {
    if (!selectedRFQ) {
      setComparisonAccountCodes([])
      return
    }

    const dedupeAccounts = (items: AccountingAccount[]) => {
      const map = new Map<number, AccountingAccount>()
      items.forEach((item) => {
        const id = Number(item?.id)
        if (Number.isFinite(id) && id > 0 && !map.has(id)) {
          map.set(id, item)
        }
      })
      return Array.from(map.values())
    }

    const loadAccountCodes = async () => {
      try {
        const { cgaId, vesselId } = resolveCompanyScopeContext()

        let filteredAccounts: AccountingAccount[] = []
        let allAccounts: AccountingAccount[] = []

        try {
          if (cgaId) {
            filteredAccounts = await getAccountingAccountsWithParams(
              cgaId,
              vesselId
            )
          }
        } catch (error) {
          console.warn('Filtered account code load failed, will fallback to all accounts', error)
        }

        try {
          allAccounts = await getAllAccountingAccounts()
        } catch (error) {
          console.warn('All account code load failed', error)
        }

        const merged = dedupeAccounts([
          ...(Array.isArray(filteredAccounts) ? filteredAccounts : []),
          ...(Array.isArray(allAccounts) ? allAccounts : []),
        ])

        setComparisonAccountCodes(merged)
      } catch (error) {
        console.error('Failed to load account codes for quotation comparison grid', error)
        setComparisonAccountCodes([])
      }
    }

    loadAccountCodes()
  }, [resolveCompanyScopeContext, selectedRFQ])

  const accountCodeOptions = useMemo(
    () =>
      comparisonAccountCodes
        .map((account) => ({
          id: Number(account?.id),
          code: String(account?.accountCodes || '').trim(),
        }))
        .filter((account) => Number.isFinite(account.id) && account.id > 0 && account.code.length > 0),
    [comparisonAccountCodes]
  )

  const accountCodeById = useMemo(() => {
    const map = new Map<number, string>()
    accountCodeOptions.forEach((account) => map.set(account.id, account.code))
    return map
  }, [accountCodeOptions])

  const resolveAccountIdFromCode = useCallback(
    (accountCode?: string | null): number | null => {
      const normalized = String(accountCode || '').trim().toLowerCase()
      if (!normalized) return null
      const found = accountCodeOptions.find((account) => account.code.toLowerCase() === normalized)
      return found ? found.id : null
    },
    [accountCodeOptions]
  )

  const resolveContextIdsForAccounting = useCallback(() => resolveCompanyScopeContext(), [resolveCompanyScopeContext])

  useEffect(() => {
    if (!selectedRFQ || !comparisonRequisition?.companyGroupAdminId) {
      setCompanyPoDefaults(null)
      return
    }

    const { cgaId } = resolveCompanyScopeContext()
    const quotationScope = getProcurementScope(PROCUREMENT_PERMS.QUOTATION_VIEW)
    if (quotationScope === 'ALL' && !cgaId) {
      setCompanyPoDefaults(null)
      return
    }

    let active = true
    const loadCompanyPoDefaults = async () => {
      try {
        const data = await getCompanyPoInfo(cgaId)
        if (active) {
          setCompanyPoDefaults(data || null)
        }
      } catch (error) {
        console.error('Failed to load company PO defaults', error)
        if (active) {
          setCompanyPoDefaults(null)
        }
      }
    }

    loadCompanyPoDefaults()
    return () => {
      active = false
    }
  }, [comparisonRequisition?.companyGroupAdminId, getProcurementScope, resolveCompanyScopeContext, selectedRFQ])

  const ensureSubAccountsForAccount = useCallback(
    async (accountCodeId: number) => {
      if (!(accountCodeId > 0) || comparisonSubAccounts[accountCodeId]) return
      const { cgaId, vesselId } = resolveContextIdsForAccounting()
      try {
        const subaccounts = await getSubAccounts(accountCodeId, cgaId, vesselId)
        setComparisonSubAccounts((prev) => ({
          ...prev,
          [accountCodeId]: Array.isArray(subaccounts) ? subaccounts : [],
        }))
      } catch (error) {
        console.error(`Failed to load subaccounts for account ${accountCodeId}`, error)
        setComparisonSubAccounts((prev) => ({
          ...prev,
          [accountCodeId]: [],
        }))
      }
    },
    [comparisonSubAccounts, resolveContextIdsForAccounting]
  )

  useEffect(() => {
    const id = commonBudgetAccountCodeId
    if (id && id > 0) {
      void ensureSubAccountsForAccount(id)
    }
  }, [commonBudgetAccountCodeId, ensureSubAccountsForAccount])

  useEffect(() => {
    if (comparisonRows.length === 0) return
    const accountCodesLoaded = accountCodeOptions.length > 0
    setRowAccountingValues((prev) => {
      const next = { ...prev }
      let changed = false
      comparisonRows.forEach((row) => {
        const existing = next[row.rowKey]
        if (existing) {
          if (existing.accountCodeId == null && accountCodesLoaded) {
            const resolvedId = resolveAccountIdFromCode(existing.accountCode || row.accountCode)
            if (resolvedId) {
              next[row.rowKey] = {
                ...existing,
                accountCodeId: resolvedId,
                accountCode: existing.accountCode || accountCodeById.get(resolvedId) || '',
              }
              changed = true
            }
          }
          return
        }
        const accountCodeId =
          row.accountCodeId ??
          (accountCodesLoaded ? resolveAccountIdFromCode(row.accountCode) : null)
        next[row.rowKey] = {
          accountCodeId,
          accountCode:
            row.accountCode && row.accountCode !== '-'
              ? row.accountCode
              : accountCodeId
                ? accountCodeById.get(accountCodeId) || ''
                : '',
          subaccountCodeId: row.subaccountCodeId ?? null,
          subaccountCode:
            row.subaccountCode && row.subaccountCode !== '-' ? row.subaccountCode : '',
        }
        changed = true
      })
      return changed ? next : prev
    })
  }, [accountCodeById, accountCodeOptions.length, comparisonRows, resolveAccountIdFromCode])

  useEffect(() => {
    const accountIdsToLoad = Array.from(
      new Set(
        Object.values(rowAccountingValues)
          .map((selection) => Number(selection?.accountCodeId || 0))
          .filter((id) => id > 0 && !comparisonSubAccounts[id])
      )
    )
    if (accountIdsToLoad.length === 0) return
    accountIdsToLoad.forEach((accountId) => {
      ensureSubAccountsForAccount(accountId)
    })
  }, [comparisonSubAccounts, ensureSubAccountsForAccount, rowAccountingValues])

  useEffect(() => {
    const id = commonLogisticAccountCodeId
    if (id != null && id > 0) {
      void ensureSubAccountsForAccount(id)
    }
  }, [commonLogisticAccountCodeId, ensureSubAccountsForAccount])

  const getInlineAllocationKey = useCallback((rowKey: string, vendorId: number) => {
    return `${rowKey}-${vendorId}`
  }, [])

  const getInlineAllocationValue = useCallback(
    (rowKey: string, vendorId: number) => {
      const key = getInlineAllocationKey(rowKey, vendorId)
      return inlineAllocationValues[key] ?? 0
    },
    [getInlineAllocationKey, inlineAllocationValues]
  )

  const computeVendorAwardTotals = useCallback(
    (vendorId: number) => {
      let baseSum = 0
      let discountSum = 0
      let taxSum = 0
      let amountSum = 0
      let companyAmountSum = 0
      comparisonRows.forEach((row) => {
        const line = row.vendorLineMap[vendorId]
        if (!line) return
        const qty = Math.max(0, Number(getInlineAllocationValue(row.rowKey, vendorId) ?? line.assignedQuantity ?? 0))
        const unitPrice = Number(line?.unitPrice ?? 0)
        const discountPct = Number(line?.discountPercent ?? 0)
        const gstPct = Number(line?.gstPercent ?? 0)
        const base = Number.isFinite(qty) && Number.isFinite(unitPrice) ? qty * unitPrice : 0
        const discount = Number.isFinite(discountPct) ? (base * discountPct) / 100 : 0
        const taxable = base - discount
        const tax = Number.isFinite(gstPct) ? (taxable * gstPct) / 100 : 0
        const currentAmount = calculateQuotationLineAmount(qty, unitPrice, discountPct, gstPct)
        const currentCompanyAmount = convertOriginalAmountToCompany(currentAmount, quotations.find((quotation) => quotation.id === vendorId) || {})
        baseSum += base
        discountSum += discount
        taxSum += tax
        amountSum += currentAmount
        companyAmountSum += Number(currentCompanyAmount ?? 0)
      })
      return {
        baseSum,
        discountSum,
        taxSum,
        amountSum,
        companyAmountSum,
      }
    },
    [comparisonRows, getInlineAllocationValue, quotations]
  )

  const rowAllocationSummary = useMemo(() => {
    const summary: Record<string, { allocated: number; remaining: number; over: boolean }> = {}
    comparisonRows.forEach((row) => {
      const allocated = visibleVendorQuotations.reduce((sum, vendor) => {
        const line = row.vendorLineMap[vendor.id]
        if (!line) return sum
        const value = getInlineAllocationValue(row.rowKey, vendor.id)
        return sum + (Number.isFinite(value) ? Math.max(0, value) : 0)
      }, 0)
      const remaining = Math.max(row.reqQtyValue - allocated, 0)
      summary[row.rowKey] = {
        allocated,
        remaining,
        over: row.reqQtyValue > 0 && allocated > row.reqQtyValue,
      }
    })
    return summary
  }, [comparisonRows, getInlineAllocationValue, visibleVendorQuotations])

  const overallAllocationSummary = useMemo(() => {
    const required = comparisonRows.reduce((sum, row) => sum + (row.reqQtyValue > 0 ? row.reqQtyValue : 0), 0)
    const allocated = comparisonRows.reduce((sum, row) => {
      const rowAlloc = rowAllocationSummary[row.rowKey]?.allocated ?? 0
      return sum + rowAlloc
    }, 0)
    return {
      required,
      allocated,
      remaining: Math.max(required - allocated, 0),
      overAllocated: required > 0 && allocated > required,
    }
  }, [comparisonRows, rowAllocationSummary])

  const singleComparisonRow = comparisonRows.length === 1 ? comparisonRows[0] : null
  const isSingleItemRfq = !!singleComparisonRow

  const fixedColumnWidths = useMemo(() => {
    const widths = [
      56, // S.No
      104, // Part #
      190, // Item Name / Description
      64, // Unit
    ]
    // Only include account code columns in INDIVIDUAL mode
    if (accountCodeMode === 'INDIVIDUAL') {
      widths.push(150) // Account code
      widths.push(170) // Subaccount code
    }
    return widths
  }, [accountCodeMode])

  const fixedColumnLefts = useMemo(() => {
    const offsets: number[] = []
    let left = 0
    fixedColumnWidths.forEach((width) => {
      offsets.push(left)
      left += width
    })
    return offsets
  }, [fixedColumnWidths])

  const getFixedHeaderStyle = useCallback((columnIndex: number): CSSProperties => {
    const isLastFixed = columnIndex === fixedColumnWidths.length - 1
    return {
      position: 'sticky',
      left: fixedColumnLefts[columnIndex],
      zIndex: 20,
      minWidth: `${fixedColumnWidths[columnIndex]}px`,
      width: `${fixedColumnWidths[columnIndex]}px`,
      background: '#f8f9fb',
      boxShadow: isLastFixed ? '2px 0 0 #d8deea' : undefined,
      borderRight: isLastFixed ? '2px solid #d8deea' : undefined,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }
  }, [fixedColumnLefts, fixedColumnWidths])

  const getFixedBodyStyle = useCallback((columnIndex: number, rowIndex: number): CSSProperties => {
    const isLastFixed = columnIndex === fixedColumnWidths.length - 1
    const rowBg = rowIndex % 2 === 0 ? '#ffffff' : '#f8fbff'
    return {
      position: 'sticky',
      left: fixedColumnLefts[columnIndex],
      zIndex: 10,
      minWidth: `${fixedColumnWidths[columnIndex]}px`,
      width: `${fixedColumnWidths[columnIndex]}px`,
      background: rowBg,
      boxShadow: isLastFixed ? '2px 0 0 #d8deea' : undefined,
      borderRight: isLastFixed ? '2px solid #d8deea' : undefined,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }
  }, [fixedColumnLefts, fixedColumnWidths])

  // Pagination logic
  const indexOfLastRecord = currentPage * rowsPerPage
  const indexOfFirstRecord = indexOfLastRecord - rowsPerPage
  const currentRecords = filteredVendorQuotations.slice(indexOfFirstRecord, indexOfLastRecord)
  const totalPages = Math.max(1, Math.ceil(filteredVendorQuotations.length / rowsPerPage))
  const filteredPastTotalElements = filteredPastRFQs.length
  const pagedPastRFQs = filteredPastRFQs.slice(
    (pastCurrentPage - 1) * pastRowsPerPage,
    pastCurrentPage * pastRowsPerPage
  )
  const pastTotalPages = Math.max(1, Math.ceil(filteredPastTotalElements / pastRowsPerPage))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  useEffect(() => {
    if (pastCurrentPage > pastTotalPages) {
      setPastCurrentPage(pastTotalPages)
    }
  }, [pastCurrentPage, pastTotalPages])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearchTerm('')
    setVendorFilter('')
    setCompanyFilter('')
    setReferenceFilter('')
    setVesselFilter('')
    setDepartmentFilter('')
    setCatalogueFilter('')
    setRequisitionTitleFilter('')
    setRfqTitleFilter('')
    setApprovalPendingWithFilter('')
    setPoDateFromFilter('')
    setPoDateToFilter('')
    setDeliveredToVesselFilter('')
    setStageFilter('')
    setCurrentPage(1)
    setPastCurrentPage(1)
  }

  const applyVendorAward = async (
    quotationId: number,
    assignedQty: number,
    isAlreadySelected: boolean,
    assignedLines?: QuotationAwardLineAssignment[]
  ) => {
    if (isAlreadySelected) {
      await updateQuotationAward(quotationId, assignedQty, assignedLines)
      return
    }
    await selectQuotation(quotationId, assignedQty, assignedLines)
  }

  const buildAssignedLinesForVendor = useCallback(
    (vendorId: number): QuotationAwardLineAssignment[] => {
      const allocations: QuotationAwardLineAssignment[] = []

      comparisonRows.forEach((row) => {
        const line = row.vendorLineMap[vendorId]
        if (!line || row.lineId == null) return

        const assigned = getInlineAllocationValue(row.rowKey, vendorId)
        if (assigned <= 0) return

        if (accountCodeMode === 'COMMON') {
          allocations.push({
            requisitionLineId: row.lineId,
            assignedQuantity: assigned,
            accountCodeId:
              commonBudgetAccountCodeId !== null && commonBudgetAccountCodeId !== undefined
                ? Number(commonBudgetAccountCodeId)
                : null,
            accountCode:
              String(commonBudgetAccountCode || '').trim() || undefined,
            subaccountCodeId:
              commonBudgetSubaccountCodeId !== null && commonBudgetSubaccountCodeId !== undefined
                ? Number(commonBudgetSubaccountCodeId)
                : null,
            subaccountCode:
              String(commonBudgetSubaccountCode || '').trim() || undefined,
          })
          return
        }

        const rowAccounting = rowAccountingValues[row.rowKey]
        const accountCodeId =
          rowAccounting?.accountCodeId ??
          row.accountCodeId ??
          line.accountCodeId ??
          null

        const accountCode =
          rowAccounting?.accountCode ||
          row.accountCode ||
          line.accountCode ||
          undefined

        const subaccountCodeId =
          rowAccounting?.subaccountCodeId ??
          row.subaccountCodeId ??
          line.subaccountCodeId ??
          null

        const subaccountCode =
          rowAccounting?.subaccountCode ||
          row.subaccountCode ||
          line.subaccountCode ||
          undefined

        allocations.push({
          requisitionLineId: row.lineId,
          assignedQuantity: assigned,
          accountCodeId,
          accountCode: accountCode && accountCode !== '-' ? accountCode : undefined,
          subaccountCodeId,
          subaccountCode: subaccountCode && subaccountCode !== '-' ? subaccountCode : undefined,
        })
      })

      return allocations
    },
    [
      comparisonRows,
      getInlineAllocationValue,
      rowAccountingValues,
      accountCodeMode,
      commonBudgetAccountCodeId,
      commonBudgetAccountCode,
      commonBudgetSubaccountCodeId,
      commonBudgetSubaccountCode,
    ]
  )

  const activeVendorAssignedLines = useMemo(
    () => (activeVendorForModal ? buildAssignedLinesForVendor(activeVendorForModal.id) : []),
    [activeVendorForModal, buildAssignedLinesForVendor]
  )

  const activeVendorAccountingValidationError = useMemo(() => {
    const missingLines = collectMissingAssignedQuotationAccounting(activeVendorAssignedLines)
    if (missingLines.length === 0) {
      return null
    }
    return validateAssignedQuotationAccountingLines(activeVendorAssignedLines)
  }, [activeVendorAssignedLines])

  const handleSelectQuotation = async (
    quotationId: number,
    actualQuantityAssignedToVendor?: number,
    isAlreadySelected?: boolean,
    assignedLines?: QuotationAwardLineAssignment[]
  ) => {
    const statusFromApi =
      quotations.find((quotation) => quotation.id === quotationId)?.status ||
      allVendorQuotations.find((vendor) => vendor.id === quotationId)?.status ||
      vendorQuotations.find((vendor) => vendor.id === quotationId)?.status
    if (isAllocationLockedForRework(statusFromApi)) {
      toast.error('This vendor quotation is pending rework response and cannot be awarded yet.')
      return false
    }

    if (actualQuantityAssignedToVendor === undefined || actualQuantityAssignedToVendor === null) {
      toast.error('Please enter an allocation quantity before selecting.')
      return false
    }

    if (actualQuantityAssignedToVendor <= 0) {
      toast.error('Allocation must be greater than zero.')
      return false
    }

    try {
      const accountingValidationError = validateAssignedQuotationAccountingLines(assignedLines)
      if (accountingValidationError) {
        toast.error(accountingValidationError)
        return false
      }
      setLoading(true)
      await applyVendorAward(
        quotationId,
        actualQuantityAssignedToVendor,
        !!isAlreadySelected,
        assignedLines
      )
      // Refresh the quotations list after selection
      if (selectedRFQ) {
        await fetchQuotations(selectedRFQ)
      }
      toast.success('Quotation selected successfully!')
      return true
    } catch (error: any) {
      console.error('Error selecting quotation:', error)
      toast.error('Failed to select quotation')
      return false
    } finally {
      setLoading(false)
    }
  }

  const canOwnerApprove = useCallback((status: string) => {
    const normalized = String(status || '').toUpperCase()
    return (
      normalized === 'SUBMITTED' ||
      normalized === 'PENDING_OWNER_APPROVAL' ||
      normalized === 'SENT_FOR_REWORK' ||
      normalized === 'REWORK_REQUESTED' ||
      normalized === 'OWNER_REWORK' ||
      normalized === 'UPDATED_AFTER_REWORK'
    )
  }, [])

  const canOwnerReject = useCallback((status: string) => {
    const normalized = String(status || '').toUpperCase()
    return (
      normalized === 'SUBMITTED' ||
      normalized === 'PENDING_OWNER_APPROVAL' ||
      normalized === 'OWNER_APPROVED' ||
      normalized === 'SENT_TO_VESSEL' ||
      normalized === 'SENT_TO_VESSEL_FOR_SELECTION' ||
      normalized === 'UPDATED_AFTER_REWORK'
    )
  }, [])

  const canSendToVessel = useCallback((status: string) => {
    const normalized = String(status || '').toUpperCase()
    return normalized === 'OWNER_APPROVED' || normalized === 'SELECTED'
  }, [])

  const canRequestRework = useCallback((status: string) => {
    const normalized = String(status || '').toUpperCase()
    return (
      normalized === 'SUBMITTED' ||
      normalized === 'PENDING_OWNER_APPROVAL' ||
      normalized === 'OWNER_APPROVED' ||
      normalized === 'SENT_TO_VESSEL' ||
      normalized === 'SENT_TO_VESSEL_FOR_SELECTION' ||
      normalized === 'UPDATED_AFTER_REWORK'
    )
  }, [])

  const openActionDialog = (
    action: 'OWNER_APPROVE' | 'OWNER_REJECT' | 'SEND_TO_VESSEL' | 'REWORK',
    quotationId: number
  ) => {
    const configMap = {
      OWNER_APPROVE: {
        requireRemark: false,
        title: 'Owner Approval',
        placeholder: 'Optional approval note',
        submitLabel: 'Approve',
      },
      OWNER_REJECT: {
        requireRemark: true,
        title: 'Owner Rejection',
        placeholder: 'Enter rejection reason',
        submitLabel: 'Reject',
      },
      SEND_TO_VESSEL: {
        requireRemark: false,
        title: 'Send To Vessel',
        placeholder: 'Optional note for vessel',
        submitLabel: 'Send',
      },
      REWORK: {
        requireRemark: true,
        title: 'Request Rework',
        placeholder: 'Enter rework reason',
        submitLabel: 'Request Rework',
      },
    } as const
    setActionRemark('')
    setActionDialog({
      type: action,
      quotationId,
      ...configMap[action],
    })
  }

  const closeActionDialog = () => {
    setActionDialog(null)
    setActionRemark('')
  }

  const performQuotationAction = async (
    quotationId: number,
    action: 'OWNER_APPROVE' | 'OWNER_REJECT' | 'SEND_TO_VESSEL' | 'REWORK',
    remark?: string
  ) => {
    try {
      setLoading(true)
      if (action === 'OWNER_APPROVE') {
        await ownerApproveQuotation(quotationId, remark)
        toast.success('Quotation approved by owner')
      } else if (action === 'OWNER_REJECT') {
        await ownerRejectQuotation(quotationId, String(remark || '').trim())
        toast.success('Quotation rejected by owner')
      } else if (action === 'SEND_TO_VESSEL') {
        await sendQuotationToVessel(quotationId, String(remark || '').trim() || undefined)
        toast.success('Quotation sent to vessel')
      } else if (action === 'REWORK') {
        await requestQuotationRework(quotationId, String(remark || '').trim())
        toast.success('Rework requested from vendor')
      }
      if (selectedRFQ) {
        await fetchQuotations(selectedRFQ)
      }
    } catch (error: any) {
      console.error('Error performing quotation action:', error)
      toast.error('Failed to update quotation status')
    } finally {
      setLoading(false)
    }
  }
  const buildPoFormValidationErrors = useCallback(() => {
    const errors: Record<string, string> = {}

    if (!String(poDateDraft || '').trim()) {
      errors.poDate = 'PO Date is required'
    } else if (
      requisitionCreatedDateValue &&
      requisitionCreatedDateValue !== '-' &&
      String(poDateDraft) < String(requisitionCreatedDateValue)
    ) {
      errors.poDate = 'PO Date cannot be before Requisition Created Date'
    } else if (String(poDateDraft) > new Date().toISOString().slice(0, 10)) {
      errors.poDate = 'PO Date cannot be in the future'
    }
    if (!poRaisedByCompanyValue) {
      errors.poRaisedByCompany = 'PO Raised By Company is required'
    }
    if (!String(poRaisedByUserDraft || '').trim()) {
      errors.poRaisedByUser = 'PO Raised By User is required'
    }
    if (!String(invoiceAddressedToDraft || '').trim()) {
      errors.invoiceAddressedTo = 'Invoice Addressed To is required'
    }
    if (!String(accountCategoryDraft || '').trim()) {
      errors.accountCategory = 'Account Category is required'
    }

    if (accountCodeMode === 'COMMON') {
      if (!String(commonBudgetAccountCode || '').trim()) {
        errors.accountCode = 'Budget / Account Code is required'
      }
      if (!String(commonBudgetSubaccountCode || '').trim()) {
        errors.subaccountCode = 'Sub Account Code is required'
      }
    }

    if (accountCodeMode === 'INDIVIDUAL') {
      const hasAtLeastOneRowAccount = comparisonRows.some((row) => {
        const rowSelection = rowAccountingValues[row.rowKey]
        return !!String(
          rowSelection?.accountCode ||
          row.accountCode ||
          ''
        ).trim()
      })

      if (!hasAtLeastOneRowAccount) {
        errors.accountCode = 'At least one line-level Account Code is required'
      }

      if (!String(commonLogisticAccountCode || '').trim()) {
        errors.logisticAccountCode = 'Logistic Account Code is required'
      }
    }

    if (!String(deliveryDateDraft || '').trim()) {
      errors.deliveryDate = 'Delivery Date is required'
    }
    if (!String(deliveryPortDraft || '').trim()) {
      errors.deliveryPort = 'Delivery Port is required'
    }

    return errors
  }, [
    poDateDraft,
    poRaisedByCompanyValue,
    poRaisedByUserDraft,
    invoiceAddressedToDraft,
    accountCategoryDraft,
    accountCodeMode,
    commonBudgetAccountCode,
    commonBudgetSubaccountCode,
    commonLogisticAccountCode,
    comparisonRows,
    rowAccountingValues,
    deliveryDateDraft,
    deliveryPortDraft,
    requisitionCreatedDateValue,
  ])
  const submitActionDialog = async () => {
    if (!actionDialog) return
    const remark = actionRemark.trim()
    if (actionDialog.requireRemark && !remark) {
      toast.warning('Remark is required')
      return
    }
    await performQuotationAction(actionDialog.quotationId, actionDialog.type, remark || undefined)
    closeActionDialog()
  }

  const handleDownloadComparison = () => {
    if (!selectedRFQ) {
      toast.warning('Select an RFQ before downloading the comparison.')
      return
    }
    if (visibleVendorQuotations.length === 0 || comparisonRows.length === 0) {
      toast.warning('No comparison data is available for download.')
      return
    }
    setIsComparisonModalOpen(true)
  }

  const handleSubmitRevisionRequest = async (payload: { quotationIds: number[]; remarks: string }) => {
    if (!selectedRFQ) return
    try {
      setRevisionRequestSubmitting(true)
      await requestQuotationReworkForRfq(payload.quotationIds, payload.remarks)
      toast.success('Revision request sent to selected vendor(s)')
      setRevisionRequestModalOpen(false)
      await fetchQuotations(selectedRFQ)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send revision request')
    } finally {
      setRevisionRequestSubmitting(false)
    }
  }

  const handleApproveLogisticsRequest = useCallback(
    async (requestId: number) => {
      try {
        setLogisticsActionLoading(true)
        await approveVendorLogisticsChangeRequest(requestId)
        toast.success('Logistics pricing change approved')
        setActiveLogisticsRequest(null)
        await refreshPendingLogisticsRequests()
      } catch (error: any) {
        toast.error(error?.message || 'Failed to approve logistics request')
      } finally {
        setLogisticsActionLoading(false)
      }
    },
    [refreshPendingLogisticsRequests]
  )

  const handleRejectLogisticsRequest = useCallback(
    async (requestId: number, reason: string) => {
      try {
        setLogisticsActionLoading(true)
        await rejectVendorLogisticsChangeRequest(requestId, reason)
        toast.success('Logistics pricing change rejected')
        setActiveLogisticsRequest(null)
        await refreshPendingLogisticsRequests()
      } catch (error: any) {
        toast.error(error?.message || 'Failed to reject logistics request')
      } finally {
        setLogisticsActionLoading(false)
      }
    },
    [refreshPendingLogisticsRequests]
  )

  const handleCreatePOFromQuotation = async (
    quotationId: number,
    currentStatus: string,
    options?: {
      logisticAccountCode?: string
      logisticSubaccountCode?: string
      poDate?: string
      supplierReference?: string
      invoiceAddressedTo?: string
      billingAddressDisplay?: string
      poRaisedByCompany?: string
      poRaisedByUser?: string
      accountCategory?: string
      budgetCode?: string
      ownerApprovalRequired?: boolean
      ownerApprovalRemark?: string
      deliveryDate?: string
      deliveryTerms?: string
      terms?: string
      paymentTerms?: string
      specialInstructions?: string
      suppressSuccessToast?: boolean
      supplierReferenceFallback?: string
    }
  ): Promise<boolean> => {
    if (isAllocationLockedForRework(currentStatus)) {
      toast.warning('PO cannot be created while quotation is pending rework response.')
      return false
    }
    const normalizedStatus = String(currentStatus || '').toUpperCase()
    if (normalizedStatus !== 'SELECTED' && normalizedStatus !== 'VESSEL_SELECTED') {
      toast.warning('Only selected quotations can be used to create purchase orders')
      return false
    }

    const poDateValue = String(options?.poDate || '').trim()
    const invoiceAddressedToValue = String(options?.invoiceAddressedTo || '').trim()
    const poRaisedByCompanyValue = String(options?.poRaisedByCompany || '').trim()
    const poRaisedByUserValue = String(options?.poRaisedByUser || '').trim()
    const accountCategoryValue = String(options?.accountCategory || '').trim()
    const logisticAccountCodeValue = String(options?.logisticAccountCode || '').trim()
    const logisticSubaccountCodeValue = String(options?.logisticSubaccountCode || '').trim()
    const budgetCodeValue = String(options?.budgetCode || '').trim()
    const supplierReferenceValue = String(options?.supplierReference || '').trim()
    const deliveryDateValue = String(options?.deliveryDate || '').trim()
    const deliveryTermsValue = String(options?.deliveryTerms || '').trim()
    const termsValue = String(options?.terms || '').trim()
    const fallbackVendorCode = String(
      options?.supplierReferenceFallback ||
      detailQuotation?.vendor?.code ||
      detailQuotation?.vendor?.name ||
      detailVendor?.vendorCode ||
      ''
    ).trim()

    if (!poDateValue) {
      toast.warning('PO Date is mandatory before creating PO.')
      return false
    }
    if (
      requisitionCreatedDateValue &&
      requisitionCreatedDateValue !== '-' &&
      poDateValue < requisitionCreatedDateValue
    ) {
      toast.warning('PO Date cannot be before Requisition Created Date.')
      return false
    }
    if (poDateValue > new Date().toISOString().slice(0, 10)) {
      toast.warning('PO Date cannot be in the future.')
      return false
    }
    if (!invoiceAddressedToValue) {
      toast.warning('Invoice Addressed To is mandatory before creating PO.')
      return false
    }
    if (!poRaisedByCompanyValue) {
      toast.warning('PO Raised By Company is mandatory before creating PO.')
      return false
    }
    if (!poRaisedByUserValue) {
      toast.warning('PO Raised By User is mandatory before creating PO.')
      return false
    }
    if (!accountCategoryValue) {
      toast.warning('Account Category is mandatory before creating PO.')
      return false
    }
    if (accountCodeMode === 'COMMON' && !budgetCodeValue) {
      toast.warning('Budget / Account Code is mandatory before creating PO.')
      return false
    }
    if (accountCodeMode === 'COMMON' && !String(options?.logisticSubaccountCode || '').trim()) {
      toast.warning('Sub Account Code is mandatory before creating PO in common mode.')
      return false
    }

    if (accountCodeMode === 'INDIVIDUAL' && !logisticAccountCodeValue) {
      toast.warning('Logistic Account Code is mandatory before creating PO.')
      return false
    }
    if (!supplierReferenceValue && !fallbackVendorCode) {
      toast.warning('Supplier Reference is mandatory before creating PO.')
      return false
    }
    if (!deliveryDateValue) {
      toast.warning('Delivery Date is mandatory before creating PO.')
      return false
    }
    if (!deliveryTermsValue) {
      toast.warning('Delivery Port is mandatory before creating PO.')
      return false
    }

    try {
      const ownerApprovalRequired = Boolean(options?.ownerApprovalRequired)
      await createPurchaseOrderFromQuotation(quotationId, {
        logisticAccountCode: logisticAccountCodeValue || undefined,
        logisticSubaccountCode: logisticSubaccountCodeValue || undefined,
        poDate: poDateValue || undefined,
        supplierReference: supplierReferenceValue || fallbackVendorCode || undefined,
        invoiceAddressedTo: invoiceAddressedToValue || undefined,
        billingAddressDisplay: String(options?.billingAddressDisplay || '').trim() || undefined,
        poRaisedByCompany: poRaisedByCompanyValue || undefined,
        poRaisedByUser: poRaisedByUserValue || undefined,
        accountCategory: accountCategoryValue || undefined,
        budgetCode: budgetCodeValue || undefined,
        ownerApprovalRequired,
        ownerApprovalRemark: String(options?.ownerApprovalRemark || '').trim() || undefined,
        deliveryDate: deliveryDateValue || undefined,
        deliveryTerms: deliveryTermsValue || undefined,
        terms: options ? termsValue : undefined,
        paymentTerms: String(options?.paymentTerms || '').trim() || undefined,
        specialInstructions: options ? String(options?.specialInstructions ?? '').trim() : undefined,
      })
      if (!options?.suppressSuccessToast) {
        toast.success(
          ownerApprovalRequired
            ? 'PO submitted for owner approval successfully'
            : 'Purchase order created successfully'
        )
      }
      return true
    } catch (error: any) {
      console.error('Error creating purchase order from quotation:', error)
      toast.error('Failed to create purchase order')
      return false
    }
  }

  const buildPoCreateOptions = useCallback(
    (vendor: VendorQuotation) => ({
      // Logistics are ALWAYS their own fields.
      logisticAccountCode:
        String(commonLogisticAccountCode || '').trim() || undefined,

      logisticSubaccountCode:
        String(commonLogisticSubaccountCode || '').trim() || undefined,

      poDate: poDateDraft,

      supplierReference: supplierReferenceDraft,

      invoiceAddressedTo: invoiceAddressedToDraft,

      billingAddressDisplay: billingAddressDraft,


      poRaisedByCompany: poRaisedByCompanyValue,

      poRaisedByUser: poRaisedByUserDraft,

      accountCategory: accountCategoryDraft,

      // Budget code belongs ONLY to budget/item accounting.
      // In INDIVIDUAL mode the item-level accounting is stored
      // through assigned quotation lines instead.
      budgetCode:
        accountCodeMode === 'COMMON'
          ? String(commonBudgetAccountCode || '').trim() || undefined
          : undefined,

      ownerApprovalRequired: ownerApprovalRequiredDraft,

      ownerApprovalRemark: ownerRemarksDraft,

      deliveryDate: deliveryDateDraft,

      deliveryTerms: deliveryPortDraft,

      terms: poTermsDraft,

      paymentTerms: vendor.paymentTerms,

      specialInstructions: agentInstructionsDraft,

      supplierReferenceFallback:
        String(vendor.vendorCode || vendor.vendor || '').trim(),
    }),
    [
      accountCodeMode,

      commonLogisticAccountCode,
      commonLogisticSubaccountCode,

      commonBudgetAccountCode,

      poDateDraft,
      supplierReferenceDraft,
      invoiceAddressedToDraft,
      billingAddressDraft,
      poRaisedByCompanyValue,
      poRaisedByUserDraft,
      accountCategoryDraft,

      ownerApprovalRequiredDraft,
      ownerRemarksDraft,

      deliveryDateDraft,
      deliveryPortDraft,
      poTermsDraft,
      agentInstructionsDraft,
    ]
  )

  const openQuickActionMenu = useCallback((vendorId: number, event: ReactMouseEvent<HTMLButtonElement>) => {
    const trigger = event?.currentTarget as HTMLElement | null
    if (!trigger) {
      setQuickActionMenuState(null)
      return
    }
    const rect = trigger.getBoundingClientRect()
    const menuWidth = 190
    const menuHeight = 138
    const gap = 8
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    let left = rect.right - menuWidth
    if (left < gap) left = gap
    if (left + menuWidth > viewportWidth - gap) left = viewportWidth - menuWidth - gap

    let top = rect.bottom + 6
    if (top + menuHeight > viewportHeight - gap) {
      top = rect.top - menuHeight - 6
    }
    if (top < gap) top = gap

    setQuickActionMenuState({ vendorId, top, left })
  }, [])

  useEffect(() => {
    if (!quickActionMenuState) return

    const closeOnOutside = (event: Event) => {
      const target = event.target as HTMLElement | null
      if (!target) return
      if (target.closest('[data-quick-action-menu]')) return
      if (target.closest('[data-quick-action-trigger]')) return
      setQuickActionMenuState(null)
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setQuickActionMenuState(null)
      }
    }

    const closeOnViewportShift = () => setQuickActionMenuState(null)

    document.addEventListener('mousedown', closeOnOutside)
    document.addEventListener('touchstart', closeOnOutside)
    document.addEventListener('keydown', closeOnEscape)
    window.addEventListener('resize', closeOnViewportShift)
    window.addEventListener('scroll', closeOnViewportShift, true)

    return () => {
      document.removeEventListener('mousedown', closeOnOutside)
      document.removeEventListener('touchstart', closeOnOutside)
      document.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('resize', closeOnViewportShift)
      window.removeEventListener('scroll', closeOnViewportShift, true)
    }
  }, [quickActionMenuState])

  const activeQuickActionVendor = useMemo(() => {
    if (!quickActionMenuState) return null
    return submittedQuickVendors.find((vendor) => vendor.id === quickActionMenuState.vendorId) || null
  }, [quickActionMenuState, submittedQuickVendors])

  const handleFinalizeSelection = async () => {
    if (!selectedRFQ) {
      toast.error('Please select an RFQ first')
      return
    }

    const pendingReworkVendors = quotations.filter((quotation) =>
      isAllocationLockedForRework(quotation.status)
    )
    if (pendingReworkVendors.length > 0) {
      toast.error(
        `Cannot finalize while rework response is pending for: ${pendingReworkVendors
          .map((quotation) => quotation.vendor?.name || `Vendor ${quotation.vendorId}`)
          .join(', ')}`
      )
      return
    }
    const formErrors = buildPoFormValidationErrors()
    if (Object.keys(formErrors).length > 0) {
      setPoFormErrors(formErrors)
      toast.error(
        `Complete required PO details before finalizing: ${Object.values(formErrors).join(', ')}`
      )
      return
    }
    setPoFormErrors({})

    const invalidRows = comparisonRows.filter((row) => rowAllocationSummary[row.rowKey]?.over)
    if (invalidRows.length > 0) {
      toast.error('Allocation exceeds required quantity for one or more items')
      return
    }

    if (!window.confirm('Are you sure you want to finalize the selection? This action cannot be undone.')) {
      return
    }

    setLoading(true)
    try {
      const inlineTotals = visibleVendorQuotations.map((vendor) => {
        const total = comparisonRows.reduce((sum, row) => {
          const line = row.vendorLineMap[vendor.id]
          if (!line) return sum
          return sum + getInlineAllocationValue(row.rowKey, vendor.id)
        }, 0)
        return { vendor, total }
      })
      const inlineCandidates = inlineTotals.filter(
        (entry) => entry.total > 0 && !isAllocationLockedForRework(entry.vendor.status)
      )
      for (const entry of inlineCandidates) {
        const assignedLines = buildAssignedLinesForVendor(entry.vendor.id)
        await applyVendorAward(
          entry.vendor.id,
          entry.total,
          entry.vendor.status === 'SELECTED',
          assignedLines
        )
      }

      const latestComparison = await getQuotationComparison(selectedRFQ)
      const latestQuotations = latestComparison?.quotations || []
      const selectedForPo = latestQuotations.filter((quotation) => {
        const status = String(quotation?.status || '').toUpperCase()
        if (status !== 'SELECTED') return false
        if (isAllocationLockedForRework(status)) return false
        const lineAssigned = Array.isArray(quotation?.lines)
          ? quotation.lines.reduce(
            (sum: number, line: QuotationLineDto) => sum + Number(line?.assignedQuantity || 0),
            0
          )
          : 0
        const fallbackAssigned = Number(quotation?.actualQuantityAssignedToVendor || 0)
        const awarded = lineAssigned > 0 ? lineAssigned : fallbackAssigned
        return awarded > 0
      })

      if (selectedForPo.length === 0) {
        toast.error('No eligible selected vendor allocation found for PO generation')
        return
      }

      let createdCount = 0
      const failedVendors: string[] = []
      for (const quotation of selectedForPo) {
        const vendor = mapApiQuotationToVendorQuotation(quotation)
        const created = await handleCreatePOFromQuotation(quotation.id, String(quotation.status || ''), {
          ...buildPoCreateOptions(vendor),
          suppressSuccessToast: true,
        })
        if (created) {
          createdCount += 1
        } else {
          failedVendors.push(vendor.vendor || `Vendor ${quotation.vendorId}`)
        }
      }

      if (createdCount === 0) {
        toast.error('Failed to generate purchase orders from awarded quantities')
        return
      }

      if (ownerApprovalRequiredDraft) {
        await fetchQuotations(selectedRFQ)
        toast.success(
          `Submitted ${createdCount} vendor PO(s) for owner approval${failedVendors.length ? `, failed for: ${failedVendors.join(', ')}` : ''
          }`
        )
      } else {
        await closeQuotation(selectedRFQ)
        toast.success(
          `Generated ${createdCount} vendor PO(s) and finalized selection${failedVendors.length ? `, failed for: ${failedVendors.join(', ')}` : ''
          }`
        )
        setSelectedRFQ(null)
        setQuotations([])
        setVendorQuotations([])
      }
    } catch (error: any) {
      console.error('Error finalizing selection:', error)
      toast.error('Failed to finalize selection')
    } finally {
      setLoading(false)
    }
  }

  const toggleCardExpansion = (vendorId: number) => {
    setExpandedCards(prev => ({
      ...prev,
      [vendorId]: !prev[vendorId]
    }))
  }

  const handleOpenQuotationModal = (vendor: VendorQuotation) => {
    setActiveVendorForModal(vendor)
    setIsQuotationModalVisible(true)
    setAllocationValues(prev => ({ ...prev }))
    const quotation = quotations.find((q) => q.id === vendor.id)
    const lines = buildModalLinesForQuotation(quotation)
    setModalLinesLoading(true)
    setModalLines(lines)
    const firstKey = lines.length
      ? `${lines[0].partId ?? 'p'}-${lines[0].subcomponentId ?? 's'}-${lines[0].manualItemName ?? 'm'}-0`
      : null
    setSelectedLineKey(firstKey)
    setModalLinesLoading(false)

    if (quotationDetailCache[vendor.id]) {
      return
    }

    setQuotationDetailLoading(true)
    getQuotationById(vendor.id)
      .then((detail) => {
        setQuotationDetailCache((prev) => ({
          ...prev,
          [vendor.id]: detail,
        }))
      })
      .catch((err) => {
        console.error('Failed to load quotation details for modal', err)
        toast.error('Failed to load quotation details')
      })
      .finally(() => setQuotationDetailLoading(false))
  }

  const handleCloseQuotationModal = () => {
    setIsQuotationModalVisible(false)
    setActiveVendorForModal(null)
    setModalLines([])
    setModalLinesLoading(false)
    setQuotationDetailLoading(false)
  }

  const handleAllocationChange = (vendorId: number, value: number) => {
    const sanitizedValue = Number.isNaN(value) ? 0 : Math.max(0, value)
    const key = selectedLineKey ? `${vendorId}-${selectedLineKey}` : `${vendorId}`
    setAllocationValues(prev => ({
      ...prev,
      [key]: sanitizedValue
    }))
  }

  const handleInlineAllocationChange = (row: ComparisonRow, vendorId: number, value: number) => {
    const line = row.vendorLineMap[vendorId]
    if (!line) return
    const vendor = visibleVendorQuotations.find((entry) => entry.id === vendorId)
    if (vendor && isAllocationLockedForRework(vendor.status)) return

    const offeredQty = Number(line.quantity ?? 0)
    const hasReqCap = row.reqQtyValue > 0
    const sanitizedInput = Number.isFinite(value) ? Math.max(0, value) : 0

    setInlineAllocationValues((prev) => {
      const key = getInlineAllocationKey(row.rowKey, vendorId)
      const allocatedByOthers = visibleVendorQuotations.reduce((sum, vendor) => {
        if (vendor.id === vendorId) return sum
        const otherLine = row.vendorLineMap[vendor.id]
        if (!otherLine) return sum
        const otherKey = getInlineAllocationKey(row.rowKey, vendor.id)
        return sum + (prev[otherKey] ?? 0)
      }, 0)

      const maxByRequirement = hasReqCap ? Math.max(row.reqQtyValue - allocatedByOthers, 0) : Number.MAX_SAFE_INTEGER
      const maxByOffer = offeredQty > 0 ? offeredQty : Number.MAX_SAFE_INTEGER
      const capped = Math.min(sanitizedInput, maxByRequirement, maxByOffer)

      return {
        ...prev,
        [key]: capped,
      }
    })
  }

  const handleSaveInlineAllocations = async () => {
    if (!selectedRFQ) {
      toast.error('Please select an RFQ first')
      return
    }

    const invalidRows = comparisonRows.filter((row) => rowAllocationSummary[row.rowKey]?.over)
    if (invalidRows.length > 0) {
      toast.error('Allocation exceeds required quantity for one or more items')
      return
    }

    const vendorAwardTotals = visibleVendorQuotations.map((vendor) => {
      const total = comparisonRows.reduce((sum, row) => {
        const line = row.vendorLineMap[vendor.id]
        if (!line) return sum
        return sum + getInlineAllocationValue(row.rowKey, vendor.id)
      }, 0)
      return {
        vendor,
        total,
      }
    })

    const blockedByRework = vendorAwardTotals.filter(
      (v) => v.total > 0 && isAllocationLockedForRework(v.vendor.status)
    )
    if (blockedByRework.length > 0) {
      toast.warning(
        `Skipped rework-pending vendor(s): ${blockedByRework.map((entry) => entry.vendor.vendor).join(', ')}`
      )
    }

    const candidates = vendorAwardTotals.filter(
      (v) => v.total > 0 && !isAllocationLockedForRework(v.vendor.status)
    )
    if (candidates.length === 0) {
      toast.error('Please assign at least one item quantity in the table for non-rework-pending vendors')
      return
    }

    setInlineSaving(true)
    let successCount = 0
    const failedVendors: string[] = []

    try {
      for (const entry of candidates) {
        try {
          const assignedLines = buildAssignedLinesForVendor(entry.vendor.id)
          await applyVendorAward(
            entry.vendor.id,
            entry.total,
            entry.vendor.status === 'SELECTED',
            assignedLines
          )
          successCount += 1
        } catch (error) {
          console.error(`Failed to save inline allocation for vendor ${entry.vendor.vendor}`, error)
          failedVendors.push(entry.vendor.vendor)
        }
      }

      await fetchQuotations(selectedRFQ)

      if (successCount > 0 && failedVendors.length === 0) {
        toast.success('Inline allocation saved successfully')
      } else if (successCount > 0 && failedVendors.length > 0) {
        toast.warning(`Saved ${successCount} vendor allocation(s), failed for: ${failedVendors.join(', ')}`)
      } else {
        toast.error('Failed to save inline allocations')
      }
    } finally {
      setInlineSaving(false)
    }
  }

  // Fetch past (closed) RFQs
  const fetchPastRFQs = async () => {
    setPastRFQsLoading(true)
    try {
      const resolvedCgaId =
        Number(
          (currentUser as any)?.companyGroupAdminId ??
          (currentUser as any)?.companyGroupAdmin?.id ??
          (currentUser as any)?.cgaid?.id ??
          (currentUser as any)?.cgaid ??
          (currentUser as any)?.roleEntityId ??
          0
        ) || undefined
      const rfqScope = getProcurementScope(PROCUREMENT_PERMS.RFQ_VIEW)
      const cgaid = rfqScope === 'ALL' ? undefined : resolvedCgaId
      const pageSize = 200
      let pageIndex = 0
      const closedRFQs: any[] = []
      while (true) {
        const rfqPage = await getRFQsPage({
          cgaid,
          status: 'CLOSED',
          page: pageIndex,
          size: pageSize,
        })
        const content = rfqPage.content || []
        closedRFQs.push(...content)
        const lastPage =
          rfqPage.last === true ||
          content.length < pageSize ||
          pageIndex >= Math.max((rfqPage.totalPages ?? 1) - 1, 0)
        pageIndex += 1
        if (lastPage || pageIndex > 200) break
      }

      setPastRFQs(
        closedRFQs.map((rfq: any) => ({
          ...rfq,
          quotations: Array.isArray(rfq?.quotations) ? rfq.quotations : [],
          selectedQuotations: Array.isArray(rfq?.selectedQuotations) ? rfq.selectedQuotations : [],
          selectedQuotation: rfq?.selectedQuotation || null,
        }))
      )
    } catch (error) {
      console.error('Error fetching past RFQs:', error)
      toast.error('Failed to load past RFQs')
    } finally {
      setPastRFQsLoading(false)
    }
  }

  const handleViewPastRFQ = async (rfq: any) => {
    if (!rfq) return
    let rfqWithQuotes = rfq
    try {
      const [fullRfq, quotationsData] = await Promise.all([
        getRFQById(rfq.id),
        getQuotationsByRFQ(rfq.id),
      ])
      const selectedQuotations = quotationsData.filter((q: any) => q.status === 'SELECTED')
      const selectedQuotation = selectedQuotations[0] || quotationsData[0] || null
      rfqWithQuotes = {
        ...rfq,
        ...fullRfq,
        quotations: quotationsData,
        selectedQuotations,
        selectedQuotation,
      }
      setPastRFQs((prev) => prev.map((item) => (item.id === rfq.id ? rfqWithQuotes : item)))
    } catch (error) {
      console.error(`Error fetching full past RFQ details for RFQ ${rfq.id}:`, error)
    }
    setSelectedPastRFQ(rfqWithQuotes)
    setIsViewPastRFQModalVisible(true)
  }

  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `
  if (procurementPermissionsLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading procurement permissions...</span>
        </div>
      </div>
    )
  }

  if (!canViewQuotation) {
    return <div className='alert alert-warning m-6'>You do not have quotation view access.</div>
  }

  const fixedInfoColumnCount = accountCodeMode === 'INDIVIDUAL' ? 6 : 4
  return (
    <>
      <style>{shimmerKeyframes}</style>
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-column-fluid'>
          <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
            <div id='kt_app_content_container' className='app-container container-fluid'>
              <div className='card'>
                <div className='card-header border-0 pt-5 pb-3 bg-white'>
                  <div className='d-flex flex-column w-100'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <div>
                        <h3 className='card-label text-dark fw-bold mb-1' style={{ fontSize: '20px' }}>Quotation Management</h3>
                        <p className='text-muted mb-0' style={{ fontSize: '13px' }}>Review and compare quotations from multiple vendors</p>
                      </div>
                    </div>

                  </div>
                </div>

                <div className='card-body py-3 bg-white border-top'>
                  {false && <ProcurementFilterPanel
                    searchValue={searchTerm}
                    onSearchChange={(value) => {
                      setSearchTerm(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    searchPlaceholder='Search quotations...'
                    extraPrimary={
                      <>
                        <div className='col-12 col-md-6 col-lg-3'>
                          <label className='form-label text-muted fw-semibold fs-7 mb-2'>Vendor</label>
                          <SearchableSelect
                            className='form-select form-select-sm'
                            value={vendorFilter}
                            onChange={(e) => {
                              setVendorFilter(e.target.value)
                              setCurrentPage(1)
                              setPastCurrentPage(1)
                            }}
                          >
                            {vendorOptions.map((option) => (
                              <option key={`vendor-${option.value || 'all'}`} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        <div className='col-12 col-md-6 col-lg-3'>
                          <label className='form-label text-muted fw-semibold fs-7 mb-2'>Company</label>
                          <SearchableSelect
                            className='form-select form-select-sm'
                            value={companyFilter}
                            onChange={(e) => {
                              setCompanyFilter(e.target.value)
                              setCurrentPage(1)
                              setPastCurrentPage(1)
                            }}
                          >
                            {companyOptions.map((option) => (
                              <option key={`company-${option.value || 'all'}`} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      </>
                    }
                    referenceValue={referenceFilter}
                    onReferenceChange={(value) => {
                      setReferenceFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    vesselValue={vesselFilter}
                    onVesselChange={(value) => {
                      setVesselFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    vesselOptions={vesselOptions}
                    departmentValue={departmentFilter}
                    onDepartmentChange={(value) => {
                      setDepartmentFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    departmentOptions={departmentOptions}
                    catalogueValue={catalogueFilter}
                    onCatalogueChange={(value) => {
                      setCatalogueFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    catalogueOptions={catalogueOptions}
                    requisitionTitleValue={requisitionTitleFilter}
                    onRequisitionTitleChange={(value) => {
                      setRequisitionTitleFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    rfqTitleValue={rfqTitleFilter}
                    onRfqTitleChange={(value) => {
                      setRfqTitleFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    approvalPendingWithValue={approvalPendingWithFilter}
                    onApprovalPendingWithChange={(value) => {
                      setApprovalPendingWithFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    approvalPendingWithOptions={approvalPendingWithOptions}
                    poDateFrom={poDateFromFilter}
                    onPoDateFromChange={(value) => {
                      setPoDateFromFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    poDateTo={poDateToFilter}
                    onPoDateToChange={(value) => {
                      setPoDateToFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    deliveredToVesselValue={deliveredToVesselFilter}
                    onDeliveredToVesselChange={(value) => {
                      setDeliveredToVesselFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    stageValue={stageFilter}
                    onStageChange={(value) => {
                      setStageFilter(value)
                      setCurrentPage(1)
                      setPastCurrentPage(1)
                    }}
                    stageOptions={stageOptions}
                    additionalOpen={additionalFiltersOpen}
                    onToggleAdditional={() => setAdditionalFiltersOpen((prev) => !prev)}
                    onReset={resetFilters}
                  />}
                  {canManageQuotationWorkflow && selectedRFQ && (
                    <div className='d-flex justify-content-end mb-3'>
                      <button
                        className='btn btn-sm btn-light-primary'
                        onClick={async () => {
                          const remark = window.prompt('Optional note to vessel for selection?') || undefined
                          try {
                            await sendQuotationsToVesselSelection(selectedRFQ, remark)
                            toast.success('Quotations sent to vessel for selection')
                            await fetchQuotations(selectedRFQ)
                          } catch (err: any) {
                            toast.error('Failed to send quotations to vessel for selection')
                          }
                        }}
                      >
                        Send Options To Vessel
                      </button>
                    </div>
                  )}
                  {/* Recent RFQs Tab Content */}
                  {true && (
                    <>
                      {/* RFQ Selection Dropdown */}
                      <div className='row mb-4'>
                        <div className='col-12 col-md-4'>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <label className='form-label fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>Select RFQ</label>
                            {rfqLoading && (
                              <span className='spinner-border spinner-border-sm text-primary' role='status' aria-hidden='true'></span>
                            )}
                          </div>
                          <SearchableSelect
                            className='form-select'
                            value={selectedRFQ || ''}
                            onChange={(e) => {
                              const value = Number(e.target.value)
                              const nextSelection = Number.isFinite(value) && value > 0 ? value : null
                              setSelectedRFQ(nextSelection)
                              setSelectedRfqOption(
                                nextSelection != null
                                  ? rfqOptions.find((option) => option.id === nextSelection) || null
                                  : null
                              )
                            }}
                            onInputChange={(value) => setRfqSearchTerm(value)}
                            onMenuScrollToBottom={loadMoreRFQs}
                            disabled={rfqLoading}
                            style={{ fontSize: '14px' }}
                          >
                            <option value=''>{rfqLoading ? 'Loading RFQs...' : '-- Select RFQ --'}</option>
                            {rfqOptions.map((rfq) => (
                              <option key={rfq.id} value={rfq.id}>
                                {rfq.requisitionLabel
                                  ? `${rfq.title} | Requisition: ${rfq.requisitionLabel}`
                                  : rfq.requisitionDescription
                                    ? `${rfq.title} | Requisition: ${rfq.requisitionDescription}`
                                    : rfq.title}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      </div>

                      {loading && (
                        <div className='text-center py-5'>
                          <div className='spinner-border text-primary' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                          </div>
                        </div>
                      )}

                      {!loading && selectedRFQ && allVendorQuotations.length === 0 && (
                        <div className='alert alert-info'>
                          No quotations found for this RFQ.
                        </div>
                      )}

                      {!loading && selectedRFQ && allVendorQuotations.length > 0 && (
                        <>
                          {/* RFQ Details Card */}
                          <div className='row mb-4'>
                            <div className='col-md-12'>
                              <div
                                className='custom-card p-4 border rounded-3'
                                style={{ background: '#f8faff', borderColor: '#d8e1f3' }}
                              >
                                <div className='d-flex justify-content-between align-items-center mb-3'>
                                  <h6 className='fw-bold text-primary mb-0' style={{ fontSize: '15px' }}>{rfqDetails.rfqNumber}</h6>
                                </div>
                                <div className='row g-3'>
                                  <div className='col-12 col-sm-6 col-md-3'>
                                    <label className='form-label text-muted fw-semibold mb-1' style={{ fontSize: '12px' }}>RFQ Title</label>
                                    <div className='text-dark fw-bold' style={{ fontSize: '14px' }}>{comparisonRfq?.title || '-'}</div>
                                  </div>
                                  <div className='col-12 col-sm-6 col-md-2'>
                                    <label className='form-label text-muted fw-semibold mb-1' style={{ fontSize: '12px' }}>Line Items</label>
                                    <div className='text-dark fw-bold' style={{ fontSize: '14px' }}>{rfqDetails.lineItems}</div>
                                  </div>
                                  <div className='col-12 col-sm-6 col-md-3'>
                                    <label className='form-label text-muted fw-semibold mb-1' style={{ fontSize: '12px' }}>Department</label>
                                    <div className='text-dark fw-bold' style={{ fontSize: '14px' }}>{rfqDetails.department}</div>
                                  </div>
                                  <div className='col-12 col-sm-6 col-md-4'>
                                    <label className='form-label text-muted fw-semibold mb-1' style={{ fontSize: '12px' }}>Requested Date</label>
                                    <div className='text-dark fw-bold' style={{ fontSize: '14px' }}>{rfqDetails.requestedDate}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Section 1: Top quotation summary / quick vendor lists */}
                          <div className='row g-4 mb-4'>
                            <div className='col-12'>
                              <div
                                className='border rounded-3 p-4'
                                style={{ background: '#ffffff', borderColor: '#d7deef' }}
                              >
                                <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                                  <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '15px' }}>
                                    Quotation Quick Summary
                                  </h6>
                                  <div className='d-flex flex-wrap gap-2'>
                                    <span className='badge bg-light-success text-success'>
                                      Submitted: {submittedQuickVendors.length}
                                    </span>
                                    <span className='badge bg-light-warning text-warning'>
                                      Pending/Not Submitted: {pendingQuickVendors.length}
                                    </span>
                                  </div>
                                </div>

                                <div
                                  className='table-responsive mb-3'
                                  style={{ overflowX: 'auto', overflowY: 'visible' }}
                                >
                                  <table className='table table-sm table-bordered align-middle mb-0'>
                                    <thead className='table-light'>
                                      <tr>
                                        <th>Quotation Ref</th>
                                        <th>Vendor</th>
                                        <th>Port</th>
                                        <th>Submitted Date</th>
                                        <th>Items</th>
                                        <th>Currency</th>
                                        <th>Grand Total</th>
                                        <th>Status</th>
                                        {isSingleItemRfq && <th>Quick Assign Qty</th>}
                                        <th className='text-center' style={{ minWidth: '86px' }}>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {submittedQuickVendors.length === 0 ? (
                                        <tr>
                                          <td colSpan={isSingleItemRfq ? 10 : 9} className='text-center text-muted py-3'>
                                            No submitted quotation snapshots available.
                                          </td>
                                        </tr>
                                      ) : (
                                        submittedQuickVendors.map((vendor) => {
                                          const pendingLogisticsRequest =
                                            pendingLogisticsMaps.byQuotationId.get(vendor.id)
                                          const allocationLocked = isAllocationLockedForRework(vendor.status)
                                          const canQuickAssign =
                                            isSingleItemRfq && !!singleComparisonRow && visibleVendorIdSet.has(vendor.id)
                                          const singleLine = canQuickAssign
                                            ? singleComparisonRow!.vendorLineMap[vendor.id]
                                            : null
                                          const quickQty = canQuickAssign
                                            ? getInlineAllocationValue(singleComparisonRow!.rowKey, vendor.id)
                                            : 0
                                          return (
                                            <tr key={`quick-submitted-${vendor.id}`}>
                                              <td>{vendor.quoteRef || `Q-${vendor.id}`}</td>
                                              <td className='fw-semibold'>{vendor.vendor}</td>
                                              <td>{vendor.deliveryPort || '-'}</td>
                                              <td>
                                                {vendor.submittedAt
                                                  ? String(vendor.submittedAt).slice(0, 10)
                                                  : '-'}
                                              </td>
                                              <td>{vendor.quotedItemCount ?? vendor.lines?.length ?? 0}</td>
                                              <td>{vendor.currency || '-'}</td>
                                              <td className='fw-semibold'>
                                                {renderVendorAmount(vendor, vendor.grandTotal ?? vendor.originalTotal, vendor.companyTotal)}
                                              </td>
                                              <td>
                                                <span className={quotationStatusPresentation(vendor.status).className}>
                                                  {quotationStatusPresentation(vendor.status).label}
                                                </span>
                                                {pendingLogisticsRequest ? (
                                                  <div className='mt-2'>
                                                    <button
                                                      type='button'
                                                      className='btn btn-sm btn-light-warning text-warning fw-semibold'
                                                      onClick={() => setActiveLogisticsRequest(pendingLogisticsRequest)}
                                                    >
                                                      Review Logistics Update
                                                    </button>
                                                  </div>
                                                ) : null}
                                              </td>
                                              {isSingleItemRfq && (
                                                <td>
                                                  {canQuickAssign && singleLine ? (
                                                    <>
                                                      <input
                                                        type='number'
                                                        min={0}
                                                        className='form-control form-control-sm'
                                                        style={{ minWidth: '110px' }}
                                                        value={quickQty}
                                                        disabled={allocationLocked}
                                                        onChange={(e) =>
                                                          handleInlineAllocationChange(
                                                            singleComparisonRow!,
                                                            vendor.id,
                                                            Number(e.target.value)
                                                          )
                                                        }
                                                      />
                                                      {allocationLocked && (
                                                        <div className='text-warning fs-8 mt-1'>
                                                          Awaiting rework update
                                                        </div>
                                                      )}
                                                    </>
                                                  ) : (
                                                    <span className='text-muted'>N/A</span>
                                                  )}
                                                </td>
                                              )}
                                              <td className='text-center'>
                                                <button
                                                  type='button'
                                                  data-quick-action-trigger
                                                  className='btn btn-sm btn-light-primary px-2 py-1'
                                                  onClick={(event) => {
                                                    if (quickActionMenuState?.vendorId === vendor.id) {
                                                      setQuickActionMenuState(null)
                                                      return
                                                    }
                                                    openQuickActionMenu(vendor.id, event)
                                                  }}
                                                  title='Vendor actions'
                                                >
                                                  <i className='bi bi-three-dots-vertical fs-5'></i>
                                                </button>
                                              </td>
                                            </tr>
                                          )
                                        })
                                      )}
                                    </tbody>
                                  </table>
                                </div>

                                <div className='table-responsive'>
                                  <table className='table table-sm table-bordered align-middle mb-0'>
                                    <thead className='table-light'>
                                      <tr>
                                        <th>Vendor</th>
                                        <th>Quotation State</th>
                                        <th>Reference</th>
                                        <th>Date Info</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {pendingQuickVendors.length === 0 ? (
                                        <tr>
                                          <td colSpan={4} className='text-center text-muted py-3'>
                                            All invited vendors have submitted quotation snapshots.
                                          </td>
                                        </tr>
                                      ) : (
                                        pendingQuickVendors.map((vendor) => (
                                          <tr key={`quick-pending-${vendor.id}`}>
                                            <td className='fw-semibold'>{vendor.vendor}</td>
                                            <td>
                                              <span className='badge bg-light-warning text-warning border text-uppercase'>
                                                {String(vendor.status || 'NOT_SUBMITTED').replace(/_/g, ' ')}
                                              </span>
                                            </td>
                                            <td>{vendor.quoteRef || `Q-${vendor.id}`}</td>
                                            <td>{vendor.submittedAt ? String(vendor.submittedAt).slice(0, 10) : '-'}</td>
                                          </tr>
                                        ))
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>

                          {filteredVendorQuotations.length === 0 && (
                            <div className='alert alert-warning mb-4'>
                              No submitted quotations match current filters. Use the quick summary above to review pending/returned vendors.
                            </div>
                          )}

                          {/* Comparison Grid */}
                          <div
                            className='mb-4 border rounded-3 p-4'
                            style={{ background: '#ffffff', borderColor: '#d7deef' }}
                          >
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                              <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '15px' }}>Commercial Comparison Grid</h6>
                              {bestVisibleMatch && (
                                <span className='badge bg-light-success text-success fw-semibold' style={{ fontSize: '11px' }}>
                                  Best Price: {bestVisibleMatch.vendor}
                                </span>
                              )}
                              {!bestVisibleMatch && visibleVendorQuotations.length > 0 && (
                                <span className='badge bg-light-warning text-warning fw-semibold' style={{ fontSize: '11px' }}>
                                  No company-currency ranking available
                                </span>
                              )}
                            </div>

                            <div className='border rounded p-3 mb-3 bg-light'>
                              <div className='d-flex flex-wrap align-items-center gap-3'>
                                <span className='badge bg-light-primary text-primary'>
                                  Requisition Required: {overallAllocationSummary.required}
                                </span>
                                <span className='badge bg-light-success text-success'>
                                  Allocated In Grid: {overallAllocationSummary.allocated}
                                </span>
                                <span className='badge bg-light-warning text-warning'>
                                  Remaining: {overallAllocationSummary.remaining}
                                </span>
                                {overallAllocationSummary.overAllocated && (
                                  <span className='badge bg-light-danger text-danger'>
                                    Over allocated
                                  </span>
                                )}
                              </div>
                              <div className='text-muted mt-2' style={{ fontSize: '12px' }}>
                                Requisition Required is demand from requisition. Award Qty is what you split/assign to vendor directly in this table.
                              </div>
                              {reworkLockedVendors.length > 0 && (
                                <div className='text-warning mt-1' style={{ fontSize: '12px' }}>
                                  Rework pending vendor(s) are allocation-locked until they submit UPDATED_AFTER_REWORK:
                                  {' '}
                                  {reworkLockedVendors.map((vendor) => vendor.vendor).join(', ')}
                                </div>
                              )}
                            </div>

                            <div className='table-responsive border rounded-3' style={{ maxHeight: '560px', borderColor: '#d7deef' }}>
                              <table
                                className='table table-bordered table-sm align-middle mb-0 procurement-list-table procurement-comparison-table'
                                style={{ minWidth: `${860 + visibleVendorQuotations.length * vendorColumnCount * 108}px` }}
                              >
                                <thead className='table-light'>
                                  <tr>
                                    <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(0)}>S.No</th>
                                    <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(1)}>Part #</th>
                                    <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(2)}>Item Name / Description</th>
                                    <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(3)}>Unit</th>
                                    {accountCodeMode === 'INDIVIDUAL' && (
                                      <>
                                        <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(4)}>Account Code</th>
                                        <th rowSpan={2} className='text-center' style={getFixedHeaderStyle(5)}>Sub Account Code</th>
                                      </>
                                    )}
                                    {visibleVendorQuotations.map((vendor) => (
                                      <th
                                        key={`vendor-block-${vendor.id}`}
                                        colSpan={vendorColumnCount}
                                        className='text-center bg-light-primary'
                                      >
                                        <div className='d-flex align-items-center justify-content-between gap-2'>
                                          <span className='fw-bold d-flex align-items-center gap-2'>
                                            <span>{vendor.vendor}</span>
                                            {(vendor.attachments?.length ?? 0) > 0 && (
                                              <button
                                                type='button'
                                                className='badge bg-light text-dark border'
                                                title='View quotation attachments'
                                                aria-label={`View ${vendor.attachments?.length} quotation attachment(s)`}
                                                onClick={() => handleOpenQuotationModal(vendor)}
                                              >
                                                <i className='bi bi-paperclip me-1'></i>
                                                {vendor.attachments?.length}
                                              </button>
                                            )}
                                            <span className='badge bg-light text-dark border text-uppercase'>
                                              {String(vendor.status || 'SUBMITTED').replace(/_/g, ' ')}
                                            </span>
                                          </span>
                                          <button
                                            type='button'
                                            className='btn btn-sm btn-light-primary py-1 px-2'
                                            onClick={() => handleOpenQuotationModal(vendor)}
                                          >
                                            Details
                                          </button>
                                        </div>
                                      </th>
                                    ))}
                                  </tr>
                                  <tr>
                                    {visibleVendorQuotations.map((vendor) => ([
                                      <th key={`head-${vendor.id}-capacity`} className='text-center'>Supply Cap.</th>,
                                      <th key={`head-${vendor.id}-award`} className='text-center'>Award Qty</th>,
                                      <th key={`head-${vendor.id}-allocated`} className='text-center'>Allocated</th>,
                                      <th key={`head-${vendor.id}-up`} className='text-center'>Unit Price</th>,
                                      <th key={`head-${vendor.id}-qty`} className='text-center'>Qty</th>,
                                      <th key={`head-${vendor.id}-disc`} className='text-center'>Discount %</th>,
                                      <th key={`head-${vendor.id}-gst`} className='text-center'>GST/VAT %</th>,
                                      <th key={`head-${vendor.id}-amt`} className='text-center'>Amount</th>,
                                      <th key={`head-${vendor.id}-lead`} className='text-center'>Lead Days</th>,
                                      <th key={`head-${vendor.id}-remark`} className='text-center'>Remark</th>,

                                    ]))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {comparisonRows.length === 0 ? (

                                    <tr>
                                      <td colSpan={fixedInfoColumnCount + visibleVendorQuotations.length * vendorColumnCount} className='text-center text-muted py-4'>
                                        No comparison lines available.
                                      </td>
                                    </tr>
                                  ) : (
                                    <>
                                      {comparisonRows.map((row, rowIndex) => {
                                        return (
                                          <tr key={row.rowKey}>
                                            <td className='text-center fw-semibold' style={getFixedBodyStyle(0, rowIndex)}>{rowIndex + 1}</td>
                                            <td style={getFixedBodyStyle(1, rowIndex)}>{row.partNo}</td>
                                            <td style={getFixedBodyStyle(2, rowIndex)}>{row.itemName}</td>
                                            <td className='text-center' style={getFixedBodyStyle(3, rowIndex)}>{row.unit}</td>
                                            {accountCodeMode === 'INDIVIDUAL' && (
                                              <>
                                                <td style={getFixedBodyStyle(4, rowIndex)}>
                                                  <SearchableSelect
                                                    className='form-select form-select-sm'
                                                    value={String(
                                                      rowAccountingValues[row.rowKey]?.accountCodeId ??
                                                      row.accountCodeId ??
                                                      ''
                                                    )}
                                                    onChange={(e) => {
                                                      const selectedId = Number(e.target.value || 0)
                                                      if (!selectedId) {
                                                        setRowAccountingValues((prev) => ({
                                                          ...prev,
                                                          [row.rowKey]: {
                                                            accountCodeId: null,
                                                            accountCode: '',
                                                            subaccountCodeId: null,
                                                            subaccountCode: '',
                                                          },
                                                        }))
                                                        return
                                                      }
                                                      const accountCode = accountCodeById.get(selectedId) || ''
                                                      setRowAccountingValues((prev) => ({
                                                        ...prev,
                                                        [row.rowKey]: {
                                                          accountCodeId: selectedId,
                                                          accountCode,
                                                          subaccountCodeId: null,
                                                          subaccountCode: '',
                                                        },
                                                      }))
                                                      ensureSubAccountsForAccount(selectedId)
                                                    }}
                                                  >
                                                    <option value=''>Optional</option>
                                                    {accountCodeOptions.map((account) => (
                                                      <option
                                                        key={`${row.rowKey}-account-${account.id}`}
                                                        value={account.id}
                                                      >
                                                        {account.code}
                                                      </option>
                                                    ))}
                                                  </SearchableSelect>
                                                </td>

                                                <td style={getFixedBodyStyle(5, rowIndex)}>
                                                  {(() => {
                                                    const selectedAccountId = Number(
                                                      rowAccountingValues[row.rowKey]?.accountCodeId ??
                                                      row.accountCodeId ??
                                                      0
                                                    )
                                                    const subAccountOptions =
                                                      selectedAccountId > 0
                                                        ? comparisonSubAccounts[selectedAccountId] || []
                                                        : []

                                                    return (
                                                      <SearchableSelect
                                                        className='form-select form-select-sm'
                                                        disabled={selectedAccountId <= 0}
                                                        value={String(
                                                          rowAccountingValues[row.rowKey]?.subaccountCodeId ??
                                                          row.subaccountCodeId ??
                                                          ''
                                                        )}
                                                        onChange={(e) => {
                                                          const selectedSubId = Number(e.target.value || 0)
                                                          const selectedSub = subAccountOptions.find(
                                                            (item) => Number(item.id) === selectedSubId
                                                          )
                                                          setRowAccountingValues((prev) => {
                                                            const existing = prev[row.rowKey] || {
                                                              accountCodeId: selectedAccountId > 0 ? selectedAccountId : null,
                                                              accountCode:
                                                                selectedAccountId > 0
                                                                  ? accountCodeById.get(selectedAccountId) || ''
                                                                  : '',
                                                              subaccountCodeId: null,
                                                              subaccountCode: '',
                                                            }
                                                            return {
                                                              ...prev,
                                                              [row.rowKey]: {
                                                                ...existing,
                                                                subaccountCodeId: selectedSubId > 0 ? selectedSubId : null,
                                                                subaccountCode:
                                                                  selectedSubId > 0
                                                                    ? String(selectedSub?.subaccountCodes || '')
                                                                    : '',
                                                              },
                                                            }
                                                          })
                                                        }}
                                                      >
                                                        <option value=''>Optional</option>
                                                        {subAccountOptions.map((subAccount) => (
                                                          <option
                                                            key={`${row.rowKey}-subaccount-${subAccount.id}`}
                                                            value={subAccount.id}
                                                          >
                                                            {subAccount.subaccountCodes}
                                                          </option>
                                                        ))}
                                                      </SearchableSelect>
                                                    )
                                                  })()}
                                                </td>
                                              </>
                                            )}
                                            {visibleVendorQuotations.map((vendor) => {
                                              const line = row.vendorLineMap[vendor.id]
                                              if (!line) {
                                                return (
                                                  <td
                                                    key={`r-${row.rowKey}-v-${vendor.id}-na`}
                                                    colSpan={vendorColumnCount}
                                                    className='text-center text-muted fst-italic'
                                                    style={{ borderLeft: '2px solid #d8deea' }}
                                                  >
                                                    Not Applicable
                                                  </td>
                                                )
                                              }

                                              const qty = Number(line?.quantity ?? 0)
                                              const vendorAllocated = Math.max(
                                                0,
                                                getInlineAllocationValue(row.rowKey, vendor.id)
                                              )
                                              const unitPrice = Number(line?.unitPrice ?? 0)
                                              const discountPct = Number(line?.discountPercent ?? 0)
                                              const gstPct = Number(line?.gstPercent ?? 0)
                                              const amount = calculateQuotationLineAmount(
                                                vendorAllocated,
                                                unitPrice,
                                                discountPct,
                                                gstPct
                                              )
                                              const allocationLocked = isAllocationLockedForRework(vendor.status)
                                              const parsedLeadDays = Number(
                                                String(vendor.deliveryTime || '').replace(/[^0-9]/g, '')
                                              )
                                              const leadDays =
                                                line?.deliveryTimeDays ??
                                                (Number.isNaN(parsedLeadDays) || parsedLeadDays <= 0
                                                  ? null
                                                  : parsedLeadDays)

                                              return [
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-capacity`} className='text-center' style={{ borderLeft: '2px solid #d8deea' }}>
                                                  {line.supplyCapacity != null ? line.supplyCapacity : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-award`} className='text-center'>
                                                  <input
                                                    type='number'
                                                    min={0}
                                                    max={Math.max(0, Math.floor(Number(line.quantity ?? 0)))}
                                                    className='form-control form-control-sm text-end'
                                                    value={getInlineAllocationValue(row.rowKey, vendor.id)}
                                                    disabled={allocationLocked}
                                                    onChange={(e) =>
                                                      handleInlineAllocationChange(row, vendor.id, Number(e.target.value))
                                                    }
                                                    style={{ width: '76px', minWidth: '76px' }}
                                                  />
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-allocated`} className='text-center fw-semibold'>
                                                  {vendorAllocated}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-up`} className='text-center'>
                                                  {line.unitPrice != null ? `${vendor.currency} ${unitPrice.toFixed(2)}` : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-qty`} className='text-center'>
                                                  {readComparisonQuantity(line)}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-disc`} className='text-center'>
                                                  {line.discountPercent != null ? `${discountPct.toFixed(2)}%` : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-gst`} className='text-center'>
                                                  {line.gstPercent != null ? `${gstPct.toFixed(2)}%` : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-amt`} className='text-center fw-semibold'>
                                                  {line.unitPrice != null
                                                    ? renderVendorAmount(
                                                      vendor,
                                                      amount,
                                                      convertOriginalAmountToCompany(amount, vendor)
                                                    )
                                                    : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-lead`} className='text-center'>
                                                  {leadDays != null && !Number.isNaN(leadDays) ? leadDays : '-'}
                                                </td>,
                                                <td key={`r-${row.rowKey}-v-${vendor.id}-remark`}>
                                                  {line.remarks || line.notes || '-'}
                                                </td>,
                                              ]
                                            })}
                                          </tr>
                                        )
                                      })}
                                      {vendorSummaryRows
                                        .filter((summaryRow) => {
                                          const isLogisticsRow = ['freight', 'packaging', 'truck', 'barge', 'other', 'vatLogistics'].includes(summaryRow.key)
                                          return !isLogisticsRow || visibleVendorQuotations.some((vendor) =>
                                            isNonZeroLogisticsValue(summaryRow.value(vendor).original)
                                          )
                                        })
                                        .map((summaryRow) => (
                                        <tr key={`summary-${summaryRow.key}`} className='bg-light comparison-summary-row'>
                                          <td
                                            colSpan={fixedInfoColumnCount}
                                            className={`fw-semibold comparison-summary-label ${summaryRow.highlight ? 'text-dark' : 'text-muted'}`}
                                            style={{
                                              position: 'sticky',
                                              left: 0,
                                              zIndex: 8,
                                              background: '#f8f9fb',
                                              borderRight: '2px solid #d8deea',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {summaryRow.label}
                                          </td>
                                          {visibleVendorQuotations.map((vendor) => (
                                            <td
                                              key={`summary-${summaryRow.key}-vendor-${vendor.id}`}
                                              colSpan={vendorColumnCount}
                                              className={`text-end comparison-summary-value ${summaryRow.highlight ? 'fw-bold text-dark' : 'fw-semibold text-muted'}`}
                                              style={{ borderLeft: '2px solid #d8deea', whiteSpace: 'nowrap' }}
                                            >
                                              {(() => {
                                                const value = summaryRow.value(vendor)
                                                return renderVendorAmount(vendor, value.original, value.company)
                                              })()}
                                            </td>
                                          ))}
                                        </tr>
                                        ))}
                                    </>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Section 3: PO / delivery / approval details below comparison */}
                          <div
                            className='border rounded-3 p-4 mt-4'
                            style={{ background: '#f8fafd', borderColor: '#d7deef' }}
                          >
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3'>
                              <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '15px' }}>
                                PO / Delivery / Approval Details
                              </h6>
                              {detailVendor ? (
                                <div className='text-muted fs-8'>
                                  Vendor: <span className='fw-semibold text-dark'>{detailVendor.vendor}</span>
                                  {' • '}{String(detailVendor.status || 'SUBMITTED').replace(/_/g, ' ')}
                                </div>
                              ) : null}
                            </div>

                            {!detailVendor || !detailQuotation ? (
                              <div className='alert alert-info mb-0'>
                                No workflow-selected quotation is available for PO/delivery/approval details.
                              </div>
                            ) : (
                              <>
                                <div className='card border mb-4'>
                                  <div className='card-header py-2'>
                                    <h6 className='mb-0 fw-bold fs-7 text-uppercase'>Account / PO Details</h6>
                                  </div>
                                  <div className='card-body'>
                                    {Object.keys(poFormErrors).length > 0 && (
                                      <div className='alert alert-danger py-2 px-3 mb-3'>
                                        <div className='fw-semibold fs-8 mb-1'>Complete required fields before finalizing:</div>
                                        <ul className='mb-0 ps-4 fs-8'>
                                          {Object.values(poFormErrors).map((error, idx) => (
                                            <li key={`po-error-${idx}`}>{error}</li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    <div className='row g-3'>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Requisition Delivery Date</label>
                                        <input className='form-control form-control-sm' value={rfqDetails.requestedDate || ''} readOnly />
                                      </div>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>PO Date</label>
                                        <input
                                          type='date'
                                          className={`form-control form-control-sm ${poFormErrors.poDate ? 'is-invalid' : ''}`}
                                          value={poDateDraft}
                                          min={
                                            requisitionCreatedDateValue && requisitionCreatedDateValue !== '-'
                                              ? requisitionCreatedDateValue
                                              : undefined
                                          }
                                          max={new Date().toISOString().slice(0, 10)}
                                          onChange={(e) => {
                                            setPoDateDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.poDate
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>PO Raised By Company</label>
                                        <input
                                          className={`form-control form-control-sm ${poFormErrors.poRaisedByCompany ? 'is-invalid' : ''}`}
                                          value={poRaisedByCompanyValue}
                                          readOnly
                                        />
                                      </div>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>PO Raised By User</label>
                                        <input
                                          className={`form-control form-control-sm ${poFormErrors.poRaisedByUser ? 'is-invalid' : ''}`}
                                          value={poRaisedByUserDraft}
                                          onChange={(e) => {
                                            setPoRaisedByUserDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.poRaisedByUser
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-4'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Invoice Addressed To</label>
                                        <input
                                          className={`form-control form-control-sm ${poFormErrors.invoiceAddressedTo ? 'is-invalid' : ''}`}
                                          value={invoiceAddressedToDraft}
                                          onChange={(e) => {
                                            setInvoiceAddressedToDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.invoiceAddressedTo
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-4'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Billing Address</label>
                                        <SearchableSelect
                                          className='form-select form-select-sm'
                                          value={billingAddressIdDraft ?? ''}
                                          onChange={(e) => {
                                            const selectedId = Number(e.target.value || 0)
                                            const selectedAddress = billingAddressOptions.find(
                                              (address) => address.id === selectedId
                                            )
                                            setBillingAddressIdDraft(selectedId > 0 ? selectedId : null)
                                            setBillingAddressDraft(
                                              selectedAddress?.fullAddress ||
                                              selectedAddress?.displayLabel ||
                                              selectedAddress?.addressName ||
                                              ''
                                            )
                                          }}
                                          disabled={billingAddressOptions.length === 0}
                                        >
                                          <option value=''>
                                            {billingAddressOptions.length > 0
                                              ? 'Select Billing Address'
                                              : 'No Billing Address configured'}
                                          </option>
                                          {billingAddressOptions.map((address) => (
                                            <option key={`po-billing-address-${address.id}`} value={address.id}>
                                              {address.displayLabel || address.addressName || address.fullAddress || `Address ${address.id}`}
                                            </option>
                                          ))}
                                        </SearchableSelect>
                                      </div>
                                      <div className='col-md-4'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Shipping Address</label>
                                        <input
                                          className='form-control form-control-sm'
                                          value={comparisonRfq?.deliveryPortName || 'N/A'}
                                          readOnly
                                        />
                                        <div className='form-text'>Requisition delivery port</div>
                                      </div>
                                      <div className='col-md-2'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Account Category</label>
                                        <input
                                          className={`form-control form-control-sm ${poFormErrors.accountCategory ? 'is-invalid' : ''}`}
                                          value={accountCategoryDraft}
                                          onChange={(e) => {
                                            setAccountCategoryDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.accountCategory
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Account Code Mode</label>
                                        <SearchableSelect
                                          className='form-select form-select-sm'
                                          value={accountCodeMode}
                                          onChange={(e) => setAccountCodeMode(e.target.value as AccountCodeMode)}
                                        >
                                          <option value='COMMON'>Common Budget / Account Code</option>
                                          <option value='INDIVIDUAL'>Individual Item-wise Account Code</option>
                                        </SearchableSelect>
                                      </div>
                                      {accountCodeMode === 'COMMON' && (
                                        <div className='col-md-3'>
                                          <label className='form-label text-muted fw-semibold fs-8'>Budget / Account Code</label>
                                          <SearchableSelect
                                            className={`form-select form-select-sm ${poFormErrors.accountCode ? 'is-invalid' : ''}`}
                                            value={String(commonBudgetAccountCodeId ?? '')}
                                            onChange={(e) => {
                                              const selectedId = Number(e.target.value || 0)
                                              const selectedCode = accountCodeById.get(selectedId) || ''
                                              setCommonBudgetAccountCodeId(selectedId > 0 ? selectedId : null)
                                              setCommonBudgetAccountCode(selectedCode)
                                              setCommonBudgetSubaccountCodeId(null)
                                              setCommonBudgetSubaccountCode('')
                                              setBudgetCodeDraft(selectedCode)
                                              setPoFormErrors((prev) => {
                                                const next = { ...prev }
                                                delete next.accountCode
                                                return next
                                              })
                                            }}
                                          >
                                            <option value=''>Select Budget / Account Code</option>
                                            {accountCodeOptions.map((account) => (
                                              <option key={`common-budget-${account.id}`} value={account.id}>
                                                {account.code}
                                              </option>
                                            ))}
                                          </SearchableSelect>
                                        </div>
                                      )}
                                      {accountCodeMode === 'COMMON' && (
                                        <div className='col-md-3'>
                                          <label className='form-label text-muted fw-semibold fs-8'>Sub Account Code</label>
                                          <SearchableSelect
                                            className={`form-select form-select-sm ${poFormErrors.subaccountCode ? 'is-invalid' : ''}`}
                                            disabled={!commonBudgetAccountCodeId || commonBudgetAccountCodeId <= 0}
                                            value={String(commonBudgetSubaccountCodeId ?? '')}
                                            onChange={(e) => {
                                              const selectedSubId = Number(e.target.value || 0)
                                              const subOptions =
                                                commonBudgetAccountCodeId && commonBudgetAccountCodeId > 0
                                                  ? comparisonSubAccounts[commonBudgetAccountCodeId] || []
                                                  : []
                                              const selectedSub = subOptions.find((item) => Number(item.id) === selectedSubId)
                                              setCommonBudgetSubaccountCodeId(selectedSubId > 0 ? selectedSubId : null)
                                              setCommonBudgetSubaccountCode(
                                                selectedSubId > 0 ? String(selectedSub?.subaccountCodes || '') : ''
                                              )
                                              setPoFormErrors((prev) => {
                                                const next = { ...prev }
                                                delete next.subaccountCode
                                                return next
                                              })
                                            }}
                                          >
                                            <option value=''>Select Sub Account Code</option>
                                            {(commonBudgetAccountCodeId && commonBudgetAccountCodeId > 0
                                              ? comparisonSubAccounts[commonBudgetAccountCodeId] || []
                                              : []
                                            ).map((subAccount) => (
                                              <option key={`po-budget-sub-${subAccount.id}`} value={subAccount.id}>
                                                {subAccount.subaccountCodes}
                                              </option>
                                            ))}
                                          </SearchableSelect>
                                        </div>
                                      )}

                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>
                                          Logistics Account Code
                                        </label>

                                        <SearchableSelect
                                          className={`form-select form-select-sm ${poFormErrors.logisticAccountCode ? 'is-invalid' : ''
                                            }`}
                                          value={String(commonLogisticAccountCodeId ?? '')}
                                          onChange={(e) => {
                                            const selectedId = Number(e.target.value || 0)
                                            const selectedCode = accountCodeById.get(selectedId) || ''

                                            setCommonLogisticAccountCodeId(
                                              selectedId > 0 ? selectedId : null
                                            )

                                            setCommonLogisticAccountCode(selectedCode)

                                            setCommonLogisticSubaccountCodeId(null)
                                            setCommonLogisticSubaccountCode('')

                                            if (selectedId > 0) {
                                              void ensureSubAccountsForAccount(selectedId)
                                            }

                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.logisticAccountCode
                                              return next
                                            })
                                          }}
                                        >
                                          <option value=''>
                                            Select Logistics Account Code
                                          </option>

                                          {accountCodeOptions.map((account) => (
                                            <option
                                              key={`logistic-account-${account.id}`}
                                              value={account.id}
                                            >
                                              {account.code}
                                            </option>
                                          ))}
                                        </SearchableSelect>
                                      </div>

                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>
                                          Logistics Sub Account Code
                                        </label>

                                        <SearchableSelect
                                          className='form-select form-select-sm'
                                          disabled={
                                            !commonLogisticAccountCodeId ||
                                            commonLogisticAccountCodeId <= 0
                                          }
                                          value={String(commonLogisticSubaccountCodeId ?? '')}
                                          onChange={(e) => {
                                            const selectedSubId = Number(e.target.value || 0)

                                            const subOptions =
                                              commonLogisticAccountCodeId &&
                                                commonLogisticAccountCodeId > 0
                                                ? comparisonSubAccounts[commonLogisticAccountCodeId] || []
                                                : []

                                            const selectedSub =
                                              subOptions.find(
                                                (item) =>
                                                  Number(item.id) === selectedSubId
                                              )

                                            setCommonLogisticSubaccountCodeId(
                                              selectedSubId > 0
                                                ? selectedSubId
                                                : null
                                            )

                                            setCommonLogisticSubaccountCode(
                                              selectedSubId > 0
                                                ? String(selectedSub?.subaccountCodes || '')
                                                : ''
                                            )
                                          }}
                                        >
                                          <option value=''>
                                            Select Logistics Sub Account Code
                                          </option>

                                          {(commonLogisticAccountCodeId &&
                                            commonLogisticAccountCodeId > 0
                                            ? comparisonSubAccounts[commonLogisticAccountCodeId] || []
                                            : []
                                          ).map((subAccount) => (
                                            <option
                                              key={`logistic-sub-${subAccount.id}`}
                                              value={subAccount.id}
                                            >
                                              {subAccount.subaccountCodes}
                                            </option>
                                          ))}
                                        </SearchableSelect>
                                      </div>
                                      <div className='col-md-4'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Supplier Reference</label>
                                        <input
                                          className='form-control form-control-sm'
                                          value={supplierReferenceDraft}
                                          onChange={(e) => setSupplierReferenceDraft(e.target.value)}
                                        />
                                      </div>
                                      <div className='col-md-8 d-flex align-items-end'>
                                        <div className='text-muted fs-8'>
                                          {accountCodeMode === 'INDIVIDUAL'
                                            ? 'Individual mode is enabled. Use row-level Account Code/Sub Account Code in the comparison grid and select one Logistic Account Code here.'
                                            : 'Common mode is enabled. One Account Code and one Sub Account Code will apply to all items across all vendors.'}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {false && (
                                <div className='card border mb-4'>
                                  <div className='card-header py-2'>
                                    <h6 className='mb-0 fw-bold fs-7 text-uppercase'>Owner Approval</h6>
                                  </div>
                                  <div className='card-body'>
                                    <div className='row g-3'>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Owner Attachment</label>
                                        <input
                                          type='file'
                                          className='form-control form-control-sm'
                                          multiple
                                          onChange={(e) =>
                                            setOwnerAttachmentFiles(Array.from(e.target.files || []))
                                          }
                                        />
                                        {ownerAttachmentFiles.length > 0 && (
                                          <div className='form-text'>
                                            {ownerAttachmentFiles.length} file(s) selected for approval notes.
                                          </div>
                                        )}
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Owner Remarks</label>
                                        <textarea
                                          className='form-control form-control-sm'
                                          rows={2}
                                          value={ownerRemarksDraft}
                                          onChange={(e) => setOwnerRemarksDraft(e.target.value)}
                                          placeholder='Owner remarks before approve/reject/rework'
                                        />
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8 d-block'>Approval Routing</label>
                                        <div className='form-check form-check-custom form-check-solid'>
                                          <input
                                            id='owner-approval-required'
                                            className='form-check-input me-2'
                                            type='checkbox'
                                            checked={ownerApprovalRequiredDraft}
                                            onChange={(e) => setOwnerApprovalRequiredDraft(e.target.checked)}
                                          />
                                          <label htmlFor='owner-approval-required' className='form-check-label text-dark fw-semibold'>
                                            Owner approval required?
                                          </label>
                                        </div>
                                        <div className='text-muted fs-8 mt-1'>
                                          When enabled, PO will be saved in pending owner approval state before vendor send.
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                )}

                                <div className='card border mb-4'>
                                  <div className='card-header py-2'>
                                    <h6 className='mb-0 fw-bold fs-7 text-uppercase'>Delivery Details</h6>
                                  </div>
                                  <div className='card-body'>
                                    <div className='row g-3'>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Port Call For Vessel</label>
                                        <PortSelect
                                          className='form-control-sm'
                                          value={portCallDraft}
                                          onChange={(e) => setPortCallDraft(e.target.value)}
                                          placeholder='Select or search port call...'
                                        />
                                      </div>
                                      {/* Voyage Number — unused for now; re-enable with:
                                        const [voyageNumberDraft, setVoyageNumberDraft] = useState('')
                                        and restore autofill from requisition voyage/job fields if needed.
                                    <div className='col-md-3'>
                                      <label className='form-label text-muted fw-semibold fs-8'>Voyage Number</label>
                                      <input
                                        className='form-control form-control-sm'
                                        value={voyageNumberDraft}
                                        onChange={(e) => setVoyageNumberDraft(e.target.value)}
                                      />
                                    </div>
                                    */}
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Tag Reference</label>
                                        <input
                                          className='form-control form-control-sm'
                                          value={tagReferenceDraft}
                                          onChange={(e) => setTagReferenceDraft(e.target.value)}
                                        />
                                      </div>
                                      <div className='col-md-3'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Delivery Date</label>
                                        <input
                                          type='date'
                                          className={`form-control form-control-sm ${poFormErrors.deliveryDate ? 'is-invalid' : ''}`}
                                          value={deliveryDateDraft}
                                          onChange={(e) => {
                                            setDeliveryDateDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.deliveryDate
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Delivery Port</label>
                                        <input
                                          className={`form-control form-control-sm ${poFormErrors.deliveryPort ? 'is-invalid' : ''}`}
                                          value={deliveryPortDraft}
                                          onChange={(e) => {
                                            setDeliveryPortDraft(e.target.value)
                                            setPoFormErrors((prev) => {
                                              const next = { ...prev }
                                              delete next.deliveryPort
                                              return next
                                            })
                                          }}
                                        />
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>PO Terms & Conditions</label>
                                        <textarea
                                          className='form-control form-control-sm'
                                          rows={2}
                                          value={poTermsDraft}
                                          onChange={(e) => setPoTermsDraft(e.target.value)}
                                        />
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Agent Address / Instructions</label>
                                        <textarea
                                          className='form-control form-control-sm'
                                          rows={2}
                                          value={agentInstructionsDraft}
                                          onChange={(e) => setAgentInstructionsDraft(e.target.value)}
                                        />
                                      </div>
                                      <div className='col-md-6'>
                                        <label className='form-label text-muted fw-semibold fs-8'>Approval Remarks</label>
                                        <textarea
                                          className='form-control form-control-sm'
                                          rows={2}
                                          value={approvalRemarksDraft}
                                          onChange={(e) => setApprovalRemarksDraft(e.target.value)}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className='alert alert-light border d-flex justify-content-between align-items-center mb-0 py-2 px-3'>
                                  <span className='text-muted fs-8'>
                                    Use the vendor <strong>Actions</strong> menu in Quotation Quick Summary for Approve, Rework, or Cancel.
                                    PO generation is triggered from <strong>Finalize Selection</strong> using this shared PO/delivery section.
                                  </span>
                                  <span className='badge bg-light-primary text-primary text-uppercase'>
                                    {String(detailVendor.status || 'SUBMITTED').replace(/_/g, ' ')}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                          <div
                            className='border rounded-3 p-4 mt-4'
                            style={{ background: '#f7f9fd', borderColor: '#d7deef' }}
                          >
                            <div className='d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3'>
                              <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '15px' }}>
                                Final Actions
                              </h6>
                              <span className='text-muted fs-8'>
                                Complete required PO, approval, and delivery fields above before final submission.
                              </span>
                            </div>
                            <div className='d-flex flex-column flex-sm-row justify-content-end gap-3 pt-2 border-top'>
                              {canCompareQuotation && (
                                <button
                                  className='btn btn-light btn-active-light-primary w-100 w-sm-auto'
                                  onClick={handleDownloadComparison}
                                  disabled={visibleVendorQuotations.length === 0}
                                >
                                  <i className='bi bi-eye me-1' /> Download / Preview Comparison
                                </button>
                              )}
                              {canManageQuotationWorkflow && (
                                <>
                                  <button
                                    className='btn btn-light btn-active-light-primary w-100 w-sm-auto'
                                    onClick={() => setRevisionRequestModalOpen(true)}
                                    disabled={revisionEligibleVendors.length === 0}
                                  >
                                    Request Revision
                                  </button>
                                  <button
                                    className='btn btn-light-primary w-100 w-sm-auto'
                                    onClick={handleSaveInlineAllocations}
                                    disabled={inlineSaving || loading}
                                  >
                                    {inlineSaving ? 'Saving...' : 'Save Inline Allocation'}
                                  </button>
                                  <button
                                    className='btn btn-primary w-100 w-sm-auto'
                                    onClick={handleFinalizeSelection}
                                    disabled={loading || inlineSaving}
                                  >
                                    {loading ? 'Finalizing...' : 'Finalize Selection'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Past RFQs Tab Content */}
                  {false && (
                    <>
                      <h4 className='fw-bold text-primary mb-4'>Past RFQs ({filteredPastTotalElements})</h4>

                      {pastRFQsLoading ? (
                        <div className='row g-3'>
                          {Array.from({ length: 4 }).map((_, idx) => (
                            <div key={idx} className='col-12'>
                              <div className='border rounded-3 p-3 bg-white shadow-sm'>
                                <div className='d-flex justify-content-between align-items-center mb-3'>
                                  <div style={{ ...shimmerBaseStyle, height: '12px', width: '160px' }} />
                                  <div style={{ ...shimmerBaseStyle, height: '12px', width: '80px' }} />
                                </div>
                                <div className='d-flex flex-column gap-2 mb-3'>
                                  <div style={{ ...shimmerBaseStyle, height: '10px', width: '90%' }} />
                                  <div style={{ ...shimmerBaseStyle, height: '10px', width: '75%' }} />
                                  <div style={{ ...shimmerBaseStyle, height: '10px', width: '60%' }} />
                                </div>
                                <div className='d-flex flex-wrap gap-2'>
                                  <div style={{ ...shimmerBaseStyle, height: '32px', width: '110px', borderRadius: '12px' }} />
                                  <div style={{ ...shimmerBaseStyle, height: '32px', width: '110px', borderRadius: '12px' }} />
                                  <div style={{ ...shimmerBaseStyle, height: '32px', width: '90px', borderRadius: '12px' }} />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='report-table table-responsive'>
                          <div style={{ overflowX: 'auto' }}>
                            <table className='table table-bordered align-middle procurement-list-table'>
                              <thead className='table-header text-start'>
                                <tr>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>RFQ NUMBER</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>ITEM DESCRIPTION</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQUISITION</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>SELECTED VENDOR</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>TOTAL AMOUNT</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>PARTS OF ORIGIN</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>COMPANY COMPARISON</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>DELIVERY DATE</th>
                                  <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>STATUS</th>
                                  <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>ACTIONS</th>
                                </tr>
                              </thead>
                              <tbody className='table-body text-start'>
                                {filteredPastRFQs.length === 0 ? (
                                  <tr>
                                    <td colSpan={10} className='text-center text-muted py-5'>
                                      No past RFQs found.
                                    </td>
                                  </tr>
                                ) : (
                                  pagedPastRFQs.map((rfq) => (
                                    (() => {
                                      const selectedQuotations = Array.isArray(rfq.selectedQuotations)
                                        ? rfq.selectedQuotations
                                        : (rfq.selectedQuotation ? [rfq.selectedQuotation] : [])
                                      const pendingLogisticsRequest =
                                        selectedQuotations
                                          .map((quotation: any) =>
                                            pendingLogisticsMaps.byQuotationId.get(Number(quotation?.id))
                                          )
                                          .find(Boolean) || pendingLogisticsMaps.byRfqId.get(Number(rfq.id))
                                      const selectedVendorNames = selectedQuotations
                                        .map((quotation: any) => quotation?.vendor?.name)
                                        .filter(Boolean)
                                      const vendorLabel =
                                        selectedVendorNames.length > 0
                                          ? selectedVendorNames.join(', ')
                                          : 'N/A'
                                      const totalAmountLabel = formatPastRfqTotalAmount(selectedQuotations)
                                      const partsOfOriginLabel = Array.from(
                                        new Set(
                                          selectedQuotations
                                            .map((quotation: any) => String(quotation?.partsOfOrigin || '').trim())
                                            .filter(Boolean)
                                        )
                                      ).join(', ') || 'N/A'
                                      const comparisonEntries = selectedQuotations.map((quotation: any) =>
                                        resolveQuotationComparisonMetadata(quotation, quotation?.companyCurrencyCode ?? null)
                                      )
                                      const comparableEntries = comparisonEntries.filter(
                                        (entry) => entry.comparableInCompanyCurrency && entry.comparisonAmount != null
                                      )
                                      const evaluationTotal = comparableEntries.reduce(
                                        (acc: number, entry) => acc + Number(entry.comparisonAmount ?? 0),
                                        0
                                      )
                                      const evaluationCurrency = comparableEntries[0]?.comparisonCurrency ?? null
                                      const excludedCount = comparisonEntries.length - comparableEntries.length
                                      const selectedDeliveryDates = selectedQuotations
                                        .map((quotation: any) => quotation?.promisedDeliveryDate)
                                        .filter(Boolean)
                                      const deliveryLabel =
                                        selectedDeliveryDates.length > 0
                                          ? selectedDeliveryDates.join(', ')
                                          : 'N/A'
                                      return (
                                        <tr key={rfq.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                          <td className='text-primary fw-semibold fs-6' style={{ padding: '12px 16px' }}>
                                            {rfq.title}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px', maxWidth: '300px' }}>
                                            {rfq.itemDescription}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {rfq.requisition?.requisition_code || 'N/A'}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {vendorLabel}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {totalAmountLabel}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {partsOfOriginLabel}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {evaluationTotal > 0 && evaluationCurrency
                                              ? `${evaluationCurrency} ${evaluationTotal.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                              })}${excludedCount > 0 ? ` (${excludedCount} excluded)` : ''}`
                                              : excludedCount > 0
                                                ? 'Company currency conversion unavailable'
                                                : 'N/A'}
                                          </td>
                                          <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                            {deliveryLabel}
                                          </td>
                                          <td style={{ padding: '12px 16px' }}>
                                            <span className='badge bg-secondary text-white'>CLOSED</span>
                                          </td>
                                          <td className='text-center' style={{ padding: '12px 16px' }}>
                                            <button
                                              className='btn btn-sm px-0'
                                              title='View'
                                              onClick={() => handleViewPastRFQ(rfq)}
                                            >
                                              <KTSVG path='/media/map/ph_eye.svg' />
                                            </button>
                                            {pendingLogisticsRequest ? (
                                              <button
                                                className='btn btn-sm btn-light-warning ms-2'
                                                title='Review Logistics Update'
                                                onClick={() => setActiveLogisticsRequest(pendingLogisticsRequest)}
                                              >
                                                Review Logistics Update
                                              </button>
                                            ) : null}
                                          </td>
                                        </tr>
                                      )
                                    })()
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          {filteredPastTotalElements > 0 && (
                            <div className='pagination-wrapper d-flex justify-content-between align-items-center py-3'>
                              <div className='d-flex align-items-center'>
                                <span className='text-muted me-2'>Rows per page</span>
                                <SearchableSelect
                                  className='form-select'
                                  style={{
                                    borderRadius: '20px',
                                    width: '70px',
                                    border: '1px solid #dee2e6',
                                    fontSize: '14px',
                                    padding: '4px 8px'
                                  }}
                                  value={pastRowsPerPage}
                                  onChange={(e) => {
                                    setPastRowsPerPage(parseInt(e.target.value))
                                    setPastCurrentPage(1)
                                  }}
                                >
                                  <option value={10}>10</option>
                                  <option value={20}>20</option>
                                  <option value={50}>50</option>
                                </SearchableSelect>
                              </div>
                              <div className='d-flex align-items-center'>
                                <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                                  Showing <strong>{pagedPastRFQs.length > 0 ? ((pastCurrentPage - 1) * pastRowsPerPage) + 1 : 0}-{Math.min(pastCurrentPage * pastRowsPerPage, filteredPastTotalElements)}</strong> of <strong>{filteredPastTotalElements}</strong>
                                </span>

                                <nav>
                                  <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                                    <li className={`page-item ${pastCurrentPage === 1 ? 'disabled' : ''}`}>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => setPastCurrentPage(pastCurrentPage - 1)}
                                        disabled={pastCurrentPage === 1}
                                      >
                                        ‹
                                      </button>
                                    </li>

                                    {(() => {
                                      const totalPages = pastTotalPages
                                      const pages = []
                                      const showPages = 5
                                      let startPage = Math.max(1, pastCurrentPage - 2)
                                      let endPage = Math.min(totalPages, startPage + showPages - 1)

                                      if (endPage - startPage < showPages - 1) {
                                        startPage = Math.max(1, endPage - showPages + 1)
                                      }

                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                          <li key={i} className={`page-item ${pastCurrentPage === i ? 'active' : ''}`}>
                                            <button
                                              className='page-link text-muted'
                                              style={{
                                                backgroundColor: pastCurrentPage === i ? '#F4F9FF' : 'transparent',
                                                border: '1px solid #dee2e6',
                                                padding: '8px 12px',
                                                fontSize: '14px',
                                                minWidth: '40px',
                                                borderRadius: '6px'
                                              }}
                                              onClick={() => setPastCurrentPage(i)}
                                            >
                                              {i}
                                            </button>
                                          </li>
                                        )
                                      }
                                      return pages
                                    })()}

                                    <li className={`page-item ${pastCurrentPage === pastTotalPages ? 'disabled' : ''}`}>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => setPastCurrentPage(pastCurrentPage + 1)}
                                        disabled={pastCurrentPage === pastTotalPages}
                                      >
                                        ›
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quotation Detail & Allocation Modal */}
        {isQuotationModalVisible && activeVendorForModal && (
          <div
            className='modal fade show d-flex align-items-center justify-content-center procurement-document-modal'
            tabIndex={-1}
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1050
            }}
          >
            <div
              className='modal-dialog modal-xl modal-dialog-centered procurement-view-modal-dialog'
            >
              <div className='modal-content procurement-document-modal-content'>
                <div className='modal-header border-0 pb-0'>
                  <div>
                    <h3 className='modal-title fw-bold text-dark mb-1'>Quotation Details</h3>
                    <p className='text-muted mb-0 fs-7'>Review the quotation and allocate quantities for this vendor.</p>
                  </div>
                  <button type='button' className='btn-close' onClick={handleCloseQuotationModal}></button>
                </div>

                <div className='modal-body procurement-document-modal-body pt-3'>
                  <div className='row g-4'>
                    <div className='col-md-6'>
                      <div
                        className='h-100 rounded-3 p-4 shadow-sm border'
                        style={{ background: 'linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 80%)' }}
                      >
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='fw-bold text-primary mb-0'>RFQ / Requisition</h6>
                          <span className='badge bg-light-primary text-primary'>Details</span>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>RFQ Number</label>
                          <div className='fw-bold text-dark'>{comparisonRfq?.title || comparisonRfq?.rfqNumber || 'N/A'}</div>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>Item / Service</label>
                          <div className='fw-bold text-dark'>{comparisonRfq?.itemDescription || '-'}</div>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>Requisition</label>
                          <div className='fw-bold text-dark'>{comparisonRequisition?.title || 'N/A'}</div>
                        </div>
                        <div className='row g-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Required Quantity</label>
                            <div className='fw-bold text-dark'>{displayTotalRequired}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Delivery Port</label>
                            <div className='fw-bold text-dark'>
                              {comparisonRfq?.deliveryPortName || activeVendorForModal.deliveryPort || 'N/A'}
                            </div>
                          </div>
                        </div>

                        <div className='mt-3'>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <h6 className='fw-bold text-primary mb-0'>Line Items</h6>
                            {modalLinesLoading && <span className='text-muted fs-8'>Loading...</span>}
                          </div>
                          {modalLines.length === 0 && !modalLinesLoading ? (
                            <div className='text-muted fs-8'>No line items found.</div>
                          ) : (
                            <div className='table-responsive'>
                              <table className='table table-sm table-striped align-middle mb-0'>
                                <thead className='table-light'>
                                  <tr>
                                    <th className='text-muted fw-semibold'>Item</th>
                                    <th className='text-muted fw-semibold'>Qty</th>
                                    <th className='text-muted fw-semibold'>UOM</th>
                                    <th className='text-muted fw-semibold'>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {modalLines.map((line, idx) => {
                                    const label =
                                      line.manualItemName ||
                                      (line.partId ? `Part #${line.partId}` : line.subcomponentId ? `Subcomponent #${line.subcomponentId}` : `Item ${idx + 1}`)
                                    return (
                                      <tr key={`${line.partId ?? 'p'}-${line.subcomponentId ?? 's'}-${line.manualItemName ?? 'm'}-${idx}`}>
                                        <td className='text-dark'>{label}</td>
                                        <td className='text-dark'>{line.quantity ?? '—'}</td>
                                        <td className='text-dark'>{line.unitOfMeasurement || '—'}</td>
                                        <td className='text-dark'>{line.notes || line.remarks || '—'}</td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className='col-md-6'>
                      <div
                        className='h-100 rounded-3 p-4 shadow-sm border'
                        style={{ background: 'linear-gradient(180deg, #FDF9FF 0%, #FFFFFF 80%)' }}
                      >
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                          <h6 className='fw-bold text-primary mb-0'>Vendor</h6>
                          <span className='badge bg-light text-primary border'>Partner</span>
                        </div>
                        {quotationDetailLoading && !modalQuotationDetails && (
                          <div className='text-muted fs-8 mb-3'>Loading vendor details...</div>
                        )}
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>Vendor Name</label>
                          <div className='fw-bold text-dark'>{modalQuotationDetails?.vendor?.name || activeVendorForModal.vendor}</div>
                        </div>
                        <div className='row g-3 mb-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Vendor Code</label>
                            <div className='fw-bold text-dark'>{modalQuotationDetails?.vendor?.code || activeVendorForModal.vendorCode}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Contact</label>
                            <div className='fw-bold text-dark'>{modalQuotationDetails?.vendor?.contactNo || activeVendorForModal.vendorContact || '-'}</div>
                          </div>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>Email</label>
                          <div className='fw-bold text-dark'>{modalQuotationDetails?.vendor?.email || activeVendorForModal.vendorEmail || '-'}</div>
                        </div>
                        <div className='mb-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>Quotation Attachments</label>
                            <ProcurementAttachmentGallery
                            attachments={
                              modalQuotationDetails?.attachments ??
                              activeVendorForModal.attachments ??
                              []
                            }
                            emptyText='No quotation attachments uploaded.'
                            showViewButton
                          />
                        </div>
                        <div className='row g-3'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Quoted Amount</label>
                            <div className='fw-bold text-dark'>{activeVendorForModal.currency} {activeVendorForModal.price?.toFixed(2)}</div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Payment Terms</label>
                            <div className='fw-bold text-dark'>{modalQuotationDetails?.termsOfPayment || activeVendorForModal.paymentTerms || '-'}</div>
                          </div>
                        </div>
                        <div className='row g-3 mt-1'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Billing Address</label>
                            <div className='fw-bold text-dark'>
                              {modalQuotationDetails?.billingAddressLabel ||
                                modalQuotationDetails?.billingAddressDisplay ||
                                activeVendorForModal.billingAddressLabel ||
                                activeVendorForModal.billingAddressDisplay ||
                                '-'}
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Shipping Address</label>
                            <div className='fw-bold text-dark'>
                              {modalQuotationDetails?.shippingAddressLabel ||
                                modalQuotationDetails?.shippingAddressDisplay ||
                                activeVendorForModal.shippingAddressLabel ||
                                activeVendorForModal.shippingAddressDisplay ||
                                '-'}
                            </div>
                          </div>
                        </div>
                        <div className='row g-3 mt-1'>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Quality Rating</label>
                            <div className='fw-bold text-dark'>
                              {activeVendorForModal.vendorDetails?.qualityRating != null
                                ? `${activeVendorForModal.vendorDetails.qualityRating}/5`
                                : activeVendorForModal.qualityRating != null
                                  ? `${activeVendorForModal.qualityRating}/5`
                                  : 'Not Rated'}
                            </div>
                          </div>
                          <div className='col-sm-6'>
                            <label className='form-label fw-semibold text-muted fs-8 mb-1'>Supply Rating</label>
                            <div className='fw-bold text-dark'>
                              {activeVendorForModal.vendorDetails?.supplyRating != null
                                ? `${activeVendorForModal.vendorDetails.supplyRating}/5`
                                : activeVendorForModal.supplyRating != null
                                  ? `${activeVendorForModal.supplyRating}/5`
                                  : 'Not Rated'}
                            </div>
                          </div>
                        </div>
                        <div className='mt-3'>
                          <label className='form-label fw-semibold text-muted fs-8 mb-1'>
                            Logistic account (from PO / Delivery / Approval)
                          </label>
                          <div className='fw-bold text-dark fs-7'>
                            {accountCodeMode === 'INDIVIDUAL'
                              ? [commonLogisticAccountCode, commonLogisticSubaccountCode]
                                .filter(Boolean)
                                .join(' / ') || '—'
                              : commonBudgetAccountCode || '—'}
                          </div>
                        </div>
                        <div className='mt-3 d-flex flex-wrap gap-2 align-items-center'>
                          <span className='badge bg-light text-dark border text-uppercase'>
                            {String(activeVendorForModal.status || 'SUBMITTED').replace(/_/g, ' ')}
                          </span>
                          {canRunOwnerApprovalActions && (
                            <>
                              {canOwnerApproveQuotation && canOwnerApprove(activeVendorForModal.status) && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-success'
                                  onClick={() => openActionDialog('OWNER_APPROVE', activeVendorForModal.id)}
                                  disabled={loading}
                                >
                                  Owner Approve
                                </button>
                              )}
                              {canOwnerRejectQuotation && canOwnerReject(activeVendorForModal.status) && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger'
                                  onClick={() => openActionDialog('OWNER_REJECT', activeVendorForModal.id)}
                                  disabled={loading}
                                >
                                  Owner Reject
                                </button>
                              )}
                              {canRequestQuotationRework && canRequestRework(activeVendorForModal.status) && (
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-warning'
                                  onClick={() => openActionDialog('REWORK', activeVendorForModal.id)}
                                  disabled={loading}
                                >
                                  Rework
                                </button>
                              )}
                            </>
                          )}
                          {canSendQuotationToVessel && canSendToVessel(activeVendorForModal.status) && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-primary'
                              onClick={() => openActionDialog('SEND_TO_VESSEL', activeVendorForModal.id)}
                              disabled={loading}
                            >
                              Send To Vessel
                            </button>
                          )}
                          {canSelectQuotation &&
                            ['SENT_TO_VESSEL', 'SENT_TO_VESSEL_FOR_SELECTION'].includes(
                              String(activeVendorForModal.status || '').toUpperCase()
                            ) && (
                              <>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-success'
                                  onClick={async () => {
                                    try {
                                      const remark = window.prompt('Optional remark for vessel decision?') || undefined
                                      await vesselSelectQuotation(activeVendorForModal.id, remark)
                                      toast.success('Marked as vessel-selected')
                                      await fetchQuotations(selectedRFQ as number)
                                    } catch (error: any) {
                                      toast.error('Failed to mark vessel selection')
                                    }
                                  }}
                                  disabled={loading}
                                >
                                  Vessel Select
                                </button>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-danger'
                                  onClick={async () => {
                                    try {
                                      const remark = window.prompt('Optional remark for vessel decision?') || undefined
                                      await vesselRejectQuotation(activeVendorForModal.id, remark)
                                      toast.info('Marked as vessel-rejected')
                                      await fetchQuotations(selectedRFQ as number)
                                    } catch (error: any) {
                                      toast.error('Failed to reject quotation')
                                    }
                                  }}
                                  disabled={loading}
                                >
                                  Vessel Reject
                                </button>
                              </>
                            )}
                        </div>
                        {(modalQuotationDetails?.ownerActionRemark ||
                          modalQuotationDetails?.reworkRemark ||
                          modalQuotationDetails?.vesselForwardRemark ||
                          activeVendorForModal.ownerActionRemark ||
                          activeVendorForModal.reworkRemark ||
                          activeVendorForModal.vesselForwardRemark) && (
                            <div className='mt-2 text-muted fs-8'>
                              {(modalQuotationDetails?.ownerActionRemark || activeVendorForModal.ownerActionRemark) && (
                                <div><strong>Owner Remark:</strong> {modalQuotationDetails?.ownerActionRemark || activeVendorForModal.ownerActionRemark}</div>
                              )}
                              {(modalQuotationDetails?.reworkRemark || activeVendorForModal.reworkRemark) && (
                                <div><strong>Rework Reason:</strong> {modalQuotationDetails?.reworkRemark || activeVendorForModal.reworkRemark}</div>
                              )}
                              {(modalQuotationDetails?.vesselForwardRemark || activeVendorForModal.vesselForwardRemark) && (
                                <div><strong>Vessel Note:</strong> {modalQuotationDetails?.vesselForwardRemark || activeVendorForModal.vesselForwardRemark}</div>
                              )}
                            </div>
                          )}

                        {priceBreakdown.length > 0 && (
                          <div className='mt-3'>
                            <div className='d-flex justify-content-between align-items-center mb-2'>
                              <span className='fw-bold text-primary fs-7'>Amount by Line</span>
                              <span className='badge bg-light-primary text-primary'>Total: {activeVendorForModal.currency} {activeVendorForModal.price?.toFixed(2)}</span>
                            </div>
                            <div className='table-responsive'>
                              <table className='table table-sm align-middle mb-0'>
                                <thead className='table-light'>
                                  <tr>
                                    <th className='text-muted fw-semibold'>Item</th>
                                    <th className='text-muted fw-semibold'>Qty</th>
                                    <th className='text-muted fw-semibold'>Amount</th>
                                    <th className='text-muted fw-semibold'>Unit Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {priceBreakdown.map((row, idx) => (
                                    <tr key={`pb-${idx}`}>
                                      <td className='text-dark'>{row.label}</td>
                                      <td className='text-dark'>{row.qty}</td>
                                      <td className='text-dark'>
                                        {row.share !== undefined
                                          ? `${activeVendorForModal.currency} ${row.share.toFixed(2)}`
                                          : '—'}
                                      </td>
                                      <td className='text-dark'>
                                        {row.unit !== undefined
                                          ? `${activeVendorForModal.currency} ${row.unit.toFixed(2)}`
                                          : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className='border rounded-3 p-4 bg-light mt-4'>
                    <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3'>
                      <div>
                        <h6 className='fw-bold text-dark mb-1'>Allocate Quantity</h6>
                        <div className='text-muted fs-8'>Distribute the requisition across multiple vendors. This value will be sent with the selection.</div>
                      </div>
                      <span className='badge bg-light-primary text-primary fs-8 px-3 py-2'>
                        Total Required: {displayTotalRequired}
                      </span>
                    </div>

                    {modalLines.length > 1 && (
                      <div className='mb-3'>
                        <label className='form-label fw-semibold text-muted fs-8'>Select Line Item</label>
                        <SearchableSelect
                          className='form-select'
                          value={selectedLineKey ?? ''}
                          onChange={(e) => setSelectedLineKey(e.target.value)}
                        >
                          {modalLines.map((line, idx) => {
                            const key = `${line.partId ?? 'p'}-${line.subcomponentId ?? 's'}-${line.manualItemName ?? 'm'}-${idx}`
                            const label =
                              line.manualItemName ||
                              (line.partId ? `Part #${line.partId}` : line.subcomponentId ? `Subcomponent #${line.subcomponentId}` : `Item ${idx + 1}`)
                            const qty = line.quantity ?? 0
                            const uom = line.unitOfMeasurement || ''
                            return (
                              <option key={key} value={key}>
                                {label} — {qty} {uom}
                              </option>
                            )
                          })}
                        </SearchableSelect>
                        <div className='form-text'>Allocation applies to the selected line only.</div>
                      </div>
                    )}

                    <div className='row g-3'>
                      <div className='col-sm-6'>
                        <label className='form-label fw-semibold text-muted fs-8'>Units for this vendor</label>
                        <input
                          type='number'
                          min={0}
                          className='form-control'
                          value={currentVendorAllocation}
                          disabled={isAllocationLockedForRework(activeVendorForModal.status)}
                          onChange={(e) => handleAllocationChange(activeVendorForModal.id, Number(e.target.value))}
                          placeholder='Enter quantity'
                        />
                        <div className='form-text'>
                          Remaining after allocations: <strong>{remainingQuantity}</strong> of {resolvedTotalRequisitionQuantity}
                        </div>
                        {isAllocationLockedForRework(activeVendorForModal.status) && (
                          <div className='text-warning fs-8 mt-1'>
                            Allocation is locked while this quotation is in rework-pending status.
                          </div>
                        )}
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label fw-semibold text-muted fs-8'>Delivery Promise</label>
                        <div className='fw-bold text-dark'>
                          {modalQuotationDetails?.promisedDeliveryDate || activeVendorForModal.deliveryTime || 'TBD'}
                        </div>
                      </div>
                    </div>

                    {isOverAllocated && (
                      <div className='alert alert-warning mt-3 mb-0 py-2 px-3 fs-8'>
                        Allocated quantities ({totalAllocatedQuantity}) exceed the requisition need ({totalRequisitionQuantity}). Adjust before finalizing.
                      </div>
                    )}

                    <div className='mt-3'>
                      <div className='fw-semibold text-muted fs-8 mb-2'>Current allocations</div>
                      <div className='d-flex flex-wrap gap-2'>
                        {vendorQuotations.map((vendor, idx) => {
                          const key = selectedLineKey ? `${vendor.id}-${selectedLineKey}` : `${vendor.id}`
                          const allocated = allocationValues[key] ?? vendor.actualQuantityAssignedToVendor ?? 0
                          return (
                            <span key={`${key}-${idx}`} className='badge bg-secondary bg-opacity-10 text-dark border'>
                              {vendor.vendor}: <strong className='ms-1'>{allocated}</strong>
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                {activeVendorAccountingValidationError && (
                  <div className='px-6 pb-2 text-danger fs-8 fw-semibold'>
                    {activeVendorAccountingValidationError}
                  </div>
                )}

                <div className='modal-footer border-0 pt-3'>
                  <button type='button' className='btn btn-light' onClick={handleCloseQuotationModal}>
                    Cancel
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary'
                    disabled={
                      loading ||
                      !!activeVendorAccountingValidationError ||
                      isAllocationLockedForRework(activeVendorForModal.status) ||
                      (!currentVendorAllocation && resolvedTotalRequisitionQuantity > 0)
                    }
                    onClick={async () => {
                      let modalAssignedLines: QuotationAwardLineAssignment[] | undefined = undefined
                      if (selectedLineKey) {
                        const selectedIndex = modalLines.findIndex((line, idx) => {
                          const key = `${line.partId ?? 'p'}-${line.subcomponentId ?? 's'}-${line.manualItemName ?? 'm'}-${idx}`
                          return key === selectedLineKey
                        })
                        const selectedLine = selectedIndex >= 0 ? modalLines[selectedIndex] : null
                        if (selectedLine?.partId != null && currentVendorAllocation > 0) {
                          const rowKey = `line-${Number(selectedLine.partId)}`
                          const rowAccounting = rowAccountingValues[rowKey]
                          modalAssignedLines = [
                            {
                              requisitionLineId: Number(selectedLine.partId),
                              assignedQuantity: currentVendorAllocation,
                              accountCodeId: rowAccounting?.accountCodeId ?? null,
                              accountCode:
                                rowAccounting?.accountCode ||
                                (selectedLine.partId != null
                                  ? lineMetaById[Number(selectedLine.partId)]?.accountCode || undefined
                                  : undefined),
                              subaccountCodeId: rowAccounting?.subaccountCodeId ?? null,
                              subaccountCode: rowAccounting?.subaccountCode || undefined,
                            },
                          ]
                        }
                      }
                      const wasSuccessful = await handleSelectQuotation(
                        activeVendorForModal.id,
                        currentVendorAllocation,
                        activeVendorForModal.status === 'SELECTED',
                        modalAssignedLines
                      )
                      if (wasSuccessful) {
                        handleCloseQuotationModal()
                      }
                    }}
                  >
                    {loading ? 'Processing...' : activeVendorForModal.status === 'SELECTED' ? 'Update Allocation' : 'Select & Allocate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quotation Comparison PDF Preview Modal */}
        {isComparisonModalOpen && (
          <div
            className='modal fade show d-block'
            tabIndex={-1}
            role='dialog'
            aria-modal='true'
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1055 }}
            onClick={() => setIsComparisonModalOpen(false)}
          >
            <div
              className='modal-dialog modal-xl modal-dialog-scrollable'
              role='document'
              style={{ maxWidth: '95vw' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='modal-content'>
                <div className='modal-header d-flex align-items-center justify-content-between py-3 px-4 bg-light'>
                  <div>
                    <h5 className='modal-title fw-bold text-dark mb-0'>Quotation Comparison Preview</h5>
                    <span className='text-muted fs-7'>
                      RFQ: {rfqDetails.rfqNumber || '-'} | Item: {rfqDetails.itemService || '-'}
                    </span>
                  </div>
                  <button
                    type='button'
                    className='btn-close'
                    aria-label='Close'
                    onClick={() => setIsComparisonModalOpen(false)}
                  />
                </div>

                <div className='modal-body p-4' style={{ overflowY: 'auto', maxHeight: '75vh' }}>
                  <div
                    id='quotation-comparison-modal-host'
                    data-export-ready='true'
                    style={{
                      background: '#ffffff',
                      color: '#172033',
                      padding: '24px',
                      borderRadius: '8px',
                      border: '1px solid #eef2f7',
                    }}
                  >
                    <div style={{ marginBottom: '24px', borderBottom: '2px solid #eef4ff', paddingBottom: '16px' }}>
                      <div className='d-flex justify-content-between align-items-start flex-wrap gap-2'>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#181C32' }}>
                            Quotation Comparison
                          </h3>
                          <div style={{ marginTop: '6px', color: '#5e6278', fontSize: '13px' }}>
                            <strong>RFQ:</strong> {rfqDetails.rfqNumber || '-'} | <strong>Item:</strong> {rfqDetails.itemService || '-'} | <strong>Due Date:</strong> {rfqDetails.requestedDate || '-'}
                          </div>
                          <div style={{ marginTop: '4px', color: '#5e6278', fontSize: '13px' }}>
                            <strong>Line Items:</strong> {rfqDetails.lineItems || 0} | <strong>Department:</strong> {rfqDetails.department || '-'} | <strong>Generated:</strong> {new Date().toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Vendor Summary */}
                    <div className='mb-4'>
                      <h6 className='fw-bold text-primary mb-2'>Vendor Summary</h6>
                      <div className='table-responsive'>
                        <table className='table table-sm table-bordered align-middle mb-0' style={{ fontSize: '12px', tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: '24%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '8%' }} />
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '12%' }} />
                            <col style={{ width: '14%' }} />
                            <col style={{ width: '14%' }} />
                          </colgroup>
                          <thead className='table-light'>
                            <tr>
                              <th>Vendor</th>
                              <th>Status</th>
                              <th>Currency</th>
                              <th className='text-end'>Grand Total</th>
                              <th>Delivery</th>
                              <th>Payment Terms</th>
                              <th>Revision State</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleVendorQuotations.map((vendor) => (
                              <tr key={`modal-vendor-${vendor.id}`}>
                                <td className='fw-semibold' style={{ overflowWrap: 'break-word' }}>{vendor.vendor}</td>
                                <td>
                                  <span className='badge bg-light-primary text-primary px-2 py-1 fs-8'>
                                    {quotationStatusPresentation(vendor.status).label}
                                  </span>
                                </td>
                                <td>{vendor.currency || '-'}</td>
                                <td className='text-end fw-bold'>
                                  {formatVendorAmount(vendor, vendor.grandTotal ?? vendor.originalTotal, vendor.companyTotal)}
                                </td>
                                <td>{vendor.deliveryTime || '-'}</td>
                                <td>{vendor.paymentTerms || '-'}</td>
                                <td style={{ overflowWrap: 'break-word' }}>{vendor.reworkRemark || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Line Comparison */}
                    <div>
                      <h6 className='fw-bold text-primary mb-2'>Line Item Comparison</h6>
                      <div className='table-responsive'>
                        <table className='table table-sm table-bordered align-middle mb-0' style={{ fontSize: '11px', tableLayout: 'fixed', width: '100%' }}>
                          <colgroup>
                            <col style={{ width: visibleVendorQuotations.length > 2 ? '22%' : '28%' }} />
                            <col style={{ width: '6%' }} />
                            <col style={{ width: '6%' }} />
                            {visibleVendorQuotations.map((vendor) => (
                              <col key={`modal-col-vendor-${vendor.id}`} style={{ width: `${(100 - (visibleVendorQuotations.length > 2 ? 34 : 40)) / Math.max(visibleVendorQuotations.length, 1)}%` }} />
                            ))}
                          </colgroup>
                          <thead className='table-light'>
                            <tr>
                              <th>Item</th>
                              <th className='text-center'>Qty</th>
                              <th className='text-center'>Unit</th>
                              {visibleVendorQuotations.map((vendor) => (
                                <th key={`modal-head-${vendor.id}`} className='text-start' style={{ overflowWrap: 'break-word' }}>
                                  {vendor.vendor}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {comparisonRows.map((row) => (
                              <tr key={`modal-row-${row.rowKey}`}>
                                <td style={{ overflowWrap: 'break-word' }}>{row.itemName}</td>
                                <td className='text-center'>{row.reqQtyValue}</td>
                                <td className='text-center'>{row.unit}</td>
                                {visibleVendorQuotations.map((vendor) => {
                                  const line = row.vendorLineMap[vendor.id]
                                  const unitPrice = Number(line?.unitPrice ?? 0)
                                  const discountPct = Number(line?.discountPercent ?? 0)
                                  const gstPct = Number(line?.gstPercent ?? 0)
                                  const amount = line
                                    ? Number(line.quantity ?? 0) * unitPrice * (1 - discountPct / 100) * (1 + gstPct / 100)
                                    : null
                                  return (
                                    <td key={`modal-cell-${row.rowKey}-${vendor.id}`} style={{ verticalAlign: 'top', overflowWrap: 'break-word' }}>
                                      {line ? (
                                        <div className='d-flex flex-column gap-1 fs-8' style={{ overflowWrap: 'break-word' }}>
                                          <div><strong>Qty:</strong> {line.quantity ?? '-'}</div>
                                          <div><strong>Unit Price:</strong> {line.unitPrice != null ? `${vendor.currency} ${unitPrice.toFixed(2)}` : '-'}</div>
                                          <div><strong>Discount:</strong> {line.discountPercent != null ? `${discountPct.toFixed(2)}%` : '-'}</div>
                                          <div><strong>GST/VAT:</strong> {line.gstPercent != null ? `${gstPct.toFixed(2)}%` : '-'}</div>
                                          <div><strong>Amount:</strong> {amount != null ? `${vendor.currency} ${amount.toFixed(2)}` : '-'}</div>
                                          <div><strong>Lead Days:</strong> {line.deliveryTimeDays ?? '-'}</div>
                                          <div><strong>Remarks:</strong> {line.remarks || line.notes || '-'}</div>
                                        </div>
                                      ) : (
                                        <span className='text-muted fs-8'>Not Applicable</span>
                                      )}
                                    </td>
                                  )
                                })}
                              </tr>
                            ))}
                            {vendorSummaryRows.map((summaryRow) => (
                              <tr key={`modal-summary-${summaryRow.key}`} className='table-secondary'>
                                <td colSpan={3} className='fw-bold'>
                                  {summaryRow.label}
                                </td>
                                {visibleVendorQuotations.map((vendor) => (
                                  <td key={`modal-summary-${summaryRow.key}-${vendor.id}`} className='fw-bold' style={{ overflowWrap: 'break-word' }}>
                                    {(() => {
                                      const val = summaryRow.value(vendor)
                                      return formatVendorAmount(vendor, val.original, val.company)
                                    })()}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <div className='modal-footer py-3 px-4 bg-light d-flex justify-content-between align-items-center'>
                  <span className='text-muted fs-7'>
                    Review the comparison preview above before downloading the PDF.
                  </span>
                  <div className='d-flex gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      onClick={() => setIsComparisonModalOpen(false)}
                    >
                      Close
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary btn-sm'
                      disabled={comparisonDownloadLoading}
                      onClick={async () => {
                        try {
                          setComparisonDownloadLoading(true)
                          const rfqLabel = String(rfqDetails.rfqNumber || `RFQ-${selectedRFQ}`).replace(/\s+/g, '_')
                          await downloadElementAsPdf('#quotation-comparison-modal-host', `quotation_comparison_${rfqLabel}`)
                          toast.success('Quotation comparison PDF downloaded')
                        } catch (err: any) {
                          console.error('Failed to download PDF:', err)
                          toast.error(err?.message || 'Failed to download comparison PDF')
                        } finally {
                          setComparisonDownloadLoading(false)
                        }
                      }}
                    >
                      {comparisonDownloadLoading ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true' />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <i className='bi bi-download me-1' /> Download PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {quickActionMenuState &&
          activeQuickActionVendor &&
          typeof document !== 'undefined' &&
          createPortal(
            <div
              data-quick-action-menu
              className='bg-white border rounded shadow-sm p-2 d-flex flex-column gap-1'
              style={{
                position: 'fixed',
                top: quickActionMenuState.top,
                left: quickActionMenuState.left,
                width: '190px',
                zIndex: 6000,
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
              }}
            >
              {canSendQuotationToVessel && (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary text-start'
                  disabled={!canSendToVessel(activeQuickActionVendor.status)}
                  onClick={() => {
                    setQuickActionMenuState(null)
                    openActionDialog('SEND_TO_VESSEL', activeQuickActionVendor.id)
                  }}
                >
                  Send To Vessel
                </button>
              )}
              {canRunOwnerApprovalActions && (
                <>
                  {canOwnerApproveQuotation && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-success text-start'
                      disabled={!canOwnerApprove(activeQuickActionVendor.status)}
                      onClick={() => {
                        setQuickActionMenuState(null)
                        openActionDialog('OWNER_APPROVE', activeQuickActionVendor.id)
                      }}
                    >
                      Approve
                    </button>
                  )}
                  {canRequestQuotationRework && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-warning text-start'
                      disabled={!canRequestRework(activeQuickActionVendor.status)}
                      onClick={() => {
                        setQuickActionMenuState(null)
                        openActionDialog('REWORK', activeQuickActionVendor.id)
                      }}
                    >
                      Rework
                    </button>
                  )}
                  {canOwnerRejectQuotation && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger text-start'
                      disabled={!canOwnerReject(activeQuickActionVendor.status)}
                      onClick={() => {
                        setQuickActionMenuState(null)
                        openActionDialog('OWNER_REJECT', activeQuickActionVendor.id)
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </>
              )}
            </div>,
            document.body
          )}

        {actionDialog && (
          <div
            className='modal fade show d-flex align-items-center justify-content-center'
            tabIndex={-1}
            style={{
              backgroundColor: 'rgba(0,0,0,0.45)',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 1060,
            }}
          >
            <div className='modal-dialog modal-dialog-centered' role='document'>
              <div className='modal-content bg-white'>
                <div className='modal-header'>
                  <h5 className='modal-title'>{actionDialog.title}</h5>
                  <button type='button' className='btn-close' onClick={closeActionDialog}></button>
                </div>
                <div className='modal-body'>
                  <label className='form-label fw-semibold fs-7 mb-2'>
                    Remark {actionDialog.requireRemark ? <span className='text-danger'>*</span> : <span className='text-muted'>(Optional)</span>}
                  </label>
                  <textarea
                    className='form-control'
                    rows={4}
                    placeholder={actionDialog.placeholder}
                    value={actionRemark}
                    onChange={(e) => setActionRemark(e.target.value)}
                  />
                </div>
                <div className='modal-footer'>
                  <button type='button' className='btn btn-light' onClick={closeActionDialog} disabled={loading}>
                    Cancel
                  </button>
                  <button type='button' className='btn btn-primary' onClick={submitActionDialog} disabled={loading}>
                    {loading ? 'Saving...' : actionDialog.submitLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Past RFQ Modal */}
        <ViewPastRFQModal
          visible={isViewPastRFQModalVisible}
          onClose={() => {
            setIsViewPastRFQModalVisible(false)
            setSelectedPastRFQ(null)
          }}
          rfq={selectedPastRFQ}
        />

        <QuotationRevisionRequestModal
          open={revisionRequestModalOpen}
          vendors={revisionEligibleVendors}
          submitting={revisionRequestSubmitting}
          onClose={() => setRevisionRequestModalOpen(false)}
          onSubmit={handleSubmitRevisionRequest}
        />

        <VendorLogisticsChangeRequestModal
          open={activeLogisticsRequest != null}
          request={activeLogisticsRequest}
          canReview={canReviewVendorLogisticsRequests}
          submitting={logisticsActionLoading}
          onClose={() => setActiveLogisticsRequest(null)}
          onApprove={handleApproveLogisticsRequest}
          onReject={handleRejectLogisticsRequest}
        />

        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ top: "5rem" }}
        />
      </div>
    </>
  )
}

export { QuotationManagement }
